import { makeAutoObservable, runInAction, computed } from "mobx";
import type { RootStore } from "@intrasync/stores";
import uuid from "../../../../shared/common/utils/uuid";

const TAB_NAMES = ['project', 'structure', 'pipes', 'internals', 'components'];
import {
  IManholeProject,
  IPipeConnection,
  ICatalogProduct,
  IStackComponent,
  IBillOfMaterialsEntry,
  IDrawingObject,
  IManholeInternals,
  IPipeValidation,
  IBalancingSuggestion,
  IManholeCasting,
  IFormAssignment,
  IManholeSequence,
  IManholeSequenceEntry,
  ISealantConfig,
  IManholeInternalsExtended,
  IPipeCollisionResult,
  isComponentType,
  getProductCost,
  getProductCode,
  generateJobIdentifier,
  incrementRevision,
  getDefaultFloorThickness,
  getDefaultHoleSize,
  getBaseInvertToFirstJointFromProduct,
  calculateOverallDepth,
  calculateAnnularSpace,
  checkAllPipeCollisions,
  getEffectiveHeightInches,
  JOINT_OVERLAP_INCHES,
  DEFAULT_FLOOR_THICKNESS,
} from "../../../../types/manhole.types";
import {
  calculateBaseContribution,
  calculateComponentContribution,
} from "../utils/rimToInvertCalculations";
import { derivePipeLocation } from "../utils/derivePipeLocation";
import type { manholeProjectPatch } from "../../../../types/api-types/estimating/manhole-projects/manhole-projects.types";

export class ManholeViewStore {
  // Observables - UI state only
  selectedPipeId: string | null = null;
  currentView: "side" | "top" = "side";
  zoomLevel: number = 1;
  panPosition: { x: number; y: number } = { x: 0, y: 0 };
  isLoading: boolean = false;
  error: string | null = null;

  // Form state for new/edit project
  editingProject: Partial<IManholeProject> | null = null;
  isDirty: boolean = false;

  // Dialog form data
  dialogFormData: Record<string, any> = {};

  // Tab control for ProjectInputCard — computed from URL queryParams
  get activeConfigTab(): number {
    const tabName = (this.rootStore as any).routerStore?.queryParams?.configTab || 'project';
    const index = TAB_NAMES.indexOf(tabName);
    return index >= 0 ? index : 0;
  }

  // Canvas view mode control
  canvasViewMode: "side" | "top" = "top";

  // NEW: Per-component top view selector for coring reference
  // null = "All Components", 0 = first component (base), 1 = second component, etc.
  selectedTopViewComponentIndex: number | null = null;

  // NEW: Sequence navigation for multi-manhole projects
  // NOTE: Multi-manhole sequences are managed locally since the API's manhole-sequences
  // service stores component stack data, not multi-manhole project sequences
  currentSequenceId: string | null = null;
  currentManholeIndex: number = 0;
  localSequence: IManholeSequence | null = null;

  // NEW: Height dismiss feature (Phase 3) / Remainder elimination (2026-01-22)
  // Per Clay Prewitt: Small remainders (< 0.1 ft) can be eliminated by double-clicking
  // because joint routing and settling absorb minor differences
  heightDismissed: boolean = false;
  eliminatedRemainderFeet: number = 0;  // Track the actual value that was eliminated

  // Toggle for pipes and invert dimensions on profile view (default OFF)
  showPipesAndDimensions: boolean = false;

  // Toggle for showing below-invert foundation portion of base (default OFF — hides it like Titan)
  showBelowInvert: boolean = false;

  // Component catalog side panel (replaces centered modal for better laptop UX)
  catalogPanelOpen: boolean = false;

  // Save guard — prevents concurrent saves
  isSaving: boolean = false;

  // ============================================
  // NEW: Structure-Keyed Edit Maps Architecture
  // ============================================
  // Project-level edits (shared across all structures)
  projectEdits: Partial<IManholeProject> | null = null;

  // Structure-level data maps (keyed by structure UUID)
  // For new unsaved structures, key is prefixed with 'temp-'
  structureEdits: Map<string, Record<string, any>> = new Map();
  pipeEdits: Map<string, IPipeConnection[]> = new Map();
  componentEdits: Map<string, IStackComponent[]> = new Map();
  internalEdits: Map<string, IManholeInternals> = new Map();

  // Track items marked for deletion (by structure ID → entity IDs to delete)
  deletedSequenceIds: Map<string, Set<string>> = new Map();
  deletedPipeIds: Map<string, Set<string>> = new Map();

  // Navigation state for multi-structure session
  currentStructureId: string | null = null;
  structureOrder: string[] = []; // Ordered list of structure UUIDs in session

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false, // Don't make rootStore observable
      selectedStructureCodeId: computed,
      selectedStructureType: computed,
      sealantConfig: computed,
    });
  }

  // Computed getters - derived from URL queryParams (single source of truth)
  get activeProjectId(): string | null {
    return ((this.rootStore as any).routerStore?.queryParams?.projectId as string) ?? null;
  }

  get activeStructureId(): string | null {
    return ((this.rootStore as any).routerStore?.queryParams?.structureId as string) ?? null;
  }

  // Actions - UI state management
  setActiveProject = async (id: string | null) => {
    // Note: activeProjectId is now a computed getter from URL queryParams
    // This method clears state and loads data for the given project
    this.selectedPipeId = null;
    this.editingProject = null;
    this.isDirty = false;
    // Clear sequence state when switching projects
    this.localSequence = null;
    this.currentSequenceId = null;
    this.currentManholeIndex = 0;

    // Clear edit maps when switching projects
    this.projectEdits = null;
    this.structureEdits.clear();
    this.pipeEdits.clear();
    this.componentEdits.clear();
    this.internalEdits.clear();
    // Clear deletion tracking
    this.deletedSequenceIds.clear();
    this.deletedPipeIds.clear();
    this.structureOrder = [];
    this.currentStructureId = null;

    // Load related data from separate services
    if (id) {
      await this.loadProjectRelatedData(id);
      // Initialize structure order for multi-structure navigation
      await this.initializeStructureOrder(id);
    }
  };

  // Load all related data from services (fetch all without query filters)
  loadProjectRelatedData = async (projectId: string) => {
    try {
      // Load ALL structures (not filtered by project)
      if ((this.rootStore as any).manholeStructuresStore) {
        await (this.rootStore as any).manholeStructuresStore.find();
      }

      // Load ALL pipes (not filtered by project)
      if ((this.rootStore as any).manholePipeConnectionsStore) {
        await (this.rootStore as any).manholePipeConnectionsStore.find();
      }

      // Load ALL sequences/components (not filtered by project)
      if ((this.rootStore as any).manholeSequencesStore) {
        await (this.rootStore as any).manholeSequencesStore.find();
      }

      // Load ALL internals (not filtered by project)
      if ((this.rootStore as any).manholeInternalsStore) {
        await (this.rootStore as any).manholeInternalsStore.find();
      }
    } catch (error: any) {
      console.error("Failed to load project related data:", error);
    }
  };

  setSelectedPipe = (id: string | null) => {
    this.selectedPipeId = id;
  };

  setCurrentView = (view: "side" | "top") => {
    this.currentView = view;
  };

  updateZoom = (level: number) => {
    this.zoomLevel = Math.max(0.1, Math.min(5, level));
  };

  updatePan = (x: number, y: number) => {
    this.panPosition = { x, y };
  };

  resetView = () => {
    this.zoomLevel = 1;
    this.panPosition = { x: 0, y: 0 };
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  get canAutoSave(): boolean {
    const project = this.editingProject;
    if (!project) return false;

    const hasJobName = !!project.jobName?.trim();
    const hasCustomer = !!project.customerName?.trim();

    return hasJobName && hasCustomer;
  }

  /**
   * Switch tabs in Project Configuration (Project / Structure / Pipes / Internals / Components).
   * Per Phase 2 plan: Save the current structure before switching tabs to prevent data loss.
   * This is awaited so the tab doesn't switch until the save completes.
   */
  setActiveConfigTab = async (tabIndex: number) => {
    // Save on every tab change — prevents data loss Clay described
    await this.saveCurrentStructureIfDirty();

    const tabName = TAB_NAMES[tabIndex] || 'project';
    const routerStore = (this.rootStore as any).routerStore;
    const currentQP = routerStore?.queryParams || {};

    // Navigate — this updates the URL, which updates the computed getter
    routerStore.goTo("Manhole Designer", null, {
      ...currentQP,
      configTab: tabName,
    });

    // Auto-switch canvas view based on tab
    // TAB ORDER: 0=Project, 1=Structure, 2=Pipes, 3=Internals, 4=Components
    runInAction(() => {
      if (tabIndex === 4) {
        this.canvasViewMode = "side";
      } else {
        this.canvasViewMode = "top";
      }
    });
  };

  setCanvasViewMode = (mode: "side" | "top") => {
    this.canvasViewMode = mode;
  };

  // Set selected component for per-component top view (coring reference)
  setSelectedTopViewComponent = (index: number | null) => {
    this.selectedTopViewComponentIndex = index;
  };

  // Toggle pipes and invert dimensions on profile view
  setShowPipesAndDimensions = (show: boolean) => {
    this.showPipesAndDimensions = show;
  };

  togglePipesAndDimensions = () => {
    this.showPipesAndDimensions = !this.showPipesAndDimensions;
  };

  toggleBelowInvert = () => {
    this.showBelowInvert = !this.showBelowInvert;
  };

  openCatalogPanel = () => {
    this.catalogPanelOpen = true;
  };

  closeCatalogPanel = () => {
    this.catalogPanelOpen = false;
  };

  // NEW: Sealant configuration
  setSealantConfig = (config: ISealantConfig) => {
    if (this.currentStructureId) {
      const edits = this.structureEdits.get(this.currentStructureId) || {};
      this.structureEdits.set(this.currentStructureId, {
        ...edits,
        sealantType: config.type,
        gasketSize: config.gasketSize,
      });
    }
    this.isDirty = true;
  };

  /**
   * Remainder elimination threshold (in feet).
   * Per Clay Prewitt (2026-01-22): Small remainders that can't be achieved
   * with available ring sizes are absorbed during installation.
   */
  static readonly REMAINDER_ELIMINATION_THRESHOLD = 0.167; // 2 inches

  /**
   * Eliminate remaining height (dismiss small remainders).
   * Per Clay Prewitt (2026-01-22): "We double click on it. And it just takes
   * that .04 and gets rid of it... by the time it stacks, and it routes
   * together and all that stuff, you're probably going to pick that .04 anyway."
   *
   * @param force - If true, eliminate even if above threshold (for manual override)
   * @returns true if elimination was successful, false if remainder too large
   */
  eliminateRemainder = (force: boolean = false): boolean => {
    const remaining = this.remainingRimToInvert;

    // Only allow elimination if within threshold (or forced)
    if (!force && remaining > ManholeViewStore.REMAINDER_ELIMINATION_THRESHOLD) {
      console.warn(
        `Cannot eliminate remainder of ${remaining.toFixed(3)} ft - ` +
        `exceeds threshold of ${ManholeViewStore.REMAINDER_ELIMINATION_THRESHOLD} ft`
      );
      return false;
    }

    this.heightDismissed = true;
    this.eliminatedRemainderFeet = remaining;
    this.isDirty = true;
    return true;
  };

  // Legacy alias for backwards compatibility
  dismissRemainingHeight = () => {
    this.eliminateRemainder(true); // Force elimination for legacy calls
  };

  resetHeightDismiss = () => {
    this.heightDismissed = false;
    this.eliminatedRemainderFeet = 0;
  };

  /**
   * Reset the current structure completely.
   * Per Clay Prewitt (2026-01-22):
   * "Have some kind of ability to say, you know, we're starting manhole three over again.
   * We're not, you know, we're going to start it over and it deletes all this information."
   *
   * Clears: components, pipes, internals, structure code selection.
   * Keeps: structure ID, position in sequence.
   */
  resetCurrentStructure = () => {
    const structureId = this.currentStructureId;
    if (!structureId) return;

    // Clear all component/pipe/internal edits for this structure
    this.componentEdits.set(structureId, []);
    this.pipeEdits.set(structureId, []);
    this.internalEdits.set(structureId, {} as any);

    // Track all existing pipes and sequences for deletion on next save
    const currentPipes = this.currentPipes;
    if (currentPipes.length > 0) {
      const pipeIds = currentPipes
        .filter((p) => p.id && !p.id.startsWith("temp-"))
        .map((p) => p.id!);
      if (pipeIds.length > 0) {
        this.deletedPipeIds.set(structureId, pipeIds);
      }
    }

    const currentComponents = this.currentComponents;
    if (currentComponents.length > 0) {
      const sequenceIds = currentComponents
        .filter((c) => c.sequenceId && !c.sequenceId.startsWith("temp-"))
        .map((c) => c.sequenceId!);
      if (sequenceIds.length > 0) {
        this.deletedSequenceIds.set(structureId, sequenceIds);
      }
    }

    // Clear structure-level edits but keep the structure itself
    this.structureEdits.delete(structureId);

    // Reset state
    this.heightDismissed = false;
    this.eliminatedRemainderFeet = 0;

    this.isDirty = true;
    console.log("[RESET] Structure reset:", structureId);
  };

  /**
   * Check if remainder can be eliminated (within threshold).
   */
  get canEliminateRemainder(): boolean {
    const remaining = this.remainingRimToInvert;
    return remaining > 0 && remaining <= ManholeViewStore.REMAINDER_ELIMINATION_THRESHOLD;
  }

  /**
   * Check if stack is complete (no remaining height or eliminated).
   */
  get isStackComplete(): boolean {
    return this.heightDismissed || this.remainingRimToInvert <= 0;
  }

  // ============================================
  // Sump Logic for Shallow Manholes
  // ============================================

  /**
   * Set sump depth for current structure.
   * Per Clay Prewitt (2026-01-22): When remaining goes negative (structure too tall),
   * user enters sump to push the base down.
   */
  setSumpDepth = (depthFeet: number) => {
    const structureId = this.currentStructureId;
    if (!structureId) return;

    const currentEdits = this.structureEdits.get(structureId) || {};
    this.structureEdits.set(structureId, {
      ...currentEdits,
      sumpDepthFeet: depthFeet,
    });
    this.isDirty = true;
  };

  /**
   * Get the current sump depth for the current structure.
   */
  get currentSumpDepthFeet(): number {
    const structureId = this.currentStructureId;
    if (!structureId) return 0;
    const edits = this.structureEdits.get(structureId);
    return (edits as any)?.sumpDepthFeet || 0;
  }

  /**
   * Whether the structure requires sump (remaining went negative before sump).
   * This means the selected components exceed the rim-to-invert distance.
   */
  get requiresSump(): boolean {
    const rimToInvert = this.rimToInvert;
    if (rimToInvert <= 0) return false;
    const filledHeight = this.rawHeightFilledByComponents;
    return filledHeight > rimToInvert;
  }

  /**
   * The raw deficit before sump is applied (how much too tall the structure is).
   * Positive means the structure exceeds rim-to-invert by this many feet.
   */
  get rawDeficitFeet(): number {
    if (!this.requiresSump) return 0;
    return this.rawHeightFilledByComponents - this.rimToInvert;
  }

  /**
   * Core drill shift in inches due to sump.
   * Per Clay (2026-01-22): "it says up 11.25 inches. So now I'm core drilling
   * my hole up 11.25 inches."
   */
  get coreDrillShiftInches(): number {
    return this.currentSumpDepthFeet * 12;
  }

  // NEW: Sequence navigation actions
  setCurrentSequence = (sequenceId: string | null) => {
    this.currentSequenceId = sequenceId;
    this.currentManholeIndex = 0;
  };

  // Get current sequence from local state
  // NOTE: Multi-manhole sequences are managed locally, not via API
  get currentSequence(): IManholeSequence | null {
    return this.localSequence;
  }

  // Get total manholes in current sequence
  get manholeCount(): number {
    return this.currentSequence?.manholes?.length || 1;
  }

  // Check if on first manhole
  get isFirstManhole(): boolean {
    return this.currentManholeIndex === 0;
  }

  // Check if on last manhole
  get isLastManhole(): boolean {
    if (!this.currentSequence) return true;
    return this.currentManholeIndex >= this.currentSequence.manholes.length - 1;
  }

  // Navigate to previous manhole in sequence
  goToPreviousManhole = async () => {
    if (this.isFirstManhole) return;

    // Save current manhole first
    if (this.isDirty) {
      await this.saveProject();
    }

    const sequence = this.currentSequence;
    const newIndex = this.currentManholeIndex - 1;

    if (sequence && sequence.manholes[newIndex]) {
      const manholeEntry = sequence.manholes[newIndex];
      runInAction(() => {
        this.currentManholeIndex = newIndex;
      });
      // Load the structure data for this manhole
      await this.loadStructureById(
        manholeEntry.structureId || manholeEntry.manholeId
      );
    }
  };

  // Navigate to next manhole in sequence (creates new if at end)
  goToNextManhole = async () => {
    // Save current manhole first
    if (this.isDirty) {
      await this.saveProject();
    }

    const sequence = this.currentSequence;

    if (!sequence) {
      // No sequence - create one with current manhole
      await this.createNewSequence();
      return;
    }

    if (this.currentManholeIndex < sequence.manholes.length - 1) {
      // Navigate to existing next manhole
      const newIndex = this.currentManholeIndex + 1;
      const manholeEntry = sequence.manholes[newIndex];
      runInAction(() => {
        this.currentManholeIndex = newIndex;
      });
      // Load the structure data for this manhole
      await this.loadStructureById(
        manholeEntry.structureId || manholeEntry.manholeId
      );
    } else {
      // At end - create new manhole in sequence
      await this.createNewManholeInSequence();
    }
  };

  // Navigate to a specific manhole by index
  goToManholeByIndex = async (index: number) => {
    const sequence = this.currentSequence;
    if (!sequence || index < 0 || index >= sequence.manholes.length) return;
    if (index === this.currentManholeIndex) return;

    // Save current manhole first
    if (this.isDirty) {
      await this.saveProject();
    }

    const manholeEntry = sequence.manholes[index];
    if (manholeEntry) {
      runInAction(() => {
        this.currentManholeIndex = index;
      });
      // Load the structure data for this manhole
      await this.loadStructureById(
        manholeEntry.structureId || manholeEntry.manholeId
      );
    }
  };

  // Load a specific structure by ID (for multi-manhole navigation)
  loadStructureById = async (structureId: string) => {
    this.setLoading(true);

    try {
      // Preserve current project-level data
      const currentProject = this.editingProject || this.activeProject;
      const projectId =
        currentProject?.projectId || currentProject?.id || this.activeProjectId;
      const projectLevelData = currentProject
        ? {
            projectId: currentProject.projectId || currentProject.id,
            customerName: currentProject.customerName,
            jobName: currentProject.jobName,
            jobIdentifier: (currentProject as any).jobIdentifier,
            accountId: currentProject.accountId,
            city: currentProject.city,
            state: currentProject.state,
            salesperson: currentProject.salesperson,
            phase: currentProject.phase,
            revision: currentProject.revision,
            status: currentProject.status,
            type: currentProject.type,
          }
        : null;

      // Find the structure in the store by human-readable structureId or UUID
      const structure = (
        this.rootStore as any
      ).manholeStructuresStore?.entities?.find(
        (s: any) => s.id === structureId || s.structureId === structureId
      );

      // Use manholeProjectId for queries (valid UUID), not human-readable structureId
      if (projectId) {
        // Load related data for this project
        if ((this.rootStore as any).manholePipeConnectionsStore) {
          await (this.rootStore as any).manholePipeConnectionsStore.find({
            query: { manholeProjectId: projectId },
          });
        }

        if ((this.rootStore as any).manholeSequencesStore) {
          await (this.rootStore as any).manholeSequencesStore.find({
            query: { manholeProjectId: projectId, $sort: { sequenceOrder: 1 } },
          });
        }

        if ((this.rootStore as any).manholeInternalsStore) {
          await (this.rootStore as any).manholeInternalsStore.find({
            query: { manholeProjectId: projectId },
          });
        }
      }

      // Get the loaded data - filter by manholeProjectId
      const pipes =
        (this.rootStore as any).manholePipeConnectionsStore?.entities?.filter(
          (p: any) => p.manholeProjectId === projectId
        ) || [];

      const sequences =
        (this.rootStore as any).manholeSequencesStore?.entities?.filter(
          (s: any) => s.manholeProjectId === projectId
        ) || [];

      const internalsData = (
        this.rootStore as any
      ).manholeInternalsStore?.entities?.find(
        (i: any) => i.manholeProjectId === projectId
      );

      // Transform sequences to components
      const components = this.transformSequencesToComponents(sequences);

      // Transform internals to UI format
      const internals = internalsData
        ? {
            hasSteps: internalsData.includeSteps || false,
            hasLadder:
              internalsData.ladderType && internalsData.ladderType !== "none",
            stepAngleDegrees: internalsData.stepAngle || 180,
            stepType: internalsData.stepType || "standard",
            ladderType: internalsData.ladderType || "none",
            hasPreChannelInvert: internalsData.preChannelInvert || false,
            hasGULiner: internalsData.guLiner || false,
            hasCustomInternal: internalsData.custom || false,
            customInternalDescription: internalsData.customDescription || "",
          }
        : null;

      runInAction(() => {
        // Create editing project with combined project + structure data
        this.editingProject = {
          ...projectLevelData,
          // Structure-level fields from the loaded structure
          structureId: structure?.structureId || structureId,
          station: structure?.station || "",
          structureSubType: structure?.structureSubType || "Type 1",
          catchDepthFeet: structure?.catchDepthFeet || 0,
          rimElevation: structure?.rimElevation || 0,
          castingId: structure?.castingId || "",
          castingType: structure?.castingType || "",
          formAssignment: structure?.formAssignment || "",
          hatchType: structure?.hatchType || "",
          manholeStructure: {
            rimElevation: structure?.rimElevation || 0,
            pipes: pipes,
            components: components,
            internals: internals || {
              hasSteps: false,
              hasLadder: false,
              stepAngleDegrees: 180,
            },
          },
        } as any;

        // Note: activeStructureId is computed from URL queryParams
        // Navigation should happen at the caller level if URL update is needed
        this.isDirty = false;
      });
    } catch (error: any) {
      console.error("Failed to load structure:", error);
      this.setError("Failed to load manhole structure");
    } finally {
      this.setLoading(false);
    }
  };

  // Helper to transform sequence records to IStackComponent format
  transformSequencesToComponents = (sequences: any[]): IStackComponent[] => {
    const sorted = [...sequences].sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder
    );
    let cumulativeHeight = 0;

    // Get manhole components for componentCode lookup
    // The seq.componentCode may contain a database ID instead of actual code
    const manholeComponents =
      (this.rootStore as any).manholeComponentsStore?.entities || [];

    return sorted.map((seq) => {
      // Look up actual componentCode from manhole-components by ID
      const matchedComponent = manholeComponents.find(
        (mc: any) => mc.id === seq.componentCode
      );
      const actualComponentCode =
        matchedComponent?.componentCode || seq.componentCode || seq.id;

      const component: IStackComponent = {
        instanceId: seq.id,
        product: {
          id: seq.componentCode || seq.id,
          componentCode: actualComponentCode,
          name: seq.componentName,
          description: seq.componentName,
          // Use UPPERCASE for new componentType, preserve for display
          type: seq.componentType?.toUpperCase() || seq.componentType,
          heightInches: seq.heightInches,
          diameterInches: seq.diameterInches || 48,
          wallThicknessInches: 4,
          weightLbs: seq.weight || 0,
          weight: seq.weight || 0,
          // Support both new and legacy cost fields
          estimatedCost: seq.estimatedCost ?? seq.cost ?? 0,
          costUSD: seq.estimatedCost ?? seq.cost ?? 0,
        },
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += seq.heightInches / 12;
      return component;
    });
  };

  // Create a new manhole sequence (managed locally, not via API)
  // NOTE: The API's manhole-sequences service stores component stack data,
  // not multi-manhole project sequences, so we manage this in local state
  createNewSequence = async () => {
    this.setLoading(true);

    try {
      // Create sequence with current project as first manhole
      const currentProjectId =
        this.activeProjectId ||
        this.editingProject?.id ||
        this.editingProject?.projectId;
      const currentStructureId =
        this.editingProject?.structureId ||
        this.activeProject?.structureId ||
        "";
      const sequenceId = uuid();

      const newSequence: IManholeSequence = {
        id: sequenceId,
        projectId: currentProjectId || uuid(),
        accountId: (this.editingProject?.accountId as number) || undefined,
        jobId: (this.editingProject?.jobId as number) || undefined,
        sequenceName: `Sequence - ${new Date().toLocaleDateString()}`,
        manholes: currentProjectId
          ? [
              {
                manholeId: currentProjectId,
                sequenceOrder: 1,
                structureId: currentStructureId,
                status: "draft",
              },
            ]
          : [],
        currentIndex: 0,
        totalManholes: currentProjectId ? 1 : 0,
        status: "draft",
      };

      runInAction(() => {
        this.localSequence = newSequence;
        this.currentSequenceId = sequenceId;
        this.currentManholeIndex = 0;
      });

      // If no current project, create the first manhole
      if (!currentProjectId) {
        await this.createNewManholeInSequence();
      }
    } catch (error: any) {
      console.error("Failed to create sequence:", error);
      this.setError("Failed to create manhole sequence");
    } finally {
      this.setLoading(false);
    }
  };

  // Create a new structure within the current project (multi-manhole flow)
  createNewManholeInSequence = async () => {
    const sequence = this.currentSequence;

    this.setLoading(true);

    try {
      // Get the current project ID - we'll create a new STRUCTURE, not a new project
      const currentProjectId =
        this.activeProjectId ||
        this.editingProject?.id ||
        this.editingProject?.projectId;

      if (!currentProjectId) {
        // No project yet - create one first
        await this.saveProject();
      }

      const projectId = this.activeProjectId || currentProjectId;
      const nextOrder = (sequence?.manholes?.length || 0) + 1;
      const defaultStructureId = `MH-${nextOrder}`;

      // Preserve project-level data from current project
      const currentProject = this.editingProject || this.activeProject;
      const projectLevelData = currentProject
        ? {
            projectId: projectId,
            id: projectId,
            customerName: currentProject.customerName,
            jobName: currentProject.jobName,
            jobIdentifier: (currentProject as any).jobIdentifier,
            accountId: currentProject.accountId,
            city: currentProject.city,
            state: currentProject.state,
            salesperson: currentProject.salesperson,
            phase: currentProject.phase,
            revision: currentProject.revision,
            status: currentProject.status,
            type: currentProject.type,
          }
        : null;

      // Create a new structure record in the database
      let newStructure: any = null;
      if ((this.rootStore as any).manholeStructuresStore && projectId) {
        newStructure = await (
          this.rootStore as any
        ).manholeStructuresStore.create({
          projectId,
          structureId: defaultStructureId,
          station: "",
          type: currentProject?.type || "Sanitary",
          status: "draft",
        });
      }

      runInAction(() => {
        // Create new editing project preserving project-level data but with fresh structure data
        this.editingProject = {
          ...projectLevelData,
          // New structure-specific fields
          structureId: defaultStructureId,
          station: "",
          structureSubType: "Type 1",
          catchDepthFeet: 0,
          rimElevation: 0,
          castingId: "",
          castingType: "",
          formAssignment: "",
          hatchType: "",
          // Fresh manhole structure (no pipes, components, internals)
          manholeStructure: {
            rimElevation: 0,
            pipes: [],
            components: [],
            internals: {
              hasSteps: false,
              hasLadder: false,
              stepAngleDegrees: 180,
            },
          },
        } as any;

        this.isDirty = true;
      });

      // Navigate to the new structure if it was saved to database
      if (newStructure?.id && this.activeProjectId && (this.rootStore as any).routerStore) {
        (this.rootStore as any).routerStore.goTo("Manhole Designer", null, {
          projectId: this.activeProjectId,
          structureId: newStructure.id,
        });
      }

      // Add to local sequence for navigation tracking
      if (sequence && this.localSequence) {
        const newEntry: IManholeSequenceEntry = {
          manholeId: newStructure?.id || uuid(),
          sequenceOrder: nextOrder,
          structureId: defaultStructureId,
          status: "draft",
        };

        runInAction(() => {
          const updatedManholes = [...(sequence.manholes || []), newEntry];
          this.localSequence = {
            ...this.localSequence!,
            manholes: updatedManholes,
            totalManholes: updatedManholes.length,
            currentIndex: updatedManholes.length - 1,
          };
          this.currentManholeIndex = updatedManholes.length - 1;
        });
      }
    } catch (error: any) {
      console.error("Failed to create structure in sequence:", error);
      this.setError("Failed to create new manhole structure");
    } finally {
      this.setLoading(false);
    }
  };

  // Mark current manhole as complete in sequence (managed locally)
  markManholeComplete = async () => {
    console.log("[FINISH] markManholeComplete called", {
      currentManholeIndex: this.currentManholeIndex,
      hasSequence: !!this.currentSequence,
      hasLocalSequence: !!this.localSequence,
      sequenceManholes: this.currentSequence?.manholes?.length || 0,
    });

    const sequence = this.currentSequence;

    if (!sequence || !this.localSequence) {
      console.log(
        "[FINISH] Aborting markManholeComplete - no sequence or localSequence"
      );
      return;
    }

    runInAction(() => {
      const updatedManholes = [...sequence.manholes];
      if (updatedManholes[this.currentManholeIndex]) {
        updatedManholes[this.currentManholeIndex] = {
          ...updatedManholes[this.currentManholeIndex],
          status: "complete",
          completedAt: new Date(),
        };

        this.localSequence = {
          ...this.localSequence!,
          manholes: updatedManholes,
          status: updatedManholes.every((m) => m.status === "complete")
            ? "complete"
            : "in_progress",
        };

        console.log("[FINISH] Marked manhole complete:", {
          manholeIndex: this.currentManholeIndex,
          newSequenceStatus: this.localSequence.status,
        });
      }
    });
  };

  // Project editing actions
  startEditingProject = (
    project?: IManholeProject,
    accountId?: string | number
  ) => {
    if (project) {
      // Get rimElevation from current structure if available (it's saved in manholeStructuresStore, not project)
      const currentStructureData = this.getStructureFromStore(
        this.currentStructureId || ""
      );
      const structureEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)
        : undefined;
      const structureRimElevation =
        structureEdits?.rimElevation ?? currentStructureData?.rimElevation;

      // Ensure the project has a proper manholeStructure when editing
      this.editingProject = {
        ...project,
        manholeStructure: project.manholeStructure || {
          rimElevation: structureRimElevation ?? project.rimElevation ?? 0,
          pipes: [],
          components: [],
          internals: {
            hasSteps: false,
            hasLadder: false,
            stepAngleDegrees: 180,
            stepType: "standard",
          },
        },
      };
      // Also update rimElevation if we have a value from the structure store
      if (
        structureRimElevation !== undefined &&
        this.editingProject.manholeStructure
      ) {
        this.editingProject.manholeStructure.rimElevation =
          structureRimElevation;
      }
      // Ensure components array exists
      if (!this.editingProject.manholeStructure.components) {
        this.editingProject.manholeStructure.components = [];
      }
      // Ensure all pipes have IDs for editing
      if (this.editingProject.manholeStructure.pipes) {
        this.editingProject.manholeStructure.pipes =
          this.editingProject.manholeStructure.pipes.map((pipe) => {
            if (!pipe.id) {
              return {
                ...pipe,
                id: `temp-pipe-${Date.now()}-${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
              };
            }
            return pipe;
          });
      }
    } else {
      // New project defaults for estimating phase (no job yet)
      // Auto-generate job identifier in YY-XXXXX format
      const existingProjects =
        (this.rootStore as any).manholeProjectsStore?.entities || [];
      const existingIdentifiers = existingProjects
        .map((p: any) => p.jobIdentifier)
        .filter((id: string | undefined) => id);
      const nextJobIdentifier = generateJobIdentifier(existingIdentifiers);

      this.editingProject = {
        accountId: accountId || undefined, // Optional for estimates
        jobId: undefined, // Optional: only set after award
        customerName: "", // Will be populated from account
        customerContact: "",
        jobName: "", // Empty until awarded
        jobNumber: "", // Empty until awarded
        jobIdentifier: nextJobIdentifier, // Auto-generated: YY-XXXXX format
        structureId: "",
        station: "",
        type: "Sanitary",
        structureType: "Sanitary",
        structureSubType: "Type 1", // NEW: Default to Type 1 (no catch)
        catchDepthFeet: 0, // NEW: No catch depth by default
        city: "",
        state: "",
        salesperson: "",
        estimator: "", // NEW: Estimator field
        phase: "", // NEW: Empty phase field
        revision: "A",
        status: "draft", // Start as draft estimate
        castingId: "", // NEW: No casting selected by default
        formAssignment: "", // NEW: No form assigned by default
        hatchType: "", // NEW: No hatch specified by default
        paymentTerms: "COD", // Default payment terms
        manholeStructure: {
          rimElevation: 0,
          pipes: [],
          components: [], // Initialize components array
          internals: {
            hasSteps: false,
            hasLadder: false,
            stepAngleDegrees: 180,
            stepType: "standard",
          },
          revision: "A", // Structure-level revision
          notes: "", // Contractor advisory notes
        },
      };
    }
    this.isDirty = false;

    // For new projects, create an initial temp structure so all edits
    // flow through the structure-keyed system and save button shows
    if (!project) {
      this.createNewStructure();
    }
  };

  updateEditingProject = (updates: Partial<IManholeProject>) => {
    if (!this.editingProject) return;

    runInAction(() => {
      this.editingProject = { ...this.editingProject, ...updates };
      this.isDirty = true;

      // Also track structure edits for save optimization
      // Structure fields that need to be tracked for patching
      const structureFields = [
        "structureId",
        "rimElevation",
        "catchDepthFeet",
        "structureSubType",
        "castingId",
        "castingType",
        "formAssignment",
        "hatchType",
        "hatchPoNumber",
        "sealantType",
        "gasketSize",
        "station",
        "type",
        "status",
        "structureCodeId",
        "customerNeededDate",
        "priority",
      ];

      // Track project-level metadata edits (fields NOT in structureFields)
      const structureFieldSet = new Set([...structureFields, "manholeStructure"]);
      for (const field of Object.keys(updates)) {
        if (!structureFieldSet.has(field)) {
          this.projectEdits = {
            ...this.projectEdits,
            [field]: (updates as any)[field],
          };
        }
      }

      if (this.currentStructureId) {
        const currentEdits =
          this.structureEdits.get(this.currentStructureId) || {};
        let hasStructureUpdates = false;

        // Check for direct structure field updates
        for (const field of structureFields) {
          if (field in updates) {
            currentEdits[field] = (updates as any)[field];
            hasStructureUpdates = true;
          }
        }

        // Check for nested manholeStructure updates
        if (updates.manholeStructure) {
          for (const field of structureFields) {
            if (field in updates.manholeStructure) {
              currentEdits[field] = (updates.manholeStructure as any)[field];
              hasStructureUpdates = true;
            }
          }
        }

        if (hasStructureUpdates) {
          this.structureEdits.set(this.currentStructureId, currentEdits);
        }
      }
    });
  };

  // Dialog form data management
  setDialogFormData = (data: Record<string, any>) => {
    this.dialogFormData = data;
  };

  updateDialogFormData = (updates: Record<string, any>) => {
    this.dialogFormData = { ...this.dialogFormData, ...updates };
  };

  clearDialogFormData = () => {
    this.dialogFormData = {};
  };

  cancelEditing = () => {
    this.editingProject = null;
    this.isDirty = false;
  };

  // Pipe management actions
  addPipe = async () => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.addPipeToCurrentStructure();
      return;
    }

    if (!this.editingProject?.manholeStructure) return;

    const pipeCount = this.editingProject.manholeStructure.pipes.length;
    const isFirstPipe = pipeCount === 0;

    // Start with a temporary pipe for the UI
    let newPipe: IPipeConnection = {
      id: `temp-pipe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Temporary ID for editing
      manholeProjectId: this.activeProjectId || "", // Will be set on save
      pipeNumber: pipeCount + 1,
      invertElevation: 0,
      pipeDiameterInches: 8,
      pipeMaterial: "PVC 3034",
      angleDegrees: 0, // All pipes default to 0°
      direction: isFirstPipe ? "outlet" : "inlet", // First pipe is always outlet
      connectionType: "hole", // Default connection type
      holeType: "40612AW",
      holeSizeInches: 12,
    };

    // Only create in backend if we have a saved project with valid UUID
    if (
      this.activeProjectId &&
      (this.rootStore as any).manholePipeConnectionsStore
    ) {
      try {
        // Create the pipe data for backend, only including optional fields if they have values
        const pipeDataForBackend: Record<string, unknown> = {
          manholeProjectId: this.activeProjectId,
          pipeNumber: newPipe.pipeNumber,
          invertElevation: newPipe.invertElevation,
          pipeDiameterInches: newPipe.pipeDiameterInches,
        };

        if (newPipe.pipeMaterial)
          pipeDataForBackend.pipeMaterial = newPipe.pipeMaterial;
        if (newPipe.angleDegrees !== undefined)
          pipeDataForBackend.angleDegrees = newPipe.angleDegrees;
        if (newPipe.holeType) pipeDataForBackend.holeType = newPipe.holeType;
        if (newPipe.holeSizeInches !== undefined)
          pipeDataForBackend.holeSizeInches = newPipe.holeSizeInches;

        const createdPipe = await (
          this.rootStore as any
        ).manholePipeConnectionsStore.create(pipeDataForBackend);
        // Use the pipe returned from backend with real ID
        if (createdPipe && createdPipe.id) {
          newPipe = createdPipe;
        }
      } catch (error) {
        console.error(
          "Failed to create pipe in backend, using temporary pipe:",
          error
        );
        // Continue with temporary pipe if backend fails
      }
    }

    runInAction(() => {
      // Create new array to trigger MobX reactivity
      this.editingProject!.manholeStructure!.pipes = [
        ...this.editingProject!.manholeStructure!.pipes,
        newPipe,
      ];
      this.isDirty = true;
    });
  };

  updatePipe = async (pipeId: string, updates: Partial<IPipeConnection>) => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.updatePipeInCurrentStructure(pipeId, updates);
      return;
    }

    if (!this.editingProject?.manholeStructure) return;

    const pipeIndex = this.editingProject.manholeStructure.pipes.findIndex(
      (p) => p.id === pipeId
    );
    if (pipeIndex !== -1) {
      const pipe = this.editingProject.manholeStructure.pipes[pipeIndex];

      // Safeguard: First pipe (pipeNumber === 1) must remain outlet at 0°
      if (pipe.pipeNumber === 1) {
        delete updates.angleDegrees; // Remove angle from updates
        delete updates.direction; // Remove direction from updates
        // Ensure it stays as outlet at 0° even if somehow passed in
        updates = {
          ...updates,
          angleDegrees: 0,
          direction: "outlet",
        };
      }

      // Skip backend updates during editing - all pipes will be saved when project is saved
      // Only update backend if explicitly saving (not during normal editing)
      // Backend updates will happen when the entire project is saved
      const shouldUpdateBackend = false; // Disable individual pipe API calls

      if (
        shouldUpdateBackend &&
        this.activeProjectId &&
        pipeId &&
        !pipeId.startsWith("temp-") &&
        (this.rootStore as any).manholePipeConnectionsStore
      ) {
        try {
          await (this.rootStore as any).manholePipeConnectionsStore.patch(
            pipeId,
            updates
          );
        } catch (error) {
          console.error("Failed to update pipe in backend:", error);
        }
      }

      runInAction(() => {
        const pipes = [...this.editingProject!.manholeStructure!.pipes];
        pipes[pipeIndex] = {
          ...pipes[pipeIndex],
          ...updates,
        };
        this.editingProject!.manholeStructure!.pipes = pipes;
        this.isDirty = true;
      });
    }
  };

  removePipe = async (pipeId: string) => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.removePipeFromCurrentStructure(pipeId);
      return;
    }

    if (!this.editingProject?.manholeStructure) return;

    // Try to remove from backend if it's not a temporary pipe
    // But don't block the UI removal if backend fails
    if (
      this.activeProjectId &&
      pipeId &&
      !pipeId.startsWith("temp-") &&
      (this.rootStore as any).manholePipeConnectionsStore
    ) {
      try {
        // Check if the pipe actually exists in the store before trying to remove it
        const pipeStore = (this.rootStore as any).manholePipeConnectionsStore;
        const existingPipe = pipeStore.entities.find(
          (p: any) => p.id === pipeId
        );

        if (existingPipe) {
          await pipeStore.remove(pipeId);
        } else {
          console.log(
            `Pipe ${pipeId} not found in backend, removing locally only`
          );
        }
      } catch (error: any) {
        // Log the error but continue with local removal
        if (
          error?.code === 404 ||
          error?.message?.includes("No record found")
        ) {
          console.log(
            `Pipe ${pipeId} doesn't exist in backend, removing locally only`
          );
        } else {
          console.error("Failed to remove pipe from backend:", error);
        }
      }
    }

    runInAction(() => {
      // Always remove the pipe locally regardless of backend success
      const filteredPipes = this.editingProject!.manholeStructure!.pipes.filter(
        (p) => p.id !== pipeId
      ).map((pipe, index) => ({
        ...pipe,
        pipeNumber: index + 1,
      }));

      this.editingProject!.manholeStructure!.pipes = filteredPipes;
      this.isDirty = true;
    });
  };

  // Component stack management
  addComponent = (product: ICatalogProduct) => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.addComponentToCurrentStructure(product);
      return;
    }

    if (!this.editingProject?.manholeStructure) return;

    if (!this.editingProject.manholeStructure.components) {
      this.editingProject.manholeStructure.components = [];
    }

    const components = this.editingProject.manholeStructure.components;

    // Calculate correct insert position based on component type ordering
    // This ensures BASE is always at bottom, CONE/FLAT_TOP above risers, etc.
    const insertPosition = this.calculateInsertPositionByType(
      components,
      product.type
    );

    // Create new component
    const newComponent: IStackComponent = {
      instanceId: `comp-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      product,
      positionInStack: insertPosition,
      bottomElevation: 0, // Will be recalculated
    };

    // Insert at correct position
    components.splice(insertPosition, 0, newComponent);

    // Recalculate all positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  removeComponent = (instanceId: string) => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.removeComponentFromCurrentStructure(instanceId);
      return;
    }

    if (!this.editingProject?.manholeStructure?.components) return;

    const components = this.editingProject.manholeStructure.components;
    const index = components.findIndex((c) => c.instanceId === instanceId);
    if (index === -1) return;

    // Create a new array to trigger MobX reactivity
    this.editingProject.manholeStructure.components = [
      ...components.slice(0, index),
      ...components.slice(index + 1),
    ];

    // Recalculate positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  // Swap a component with a new product (click-to-swap feature)
  swapComponent = (instanceId: string, newProduct: ICatalogProduct) => {
    // Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.swapComponentInCurrentStructure(instanceId, newProduct);
      return;
    }

    if (!this.editingProject?.manholeStructure?.components) return;

    const components = this.editingProject.manholeStructure.components;
    const index = components.findIndex((c) => c.instanceId === instanceId);
    if (index === -1) return;

    // Create updated component with new product, keeping position and elevation
    const updatedComponent: IStackComponent = {
      ...components[index],
      product: newProduct,
    };

    // Create new array with updated component
    this.editingProject.manholeStructure.components = [
      ...components.slice(0, index),
      updatedComponent,
      ...components.slice(index + 1),
    ];

    // Recalculate positions and elevations (heights may have changed)
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  // Swap component in structure-keyed mode
  swapComponentInCurrentStructure = (
    instanceId: string,
    newProduct: ICatalogProduct
  ) => {
    if (!this.currentStructureId) return;

    const components = [
      ...(this.componentEdits.get(this.currentStructureId) ||
        this.currentComponents),
    ];
    const index = components.findIndex((c) => c.instanceId === instanceId);

    if (index === -1) return;

    // Create updated component with new product
    const updatedComponent: IStackComponent = {
      ...components[index],
      product: newProduct,
    };

    components[index] = updatedComponent;

    // Recalculate positions and elevations
    let cumulativeHeight = 0;
    const recalculated = components.map((comp, i) => {
      const updated = {
        ...comp,
        positionInStack: i,
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += getEffectiveHeightInches(comp) / 12;
      return updated;
    });

    runInAction(() => {
      this.componentEdits.set(this.currentStructureId!, recalculated);
    });
  };

  /**
   * Update the height modifier for a specific component in the stack.
   * Per Dec 19 meeting (Justin): allow +/- height adjustment on individual components.
   */
  updateComponentHeightModifier = (instanceId: string, modifierInches: number) => {
    const structureId = this.currentStructureId;
    if (!structureId) return;

    const components = [
      ...(this.componentEdits.get(structureId) || this.currentComponents),
    ];
    const updated = components.map((c) =>
      c.instanceId === instanceId
        ? { ...c, heightModifierInches: modifierInches || 0 }
        : c
    );

    // Recalculate elevations with new effective heights
    let cumulativeHeight = 0;
    const recalculated = updated.map((comp, i) => {
      const result = {
        ...comp,
        positionInStack: i,
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += getEffectiveHeightInches(comp) / 12;
      return result;
    });

    runInAction(() => {
      this.componentEdits.set(structureId, recalculated);
      this.isDirty = true;
    });
  };

  // Component type ordering: BASE(1) → RISER(2) → SECTION(3) → CONE/FLAT_TOP(4) → ADJUST_RING(5) → FRAME/COVER/CASTING(6)
  getComponentTypeOrder = (type: string | undefined): number => {
    const typeOrder: Record<string, number> = {
      BASE: 1,
      RISER: 2,
      SECTION: 3,
      SLAB: 3,
      CONE: 4,
      FLAT_TOP: 4,
      ADJUST_RING: 5,
      RING: 5,
      FRAME: 6,
      COVER: 6,
      CASTING: 6,
      OTHER: 6,
    };
    return typeOrder[type?.toUpperCase() || "OTHER"] || 6;
  };

  // Calculate correct insert position based on component type order
  calculateInsertPositionByType = (
    components: IStackComponent[],
    newType: string | undefined
  ): number => {
    const newTypeOrder = this.getComponentTypeOrder(newType);

    // Find first component with higher type order - insert before it
    for (let i = 0; i < components.length; i++) {
      const existingType = components[i].product?.type;
      const existingOrder = this.getComponentTypeOrder(existingType);

      if (existingOrder > newTypeOrder) {
        return i; // Insert at this index (before this component)
      }
    }

    // Add at end if no higher-order component found
    return components.length;
  };

  // Check if two components can be swapped (respects type ordering)
  canSwapComponents = (
    moving: IStackComponent,
    target: IStackComponent,
    direction: "up" | "down"
  ): boolean => {
    const movingOrder = this.getComponentTypeOrder(moving.product?.type);
    const targetOrder = this.getComponentTypeOrder(target.product?.type);

    // Moving up in display (higher in stack = lower index in array for display, but higher index in storage)
    // In our storage, index 0 = bottom (base), higher index = higher in stack
    // direction 'up' means moving to higher index (higher in stack)
    // direction 'down' means moving to lower index (lower in stack)

    // Can't move a lower-order type above a higher-order type
    // e.g., can't move BASE (1) above RISER (2)
    if (direction === "up" && movingOrder < targetOrder) return false;

    // Can't move a higher-order type below a lower-order type
    // e.g., can't move CONE (4) below RISER (2)
    if (direction === "down" && movingOrder > targetOrder) return false;

    return true;
  };

  moveComponent = (instanceId: string, direction: "up" | "down") => {
    // Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      this.moveComponentInCurrentStructure(instanceId, direction);
      return;
    }

    if (!this.editingProject?.manholeStructure?.components) return;

    const components = [...this.editingProject.manholeStructure.components]; // Create a copy
    const index = components.findIndex((c) => c.instanceId === instanceId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index + 1 : index - 1;
    if (newIndex < 0 || newIndex >= components.length) return;

    // Validate move doesn't violate type ordering
    const movingComponent = components[index];
    const targetComponent = components[newIndex];

    if (!this.canSwapComponents(movingComponent, targetComponent, direction)) {
      // Can't swap - violates type ordering
      return;
    }

    // Swap components in the copy
    [components[index], components[newIndex]] = [
      components[newIndex],
      components[index],
    ];

    // Replace the entire array to trigger MobX reactivity
    this.editingProject.manholeStructure.components = components;

    // Recalculate positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  // Move a component within the current structure (multi-structure mode)
  moveComponentInCurrentStructure = (
    instanceId: string,
    direction: "up" | "down"
  ) => {
    if (!this.currentStructureId) return;

    const components = [
      ...(this.componentEdits.get(this.currentStructureId) ||
        this.currentComponents),
    ];
    const index = components.findIndex((c) => c.instanceId === instanceId);

    if (index === -1) return;

    const newIndex = direction === "up" ? index + 1 : index - 1;

    // Bounds check
    if (newIndex < 0 || newIndex >= components.length) return;

    // Validate move doesn't violate type ordering
    const movingComponent = components[index];
    const targetComponent = components[newIndex];

    if (!this.canSwapComponents(movingComponent, targetComponent, direction)) {
      // Can't swap - violates type ordering
      return;
    }

    // Swap components
    [components[index], components[newIndex]] = [
      components[newIndex],
      components[index],
    ];

    // Recalculate positions and elevations
    let cumulativeHeight = 0;
    const recalculated = components.map((comp, i) => {
      const updated = {
        ...comp,
        positionInStack: i,
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += getEffectiveHeightInches(comp) / 12;
      return updated;
    });

    runInAction(() => {
      this.componentEdits.set(this.currentStructureId!, recalculated);
    });
  };

  // Clear all components from the stack
  clearComponents = () => {
    if (!this.editingProject?.manholeStructure) return;

    if (!this.editingProject) {
      this.startEditingProject(this.activeProject || undefined);
    }

    if (this.editingProject?.manholeStructure) {
      // Clear components
      this.editingProject.manholeStructure.components = [];

      // Also clear pipes and internals since they depend on having components
      this.editingProject.manholeStructure.pipes = [];
      this.editingProject.manholeStructure.internals = {
        hasSteps: false,
        hasLadder: false,
        stepAngleDegrees: 180,
        stepType: "standard",
      };

      this.recalculateComponentPositions();
      this.isDirty = true;
    }
  };

  // Alias for addComponent for clarity in ComponentSelector
  addComponentToStack = (product: ICatalogProduct) => {
    this.addComponent(product);
  };

  // Insert a component at a specific position in the stack
  insertComponentAtPosition = (product: ICatalogProduct, position: number) => {
    if (!this.editingProject?.manholeStructure) return;

    if (!this.editingProject.manholeStructure.components) {
      this.editingProject.manholeStructure.components = [];
    }

    const components = this.editingProject.manholeStructure.components;

    // Validate position
    if (position < 0 || position > components.length) {
      console.error("Invalid insertion position:", position);
      return;
    }

    // Create new component
    const newComponent: IStackComponent = {
      instanceId: `comp-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      product,
      positionInStack: position,
      bottomElevation: 0, // Will be recalculated
    };

    // Insert at position
    components.splice(position, 0, newComponent);

    // Recalculate all positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  // NEW: Auto-fill remaining height with risers (Phase 3)
  autoFillRisers = () => {
    if (!this.editingProject?.manholeStructure) return;

    const requiredHeight = this.requiredPrecastHeight;
    const currentStackHeight = this.actualTotalHeight;
    let remainingHeightInches = (requiredHeight - currentStackHeight) * 12;

    // Only auto-fill if we need more height (at least 6 inches)
    if (remainingHeightInches < 6) return;

    // Get current stack components
    const components = this.editingProject.manholeStructure.components || [];
    if (!components.length) return;

    // Get the diameter of the current stack (from base or top component)
    const topComponent = components[components.length - 1];
    const stackDiameter = topComponent?.product.diameterInches || 48;

    // Get available risers matching the stack diameter, sorted by height descending
    // Use isComponentType for case-insensitive comparison (supports 'riser' and 'RISER')
    const availableRisers = this.catalogProducts
      .filter(
        (p) =>
          isComponentType(p.type, "RISER") &&
          p.diameterInches === stackDiameter &&
          p.heightInches
      )
      .sort((a, b) => (b.heightInches || 0) - (a.heightInches || 0));

    if (!availableRisers.length) return;

    // Greedy algorithm: add largest risers first
    const risersToAdd: ICatalogProduct[] = [];
    const usedRisers = new Map<string, number>(); // Track quantity of each riser

    while (remainingHeightInches >= 6) {
      // Find the largest riser that fits
      const fittingRiser = availableRisers.find(
        (r) => (r.heightInches || 0) <= remainingHeightInches
      );

      if (!fittingRiser) break;

      risersToAdd.push(fittingRiser);
      remainingHeightInches -= fittingRiser.heightInches || 0;
      usedRisers.set(
        getProductCode(fittingRiser),
        (usedRisers.get(getProductCode(fittingRiser)) || 0) + 1
      );

      // Safety limit to prevent infinite loops
      if (risersToAdd.length > 20) break;
    }

    // Add each riser to the stack
    risersToAdd.forEach((riser) => {
      this.addComponent(riser);
    });

    // Reset height dismissed since we're adding components
    this.heightDismissed = false;
  };

  // NEW: Get suggested riser for current remaining height
  get suggestedRiserFill(): {
    risers: ICatalogProduct[];
    remainingInches: number;
  } {
    const requiredHeight = this.requiredPrecastHeight;
    const currentStackHeight = this.actualTotalHeight;
    const remainingInches = (requiredHeight - currentStackHeight) * 12;

    if (remainingInches < 6) {
      return { risers: [], remainingInches };
    }

    const components = this.editingProject?.manholeStructure?.components || [];
    if (!components.length) {
      return { risers: [], remainingInches };
    }

    const topComponent = components[components.length - 1];
    const stackDiameter = topComponent?.product.diameterInches || 48;

    // Use isComponentType for case-insensitive comparison (supports 'riser' and 'RISER')
    const availableRisers = this.catalogProducts
      .filter(
        (p) =>
          isComponentType(p.type, "RISER") &&
          p.diameterInches === stackDiameter &&
          p.heightInches
      )
      .sort((a, b) => (b.heightInches || 0) - (a.heightInches || 0));

    const suggested: ICatalogProduct[] = [];
    let remaining = remainingInches;

    for (const riser of availableRisers) {
      while ((riser.heightInches || 0) <= remaining && suggested.length < 10) {
        suggested.push(riser);
        remaining -= riser.heightInches || 0;
      }
    }

    return { risers: suggested, remainingInches: remaining };
  }

  private recalculateComponentPositions = () => {
    if (!this.editingProject?.manholeStructure?.components) return;

    const components = this.editingProject.manholeStructure.components;
    let currentElevation = this.lowestInvert || 90; // Start from lowest point

    components.forEach((component, index) => {
      component.positionInStack = index;
      component.bottomElevation = currentElevation;
      currentElevation += getEffectiveHeightInches(component) / 12; // Convert inches to feet
    });
  };

  private calculateBottomElevation = (position: number): number => {
    if (!this.editingProject?.manholeStructure?.components) {
      return this.lowestInvert || 90;
    }

    const components = this.editingProject.manholeStructure.components;
    let elevation = this.lowestInvert || 90;

    for (let i = 0; i < position && i < components.length; i++) {
      elevation += getEffectiveHeightInches(components[i]) / 12;
    }

    return elevation;
  };

  // Internal components management
  updateInternals = (updates: Partial<IManholeInternals>) => {
    // NEW: Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      // Process mutual exclusivity for steps/ladder
      const processedUpdates = { ...updates };
      if (processedUpdates.hasSteps === true) {
        processedUpdates.hasLadder = false;
      } else if (processedUpdates.hasLadder === true) {
        processedUpdates.hasSteps = false;
      }
      this.updateCurrentInternals(processedUpdates);
      return;
    }

    if (!this.editingProject?.manholeStructure) return;

    runInAction(() => {
      if (!this.editingProject!.manholeStructure!.internals) {
        this.editingProject!.manholeStructure!.internals = {
          hasSteps: false,
          hasLadder: false,
          stepAngleDegrees: 180,
          stepType: "standard",
        };
      }

      // Make steps and ladder mutually exclusive
      const processedUpdates = { ...updates };
      if (processedUpdates.hasSteps === true) {
        processedUpdates.hasLadder = false;
      } else if (processedUpdates.hasLadder === true) {
        processedUpdates.hasSteps = false;
      }

      // Create new internals object to trigger MobX reactivity
      this.editingProject!.manholeStructure!.internals = {
        ...this.editingProject!.manholeStructure!.internals,
        ...processedUpdates,
      };

      this.isDirty = true;
    });
  };

  // Convert estimate to awarded project
  awardProject = async (jobId: string | number) => {
    if (!this.activeProject) return;

    this.setLoading(true);
    this.setError(null);

    try {
      const updates = {
        jobId,
        status: "awarded" as const,
        awardedDate: new Date(),
      };

      const id = this.activeProject.id || this.activeProject.projectId;
      await (this.rootStore as any).manholeProjectsStore.patch(id, updates);

      // Reload to get denormalized job data
      await this.loadProject(id!);
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to award project");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  // Create quote from current project using the quoting services
  createQuote = async (formData?: Record<string, any>) => {
    if (!this.activeProject) return null;

    this.setLoading(true);
    const projectId = this.activeProject.id || this.activeProject.projectId;

    try {
      // Step 1: Create the quote record
      const taxRate = parseFloat(formData?.taxRate) || 8.0;
      const markup = parseFloat(formData?.markup) || 0;
      const subtotal = this.totalCost * (1 + markup / 100);
      const taxAmount = subtotal * (taxRate / 100);
      const total = subtotal + taxAmount;

      const quoteData = {
        accountId: this.activeProject.accountId,
        manholeProjectId: projectId,
        quoteNumber: `MQ-${Date.now().toString().slice(-8)}`,
        customerName: this.activeProject.customerName,
        jobName: this.activeProject.jobName,
        structureCount: this.structureOrder.length,
        totalComponents: this.billOfMaterials.length,
        totalWeight: this.totalWeight,
        subtotal: subtotal,
        tax: taxAmount,
        total: total,
        status: "draft",
        validUntil:
          formData?.validUntil ||
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        validityDays: 30,
        notes: formData?.notes || "",
        termsAndConditions: formData?.terms || "Net 30",
      };

      const quote = await (this.rootStore as any).manholeQuotesStore?.create(
        quoteData
      );

      if (!quote) {
        throw new Error("Failed to create quote record");
      }

      // Step 2: Use calculator to populate line items
      // The calculator creates individual manhole-quote-items for each structure's
      // components, pipes, and internals, then updates the quote totals
      let calcResult: any;
      try {
        calcResult = await (
          this.rootStore as any
        ).manholeQuoteCalculatorStore?.create({
          manholeProjectId: projectId,
          quoteId: quote.id,
          includeInternals: true,
          includePipes: true,
        });
      } catch (calcError: any) {
        console.error("Quote calculator failed:", calcError.message);
        // Delete the quote since it has no line items
        try {
          await (this.rootStore as any).manholeQuotesStore?.remove(quote.id);
        } catch (removeError) {
          console.warn("Failed to cleanup empty quote:", removeError);
        }
        throw new Error(
          "Failed to calculate quote line items: " + calcError.message
        );
      }

      // Verify line items were created
      if (!calcResult?.lineItems?.length) {
        console.error("Calculator returned no line items");
        // Delete the quote since it has no line items
        try {
          await (this.rootStore as any).manholeQuotesStore?.remove(quote.id);
        } catch (removeError) {
          console.warn("Failed to cleanup empty quote:", removeError);
        }
        throw new Error("No line items were generated for the quote");
      }

      // Refresh quote to get calculator-updated totals
      const refreshedQuote = await (
        this.rootStore as any
      ).manholeQuotesStore?.get(quote.id);

      // Step 3: Update project status to quoted
      await (this.rootStore as any).manholeProjectsStore?.patch(projectId, {
        quoteNumber: quote.quoteNumber,
        status: "quoted",
      });

      return refreshedQuote || quote;
    } catch (error: any) {
      console.error("Failed to create quote:", error);
      this.setError(
        "Failed to create quote: " + (error.message || "Unknown error")
      );
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  // Async actions
  /**
   * Save project using the new multi-service architecture:
   * 1. manhole-projects: Customer/job info (shared across all structures)
   * 2. manhole-structures: Per-structure data (one per manhole)
   * 3. manhole-pipe-connections: Pipes (linked to structure)
   * 4. manhole-sequences: Component stack (linked to structure)
   * 5. manhole-internals: Steps/ladder/options (linked to structure)
   *
   * Optimization: Only saves services that have actual changes to reduce API calls.
   */
  saveProject = async () => {
    if (!this.editingProject) return;

    // Guard against concurrent saves - prevents duplicate component creation
    if (this.isLoading) {
      console.log("[SAVE] saveProject - Aborting, save already in progress");
      return;
    }

    // Determine what needs to be saved based on what has changed
    const hasStructureEdits = this.currentStructureId
      ? this.structureEdits.has(this.currentStructureId)
      : false;
    const hasPipeEdits = this.currentStructureId
      ? this.pipeEdits.has(this.currentStructureId)
      : false;
    const hasComponentEdits = this.currentStructureId
      ? this.componentEdits.has(this.currentStructureId)
      : false;
    const hasInternalEdits = this.currentStructureId
      ? this.internalEdits.has(this.currentStructureId)
      : false;

    // Check if this is a new project (needs full save) or existing project with partial changes
    const isNewProject = !this.editingProject.id && !this.activeProjectId;
    const hasStructureLevelChanges =
      hasStructureEdits ||
      hasPipeEdits ||
      hasComponentEdits ||
      hasInternalEdits;

    // For existing projects with only project-level changes (isDirty but no structure changes),
    // we only need to save to manhole-projects service
    const projectOnlyChanges =
      !isNewProject && this.isDirty && !hasStructureLevelChanges;

    // Validate required fields - check structure-keyed edits first, then editingProject
    // Only validate structureId if we're saving structure-level data
    if (!projectOnlyChanges) {
      const structureIdFromEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)?.structureId
        : undefined;
      const structureIdToValidate =
        structureIdFromEdits || this.editingProject.structureId;
      const hasStructureId =
        structureIdToValidate && structureIdToValidate.toString().trim() !== "";

      if (!hasStructureId) {
        const errorMsg = "Structure ID is required";
        this.setError(errorMsg);
        if ((this.rootStore as any).notificationStore) {
          (this.rootStore as any).notificationStore.addNotification({
            message: errorMsg,
            type: "error",
            duration: 5000,
          });
        }
        return;
      }
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // If only project-level changes, do a quick save and return early
      if (projectOnlyChanges) {
        const projectData: manholeProjectPatch = {};

        // Only include fields that exist on the manhole_projects table
        if (this.editingProject.customerName)
          projectData.customerName = this.editingProject.customerName;
        if (this.editingProject.jobName)
          projectData.jobName = this.editingProject.jobName;
        if ((this.editingProject as any).jobIdentifier)
          projectData.jobIdentifier = (this.editingProject as any).jobIdentifier;
        if (this.editingProject.city)
          projectData.city = this.editingProject.city;
        if (this.editingProject.state)
          projectData.state = this.editingProject.state;
        if (this.editingProject.salesperson)
          projectData.salesperson = this.editingProject.salesperson;
        if ((this.editingProject as any).estimator)
          projectData.estimator = (this.editingProject as any).estimator;
        if (this.editingProject.phase)
          projectData.phase = this.editingProject.phase;
        if (this.editingProject.revision)
          projectData.revision = this.editingProject.revision;
        if ((this.editingProject as any).paymentTerms)
          projectData.paymentTerms = (this.editingProject as any).paymentTerms;

        const id = this.editingProject.id || this.activeProjectId;
        await (this.rootStore as any).manholeProjectsStore.patch(
          id,
          projectData
        );

        // Show success notification
        if ((this.rootStore as any).notificationStore) {
          (this.rootStore as any).notificationStore.addNotification({
            message: "Project saved successfully",
            type: "success",
            duration: 3000,
          });
        }

        runInAction(() => {
          this.isDirty = false;
          this.projectEdits = null;
        });

        this.setLoading(false);
        return;
      }
      // Get rimElevation from structure-keyed edits first, then fallback to editingProject
      const structureRimEdit = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)?.rimElevation
        : undefined;
      const rimElevation =
        structureRimEdit ??
        this.editingProject.manholeStructure?.rimElevation ??
        this.editingProject.rimElevation ??
        0;

      // ============================================
      // 1. SAVE PROJECT (customer/job info only)
      // ============================================
      // Get structureId from structure-keyed edits first
      const structureIdFromEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)?.structureId
        : undefined;
      const projectData: manholeProjectPatch & { projectId?: string } = {
        projectId: this.editingProject.projectId || uuid(),
        structureId:
          structureIdFromEdits || this.editingProject.structureId || "MH-1",
      };

      // Add project-level fields (customer/job info)
      if (this.editingProject.customerName)
        projectData.customerName = this.editingProject.customerName;
      if (this.editingProject.jobName)
        projectData.jobName = this.editingProject.jobName;
      // Form uses jobIdentifier directly, not jobNumber
      if ((this.editingProject as any).jobIdentifier)
        projectData.jobIdentifier = (this.editingProject as any).jobIdentifier;
      // Station might be in structureEdits (it's in structureFields list), so check both
      const stationFromEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)?.station
        : undefined;
      const stationValue = stationFromEdits ?? this.editingProject.station;
      if (stationValue) projectData.station = stationValue;
      // Type might also be in structureEdits, check both
      const typeFromEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId)?.type
        : undefined;
      const typeValue = typeFromEdits ?? this.editingProject.type;
      if (typeValue) projectData.type = typeValue;
      if (this.editingProject.city) projectData.city = this.editingProject.city;
      if (this.editingProject.state)
        projectData.state = this.editingProject.state;
      if (this.editingProject.salesperson)
        projectData.salesperson = this.editingProject.salesperson;
      if ((this.editingProject as any).estimator)
        projectData.estimator = (this.editingProject as any).estimator;
      if (this.editingProject.phase)
        projectData.phase = this.editingProject.phase;
      if (this.editingProject.revision)
        projectData.revision = this.editingProject.revision;
      if ((this.editingProject as any).paymentTerms)
        projectData.paymentTerms = (this.editingProject as any).paymentTerms;

      // Override with dialog form data if present (for new projects)
      if (
        !this.editingProject.id &&
        !this.editingProject.projectId &&
        this.dialogFormData
      ) {
        if (this.dialogFormData.customerName)
          projectData.customerName = this.dialogFormData.customerName;
        if (this.dialogFormData.jobName)
          projectData.jobName = this.dialogFormData.jobName;
      }

      let finalProjectId: string | undefined;

      if (this.editingProject.id || this.activeProjectId) {
        // Update existing project
        const id = this.editingProject.id || this.activeProjectId;
        await (this.rootStore as any).manholeProjectsStore.patch(
          id,
          projectData
        );
        finalProjectId = id!;
      } else {
        // Create new project
        const result = await (
          this.rootStore as any
        ).manholeProjectsStore.create(projectData);
        if (result?.id) {
          finalProjectId = result.id;
          // Note: activeProjectId is computed from URL queryParams
          // Navigation happens at the end of saveProject
          runInAction(() => {
            if (this.editingProject) {
              this.editingProject.id = result.id;
              this.editingProject.projectId = result.projectId;
            }
          });
        } else {
          throw new Error("Project creation failed - no ID returned");
        }
      }

      if (!finalProjectId) {
        throw new Error("Failed to get project ID after save");
      }

      // ============================================
      // 2. SAVE STRUCTURE (per-manhole data)
      // ============================================
      // Get structure edits from structure-keyed map if available
      const structureKeyedEdits = this.currentStructureId
        ? this.structureEdits.get(this.currentStructureId) || {}
        : {};

      const structureData: Record<string, unknown> = {
        projectId: finalProjectId,
        structureId:
          structureKeyedEdits.structureId ||
          this.editingProject.structureId ||
          "1",
      };

      // Add structure-level fields (prefer structureKeyedEdits, fallback to editingProject)
      const station =
        structureKeyedEdits.station ?? this.editingProject.station;
      const type = structureKeyedEdits.type ?? this.editingProject.type;
      const structureSubType =
        structureKeyedEdits.structureSubType ??
        this.editingProject.structureSubType;
      const catchDepthFeet =
        structureKeyedEdits.catchDepthFeet ??
        this.editingProject.catchDepthFeet;

      if (station) structureData.station = station;
      if (type) structureData.type = type;
      if (structureSubType) structureData.structureSubType = structureSubType;
      if (rimElevation) structureData.rimElevation = rimElevation;
      if (catchDepthFeet !== undefined)
        structureData.catchDepthFeet = Number(catchDepthFeet) || 0;

      // Calculated values
      const calculatedLowestInvert = this.lowestInvert;
      if (
        calculatedLowestInvert !== undefined &&
        calculatedLowestInvert !== 0
      ) {
        structureData.lowestInvert = Number(calculatedLowestInvert);
      }
      if (this.editingProject.requiredHeight !== undefined)
        structureData.requiredHeight = Number(
          this.editingProject.requiredHeight
        );

      // Casting/hatch/form (prefer structureKeyedEdits, fallback to editingProject)
      const castingId =
        structureKeyedEdits.castingId ?? this.editingProject.castingId;
      const castingType =
        structureKeyedEdits.castingType ?? this.editingProject.castingType;
      const formAssignment =
        structureKeyedEdits.formAssignment ??
        this.editingProject.formAssignment;
      const hatchType =
        structureKeyedEdits.hatchType ?? this.editingProject.hatchType;
      const hatchPONumber =
        structureKeyedEdits.hatchPoNumber ??
        (this.editingProject as any).hatchPONumber;

      if (castingId) structureData.castingId = castingId;
      if (castingType) structureData.castingType = castingType;
      if (formAssignment) structureData.formAssignment = formAssignment;
      if (hatchType) structureData.hatchType = hatchType;
      if (hatchPONumber) structureData.hatchPoNumber = hatchPONumber;

      // Sealant config
      if (this.sealantConfig?.type)
        structureData.sealantType = this.sealantConfig.type;
      if (this.sealantConfig?.gasketSize)
        structureData.gasketSize = this.sealantConfig.gasketSize;

      // Status (prefer structureKeyedEdits, fallback to editingProject)
      const status = structureKeyedEdits.status ?? this.editingProject.status;
      if (status) structureData.status = status;

      // NEW: Structure-level fields from Phase 1 implementation
      // Structure Type (moved from project level)
      const structureType =
        this.editingProject.manholeStructure?.structureType ??
        structureKeyedEdits.structureType ??
        this.editingProject.type;
      if (structureType) structureData.structureType = structureType;

      // Contractor advisory notes
      const notes = (this.editingProject.manholeStructure as any)?.notes;
      if (notes !== undefined) structureData.notes = notes;

      // Floor thickness
      const floorThicknessInches = (this.editingProject.manholeStructure as any)
        ?.floorThicknessInches;
      if (floorThicknessInches !== undefined)
        structureData.floorThicknessInches = floorThicknessInches;

      // Interior/exterior coatings
      const interiorCoating = (this.editingProject.manholeStructure as any)
        ?.interiorCoating;
      if (interiorCoating?.type && interiorCoating.type !== "none") {
        structureData.interiorCoating = interiorCoating;
      }
      const exteriorCoating = (this.editingProject.manholeStructure as any)
        ?.exteriorCoating;
      if (exteriorCoating?.type && exteriorCoating.type !== "none") {
        structureData.exteriorCoating = exteriorCoating;
      }

      // Concrete additives
      const additives = (this.editingProject.manholeStructure as any)
        ?.additives;
      if (additives?.length) {
        structureData.additives = additives;
      }

      // Auto-increment revision on save (A -> B -> C)
      const existingStructure = this.activeStructureId
        ? this.getStructureFromStore(this.activeStructureId)
        : null;
      const currentRevision =
        existingStructure?.revision ??
        (this.editingProject.manholeStructure as any)?.revision ??
        "A";
      // Only increment if updating existing structure
      if (this.activeStructureId) {
        structureData.revision = incrementRevision(currentRevision);
      } else {
        structureData.revision = "A";
      }
      structureData.lastModified = new Date().toISOString();

      let finalStructureId: string | undefined;

      if (this.activeStructureId) {
        // Update existing structure
        await (this.rootStore as any).manholeStructuresStore.patch(
          this.activeStructureId,
          structureData
        );
        finalStructureId = this.activeStructureId;
      } else {
        // Create new structure
        const result = await (
          this.rootStore as any
        ).manholeStructuresStore.create(structureData);
        if (result?.id) {
          finalStructureId = result.id;
          // Note: activeStructureId is computed from URL queryParams
          // Navigation happens at the end of saveProject
        } else {
          throw new Error("Structure creation failed - no ID returned");
        }
      }

      if (!finalStructureId) {
        throw new Error("Failed to get structure ID after save");
      }

      // ============================================
      // 3. SAVE PIPES (linked to structure)
      // ============================================
      // Check structure-keyed pipeEdits first, then fall back to editingProject
      const pipesToSave =
        this.currentStructureId && this.pipeEdits.has(this.currentStructureId)
          ? this.pipeEdits.get(this.currentStructureId)
          : this.editingProject.manholeStructure?.pipes;

      if (
        pipesToSave &&
        pipesToSave.length > 0 &&
        (this.rootStore as any).manholePipeConnectionsStore
      ) {
        const pipes = pipesToSave;

        // Delete existing pipes for this project (query by manholeProjectId, not structureId)
        const existingPipes = await (
          this.rootStore as any
        ).manholePipeConnectionsStore.find({
          query: { manholeProjectId: finalProjectId },
        });

        if (existingPipes?.data) {
          for (const pipe of existingPipes.data) {
            await (this.rootStore as any).manholePipeConnectionsStore.remove(
              pipe.id
            );
          }
        }

        // Create new pipes
        for (const pipe of pipes) {
          // Build pipe data, only including optional fields if they have values
          const pipeData: Record<string, unknown> = {
            manholeProjectId: finalProjectId,
            structureId: finalStructureId,
            pipeNumber: pipe.pipeNumber,
            invertElevation: pipe.invertElevation,
            pipeDiameterInches: pipe.pipeDiameterInches,
            direction:
              pipe.direction || (pipe.pipeNumber === 1 ? "outlet" : "inlet"),
          };

          if (pipe.pipeMaterial) pipeData.pipeMaterial = pipe.pipeMaterial;
          if (pipe.angleDegrees !== undefined)
            pipeData.angleDegrees = pipe.angleDegrees;
          if (pipe.holeType) pipeData.holeType = pipe.holeType;
          if (pipe.holeSizeInches !== undefined)
            pipeData.holeSizeInches = pipe.holeSizeInches;
          if (pipe.holeMethod) pipeData.holeMethod = pipe.holeMethod;
          if (pipe.connectionType)
            pipeData.connectionType = pipe.connectionType;
          if (pipe.requiresSpecialFormer !== undefined)
            pipeData.requiresSpecialFormer = pipe.requiresSpecialFormer;

          await (this.rootStore as any).manholePipeConnectionsStore.create(
            pipeData
          );
        }
      }

      // ============================================
      // 4. SAVE COMPONENTS (linked to structure)
      // ============================================
      // Check structure-keyed componentEdits first, then fall back to editingProject
      const componentsToSave =
        this.currentStructureId &&
        this.componentEdits.has(this.currentStructureId)
          ? this.componentEdits.get(this.currentStructureId)
          : this.editingProject.manholeStructure?.components;

      if (
        componentsToSave &&
        componentsToSave.length > 0 &&
        (this.rootStore as any).manholeSequencesStore
      ) {
        const components = componentsToSave;

        // Delete existing sequences for this project (query by manholeProjectId, not structureId)
        const existingSequences = await (
          this.rootStore as any
        ).manholeSequencesStore.find({
          query: { manholeProjectId: finalProjectId },
        });

        if (existingSequences?.data) {
          for (const seq of existingSequences.data) {
            await (this.rootStore as any).manholeSequencesStore.remove(seq.id);
          }
        }

        // Create new sequences (components)
        for (let i = 0; i < components.length; i++) {
          const component = components[i];
          const sequenceData = {
            manholeProjectId: finalProjectId,
            structureId: finalStructureId,
            sequenceOrder: i + 1,
            // Use UPPERCASE type for new schema
            componentType: (
              component.product?.type ||
              component.product?.manholeType ||
              "RISER"
            ).toUpperCase(),
            // componentCode is the primary identifier (new schema)
            componentCode: getProductCode(component.product) || "",
            componentName:
              component.product?.name ||
              component.product?.description ||
              "Unknown",
            heightInches: getEffectiveHeightInches(component),
            heightModifierInches: component.heightModifierInches || 0,
            diameterInches: component.product?.diameterInches,
            weight: component.product?.weight || component.product?.weightLbs,
            // Use cost field (matches database schema)
            cost: getProductCost(component.product),
            quantity: 1, // Each IStackComponent is a single instance
          };
          await (this.rootStore as any).manholeSequencesStore.create(
            sequenceData
          );
        }
      }

      // ============================================
      // 5. SAVE INTERNALS (linked to structure)
      // ============================================
      // Check structure-keyed internalEdits first, then fall back to editingProject
      const internalsToSave =
        this.currentStructureId &&
        this.internalEdits.has(this.currentStructureId)
          ? this.internalEdits.get(this.currentStructureId)
          : this.editingProject.manholeStructure?.internals;

      if (internalsToSave && (this.rootStore as any).manholeInternalsStore) {
        const internals = internalsToSave;

        // Check for existing internals record (query by manholeProjectId, not structureId)
        const existingInternals = await (
          this.rootStore as any
        ).manholeInternalsStore.find({
          query: { manholeProjectId: finalProjectId },
        });

        // Cast to extended type to access additional fields
        const extInternals = internals as IManholeInternalsExtended;
        const internalsData = {
          manholeProjectId: finalProjectId,
          structureId: finalStructureId,
          includeSteps: internals.hasSteps || false,
          stepAngle: internals.stepAngleDegrees,
          stepType: internals.stepType || "standard",
          ladderType: internals.hasLadder
            ? internals.ladderType || "steel"
            : "none",
          preChannelInvert: extInternals.hasPreChannelInvert || false,
          guLiner: extInternals.hasGULiner || false,
          custom: extInternals.hasCustomInternal || false,
          customDescription: extInternals.customInternalDescription,
        };

        if (existingInternals?.data?.length > 0) {
          // Update existing
          await (this.rootStore as any).manholeInternalsStore.patch(
            existingInternals.data[0].id,
            internalsData
          );
        } else {
          // Create new
          await (this.rootStore as any).manholeInternalsStore.create(
            internalsData
          );
        }
      }

      // Show success notification
      if ((this.rootStore as any).notificationStore) {
        (this.rootStore as any).notificationStore.addNotification({
          message: "Project saved successfully",
          type: "success",
          duration: 3000,
        });
      }

      // Sync URL with persisted IDs after successful save
      if (finalProjectId && (this.rootStore as any).routerStore) {
        const queryParams: Record<string, string> = {
          projectId: finalProjectId,
        };
        // Only include structureId if it's a real persisted ID (not temp)
        if (finalStructureId && !finalStructureId.startsWith("temp-")) {
          queryParams.structureId = finalStructureId;
        }
        (this.rootStore as any).routerStore.goTo(
          "Manhole Designer",
          null,
          queryParams
        );
      }

      runInAction(() => {
        this.isDirty = false;
        this.projectEdits = null;
      });
    } catch (error: any) {
      const errorMsg = error.message || "Failed to save project";
      runInAction(() => {
        this.setError(errorMsg);
      });
      if ((this.rootStore as any).notificationStore) {
        (this.rootStore as any).notificationStore.addNotification({
          message: errorMsg,
          type: "error",
          duration: 5000,
        });
      }
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadProject = async (projectId: string, structureId?: string) => {
    this.setLoading(true);
    this.setError(null);

    try {
      // 1. Load the project
      await (this.rootStore as any).manholeProjectsStore.get(projectId);

      // 2. Load structures for this project
      let structures: any[] = [];
      if ((this.rootStore as any).manholeStructuresStore) {
        const structureResult = await (
          this.rootStore as any
        ).manholeStructuresStore.find({
          query: { projectId },
        });
        structures = structureResult?.data || [];
      }

      // 3. Determine which structure to load (specified or first one)
      const targetStructure = structureId
        ? structures.find((s: any) => s.id === structureId)
        : structures[0];

      // Navigate to update URL with project/structure IDs
      // (activeProjectId and activeStructureId are computed from URL queryParams)
      if ((this.rootStore as any).routerStore) {
        const queryParams: Record<string, string> = { projectId };
        if (targetStructure?.id) {
          queryParams.structureId = targetStructure.id;
        }
        (this.rootStore as any).routerStore.goTo("Manhole Designer", null, queryParams);
      }

      // 4. Load related data using the project ID (not structure ID)
      await this.loadStructureData(projectId);
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to load project");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  // Load structure-specific data (pipes, sequences, internals)
  // Note: Uses manholeProjectId (UUID) for queries, not human-readable structureId
  loadStructureData = async (projectId: string) => {
    try {
      // Only query if we have a valid project ID
      if (!projectId) {
        console.warn("loadStructureData called without valid projectId");
        return;
      }

      // Load pipes for this project
      if ((this.rootStore as any).manholePipeConnectionsStore) {
        await (this.rootStore as any).manholePipeConnectionsStore.find({
          query: { manholeProjectId: projectId },
        });
      }

      // Load sequences (components) for this project
      if ((this.rootStore as any).manholeSequencesStore) {
        await (this.rootStore as any).manholeSequencesStore.find({
          query: { manholeProjectId: projectId, $sort: { sequenceOrder: 1 } },
        });
      }

      // Load internals for this project
      if ((this.rootStore as any).manholeInternalsStore) {
        await (this.rootStore as any).manholeInternalsStore.find({
          query: { manholeProjectId: projectId },
        });
      }
    } catch (error: any) {
      console.error("Failed to load structure data:", error);
    }
  };

  deleteProject = async (projectId: string) => {
    this.setLoading(true);
    this.setError(null);

    try {
      await (this.rootStore as any).manholeProjectsStore.remove(projectId);
      // If deleted project was the active one, navigate away
      // (activeProjectId is computed from URL queryParams)
      if (this.activeProjectId === projectId && (this.rootStore as any).routerStore) {
        (this.rootStore as any).routerStore.goTo("Manhole Designer", null, {});
      }
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || "Failed to delete project");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  // Calculator service integration with response transformation
  calculateManholeOnServer = async () => {
    if (!this.activeProjectId) return null;

    this.setLoading(true);
    try {
      // Call backend calculator service
      const backendResponse = await (
        this.rootStore as any
      ).manholeCalculatorStore?.create({
        manholeProjectId: this.activeProjectId,
      });

      if (!backendResponse) {
        throw new Error("No response from calculator service");
      }

      // Transform backend response to match frontend expectations
      const transformedResponse =
        this.transformCalculatorResponse(backendResponse);

      // Optionally save calculated components to manhole-components service
      if (
        transformedResponse.components &&
        (this.rootStore as any).manholeComponentsStore
      ) {
        for (const component of transformedResponse.components) {
          await (this.rootStore as any).manholeComponentsStore.create({
            ...component,
            manholeProjectId: this.activeProjectId,
          });
        }
      }

      return transformedResponse;
    } catch (error: any) {
      console.error("Server calculation failed:", error);
      this.setError("Failed to calculate manhole on server");
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  // Transform backend calculator response to frontend format
  private transformCalculatorResponse(backendResponse: any) {
    const components = backendResponse.components || [];

    // Calculate bottom elevations for each component
    let currentElevation =
      this.activeProject?.manholeStructure?.rimElevation || 0;
    const transformedComponents = components.map(
      (product: any, index: number) => {
        const bottomElevation = currentElevation - product.heightInches / 12;
        currentElevation = bottomElevation;

        return {
          instanceId: `comp-${Date.now()}-${index}`,
          product: this.normalizeProduct(product),
          positionInManhole: index,
          positionInStack: index, // Keep both for compatibility
          bottomElevation,
        };
      }
    );

    // Generate bill of materials
    const bomMap = new Map<string, any>();
    transformedComponents.forEach((comp: any) => {
      // Use componentCode as primary key (new schema)
      const key = getProductCode(comp.product);
      if (bomMap.has(key)) {
        bomMap.get(key).quantity += 1;
      } else {
        const cost = getProductCost(comp.product);
        bomMap.set(key, {
          item: key,
          description: comp.product.description,
          quantity: 1,
          weight: comp.product.weightLbs || comp.product.weight || 0,
          unitCost: cost,
          totalCost: cost,
        });
      }
    });

    const billOfMaterials = Array.from(bomMap.values());
    const totalWeight = billOfMaterials.reduce(
      (sum, item) => sum + item.weight * item.quantity,
      0
    );
    const totalCost =
      backendResponse.totalCost ||
      billOfMaterials.reduce((sum, item) => sum + item.totalCost, 0);

    return {
      success: backendResponse.success,
      components: transformedComponents,
      billOfMaterials,
      totalWeight,
      totalCost,
      totalHeight: backendResponse.totalHeight,
    };
  }

  // Normalize product fields from backend
  private normalizeProduct(product: any): ICatalogProduct {
    return {
      ...product,
      id: product.id || product.productId,
      // componentCode is the new primary identifier
      componentCode:
        product.componentCode || product.productCode || product.productId || "",
      productId:
        product.componentCode || product.productCode || product.productId,
      // Normalize type to uppercase
      type: (product.type || product.manholeType || "").toUpperCase(),
      weightLbs: product.weightLbs || product.weight,
      weight: product.weight || product.weightLbs,
      // Use estimatedCost for new schema
      estimatedCost:
        product.estimatedCost ??
        product.cost ??
        product.costUSD ??
        product.basePrice ??
        0,
      costUSD:
        product.estimatedCost ??
        product.cost ??
        product.costUSD ??
        product.basePrice ??
        0,
    };
  }

  // Test data creation for development
  createTestPipes = () => {
    if (!this.editingProject?.manholeStructure) {
      console.warn("No editing project to add test pipes to");
      return;
    }

    // Clear existing pipes
    this.editingProject.manholeStructure.pipes = [];

    // Create test pipes at different angles
    const testPipes: Partial<IPipeConnection>[] = [
      {
        pipeNumber: 1,
        invertElevation: 95.5,
        pipeDiameterInches: 18,
        angleDegrees: 0, // North
        direction: "inlet",
        pipeMaterial: "RCP",
      },
      {
        pipeNumber: 2,
        invertElevation: 94.8,
        pipeDiameterInches: 24,
        angleDegrees: 90, // East
        direction: "outlet",
        pipeMaterial: "RCP",
      },
      {
        pipeNumber: 3,
        invertElevation: 95.2,
        pipeDiameterInches: 12,
        angleDegrees: 180, // South
        direction: "inlet",
        pipeMaterial: "PVC",
      },
      {
        pipeNumber: 4,
        invertElevation: 95.0,
        pipeDiameterInches: 15,
        angleDegrees: 270, // West
        direction: "inlet",
        pipeMaterial: "HDPE",
      },
      {
        pipeNumber: 5,
        invertElevation: 94.5,
        pipeDiameterInches: 8,
        angleDegrees: 45, // Northeast
        direction: "inlet",
        pipeMaterial: "PVC",
      },
    ];

    testPipes.forEach((pipeData) => {
      const fullPipe: IPipeConnection = {
        manholeProjectId: this.activeProjectId || undefined,
        pipeNumber: pipeData.pipeNumber || 1,
        invertElevation: pipeData.invertElevation || 0,
        pipeDiameterInches: pipeData.pipeDiameterInches || 8,
        pipeMaterial: pipeData.pipeMaterial || "PVC",
        angleDegrees: pipeData.angleDegrees || 0,
        direction: pipeData.direction || "inlet",
        holeType: "40612AW",
        holeSizeInches: (pipeData.pipeDiameterInches || 8) + 4,
      };
      this.editingProject.manholeStructure!.pipes.push(fullPipe);
    });

    this.isDirty = true;
    console.log(
      "Test pipes created:",
      this.editingProject.manholeStructure.pipes
    );
  };

  // Generate Bill of Materials
  generateBOM = (): IBillOfMaterialsEntry[] => {
    const bom: IBillOfMaterialsEntry[] = [];

    // Add components to BOM
    if (this.editingProject?.manholeStructure?.components) {
      this.editingProject.manholeStructure.components.forEach((comp) => {
        const cost = getProductCost(comp.product);
        bom.push({
          item: getProductCode(comp.product) || "COMP",
          description: comp.product.description,
          quantity: 1,
          weight: comp.product.weightLbs || comp.product.weight || 0,
          unitCost: cost,
          totalCost: cost,
        });
      });
    }

    // Add pipes to BOM
    if (this.editingProject?.manholeStructure?.pipes) {
      this.editingProject.manholeStructure.pipes.forEach((pipe) => {
        bom.push({
          item: `PIPE-${pipe.pipeDiameterInches}`,
          description: `${pipe.pipeDiameterInches}" ${pipe.pipeMaterial} Pipe Connection`,
          quantity: 1,
          weight: pipe.pipeDiameterInches * 10, // Estimated
          unitCost: pipe.pipeDiameterInches * 25, // Estimated
          totalCost: pipe.pipeDiameterInches * 25,
        });
      });
    }

    // Add internals to BOM
    if (this.editingProject?.manholeStructure?.internals) {
      const internals = this.editingProject.manholeStructure.internals;
      if (internals.hasSteps) {
        bom.push({
          item: "STEPS-STD",
          description: `Standard Steps at ${internals.stepAngleDegrees}°`,
          quantity: Math.floor((this.requiredPrecastHeight || 10) / 1.25), // One step per 1.25 feet
          weight: 15,
          unitCost: 45,
          totalCost: 45 * Math.floor((this.requiredPrecastHeight || 10) / 1.25),
        });
      }
      if (internals.hasLadder) {
        bom.push({
          item: "LADDER-FX",
          description: "Fixed Ladder System",
          quantity: 1,
          weight: 150,
          unitCost: 850,
          totalCost: 850,
        });
      }
    }

    return bom;
  };

  // Export BOM to CSV
  exportBOMToCSV = () => {
    const bom = this.generateBOM();
    if (bom.length === 0) {
      console.warn("No items in BOM to export");
      return;
    }

    // Create CSV content
    const headers = [
      "Item",
      "Description",
      "Quantity",
      "Weight (lbs)",
      "Unit Cost",
      "Total Cost",
    ];
    const rows = bom.map((item) => [
      item.item,
      item.description,
      item.quantity,
      item.weight,
      `$${item.unitCost.toFixed(2)}`,
      `$${item.totalCost.toFixed(2)}`,
    ]);

    // Add totals row
    const totalWeight = bom.reduce(
      (sum, item) => sum + item.weight * item.quantity,
      0
    );
    const totalCost = bom.reduce((sum, item) => sum + item.totalCost, 0);
    rows.push(["", "TOTAL", "", totalWeight, "", `$${totalCost.toFixed(2)}`]);

    // Convert to CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `manhole-bom-${
      this.editingProject?.structureId || "export"
    }-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log("BOM exported successfully");
  };

  // Load mock test components for development
  loadMockComponents = () => {
    // Mock components for testing when database is not available
    // Uses new schema: UPPERCASE types, componentCode, estimatedCost
    const mockComponents = [
      {
        id: "mh_mock_base48",
        componentCode: "MH-BASE-48",
        name: '48" Manhole Base',
        description: '48" Manhole Base with Monolithic Base',
        type: "BASE",
        heightInches: 36,
        diameterInches: 48,
        wallThicknessInches: 5,
        weight: 4500,
        estimatedCost: 850,
        isActive: true,
      },
      {
        id: "mh_mock_riser4824",
        componentCode: "MH-RISER-48-24",
        name: '48" x 24" Riser',
        description: '48" x 24" Riser Section',
        type: "RISER",
        heightInches: 24,
        diameterInches: 48,
        wallThicknessInches: 5,
        weight: 2000,
        estimatedCost: 425,
        isActive: true,
      },
      {
        id: "mh_mock_riser4812",
        componentCode: "MH-RISER-48-12",
        name: '48" x 12" Riser',
        description: '48" x 12" Riser Section',
        type: "RISER",
        heightInches: 12,
        diameterInches: 48,
        wallThicknessInches: 5,
        weight: 1000,
        estimatedCost: 225,
        isActive: true,
      },
      {
        id: "mh_mock_cone4824",
        componentCode: "MH-CONE-48-24",
        name: '48" to 24" Cone',
        description: '48" to 24" Eccentric Cone',
        type: "CONE",
        heightInches: 36,
        diameterInches: 48,
        wallThicknessInches: 5,
        weight: 2500,
        estimatedCost: 550,
        isActive: true,
      },
      {
        id: "mh_mock_ring246",
        componentCode: "MH-RING-24-6",
        name: '24" x 6" Grade Ring',
        description: '24" x 6" Grade Ring',
        type: "ADJUST_RING",
        heightInches: 6,
        diameterInches: 24,
        wallThicknessInches: 3,
        weight: 200,
        estimatedCost: 75,
        isActive: true,
      },
      {
        id: "mh_mock_fcstd",
        componentCode: "MH-FC-STD",
        name: "Frame & Cover",
        description: "Standard Cast Iron Frame & Cover",
        type: "OTHER",
        heightInches: 4,
        diameterInches: 24,
        wallThicknessInches: 2,
        weight: 350,
        estimatedCost: 425,
        isActive: true,
      },
      {
        id: "mh_mock_base60",
        componentCode: "MH-BASE-60",
        name: '60" Manhole Base',
        description: '60" Manhole Base with Monolithic Base',
        type: "BASE",
        heightInches: 36,
        diameterInches: 60,
        wallThicknessInches: 6,
        weight: 6500,
        estimatedCost: 1250,
        isActive: true,
      },
      {
        id: "mh_mock_riser6024",
        componentCode: "MH-RISER-60-24",
        name: '60" x 24" Riser',
        description: '60" x 24" Riser Section',
        type: "RISER",
        heightInches: 24,
        diameterInches: 60,
        wallThicknessInches: 6,
        weight: 3000,
        estimatedCost: 625,
        isActive: true,
      },
      {
        id: "mh_mock_base72",
        componentCode: "MH-BASE-72",
        name: '72" Manhole Base',
        description: '72" Manhole Base with Monolithic Base',
        type: "BASE",
        heightInches: 36,
        diameterInches: 72,
        wallThicknessInches: 6,
        weight: 8500,
        estimatedCost: 1650,
        isActive: true,
      },
      {
        id: "mh_mock_riser7224",
        componentCode: "MH-RISER-72-24",
        name: '72" x 24" Riser',
        description: '72" x 24" Riser Section',
        type: "RISER",
        heightInches: 24,
        diameterInches: 72,
        wallThicknessInches: 6,
        weight: 4000,
        estimatedCost: 825,
        isActive: true,
      },
    ];

    // Store mock components in sessionStorage for persistence during development
    sessionStorage.setItem(
      "mockManholeComponents",
      JSON.stringify(mockComponents)
    );

    console.log("Mock components loaded:", mockComponents.length);
    this.setError(null);

    // Trigger a re-render by updating a dummy observable
    this.isDirty = !this.isDirty;
    this.isDirty = !this.isDirty;

    return mockComponents;
  };

  // Create test components in the API/database
  createTestComponentsInAPI = async () => {
    const manholeComponentsStore = (this.rootStore as any)
      .manholeComponentsStore;
    if (!manholeComponentsStore) {
      console.error("manholeComponentsStore not found");
      this.setError("Component store not available");
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // Define test components to create in the API - using new schema (UPPERCASE types, estimatedCost)
      const testComponents = [
        {
          componentCode: "MH-BASE-48",
          componentName: '48" Manhole Base',
          componentType: "BASE", // UPPERCASE for new schema
          heightInches: 36,
          diameterInches: 48,
          weight: 4500,
          estimatedCost: 850, // New field name
          description: '48" Manhole Base with Monolithic Base',
          isActive: true,
        },
        {
          componentCode: "MH-RISER-48-24",
          componentName: '48" x 24" Riser',
          componentType: "RISER",
          heightInches: 24,
          diameterInches: 48,
          weight: 2000,
          estimatedCost: 425,
          description: '48" x 24" Riser Section',
          isActive: true,
        },
        {
          componentCode: "MH-RISER-48-12",
          componentName: '48" x 12" Riser',
          componentType: "RISER",
          heightInches: 12,
          diameterInches: 48,
          weight: 1000,
          estimatedCost: 225,
          description: '48" x 12" Riser Section',
          isActive: true,
        },
        {
          componentCode: "MH-CONE-48-24",
          componentName: '48" to 24" Cone',
          componentType: "CONE",
          heightInches: 36,
          diameterInches: 48,
          weight: 2500,
          estimatedCost: 550,
          description: '48" to 24" Eccentric Cone',
          isActive: true,
        },
        {
          componentCode: "MH-RING-24-6",
          componentName: '24" x 6" Grade Ring',
          componentType: "ADJUST_RING", // New schema name for rings
          heightInches: 6,
          diameterInches: 24,
          weight: 200,
          estimatedCost: 75,
          description: '24" x 6" Grade Ring',
          isActive: true,
        },
        {
          componentCode: "MH-FC-STD",
          componentName: "Frame & Cover",
          componentType: "OTHER", // frame maps to OTHER in new schema
          heightInches: 4,
          diameterInches: 24,
          weight: 350,
          estimatedCost: 425,
          description: "Standard Cast Iron Frame & Cover",
          isActive: true,
        },
        {
          componentCode: "MH-BASE-60",
          componentName: '60" Manhole Base',
          componentType: "BASE",
          heightInches: 36,
          diameterInches: 60,
          weight: 6500,
          estimatedCost: 1250,
          description: '60" Manhole Base with Monolithic Base',
          isActive: true,
        },
        {
          componentCode: "MH-RISER-60-24",
          componentName: '60" x 24" Riser',
          componentType: "RISER",
          heightInches: 24,
          diameterInches: 60,
          weight: 3000,
          estimatedCost: 625,
          description: '60" x 24" Riser Section',
          isActive: true,
        },
        {
          componentCode: "MH-BASE-72",
          componentName: '72" Manhole Base',
          componentType: "BASE",
          heightInches: 36,
          diameterInches: 72,
          weight: 8500,
          estimatedCost: 1650,
          description: '72" Manhole Base with Monolithic Base',
          isActive: true,
        },
        {
          componentCode: "MH-RISER-72-24",
          componentName: '72" x 24" Riser',
          componentType: "RISER",
          heightInches: 24,
          diameterInches: 72,
          weight: 4000,
          estimatedCost: 825,
          description: '72" x 24" Riser Section',
          isActive: true,
        },
      ];

      const createdComponents = [];
      let successCount = 0;
      let skipCount = 0;

      for (const component of testComponents) {
        try {
          // Check if component already exists
          const existing = manholeComponentsStore.entities.find(
            (c: any) => c.componentCode === component.componentCode
          );

          if (existing) {
            console.log(
              `Component ${component.componentCode} already exists, skipping`
            );
            skipCount++;
            continue;
          }

          // Create component in API
          const created = await manholeComponentsStore.create(component);
          createdComponents.push(created);
          successCount++;
          console.log(`Created component: ${component.componentCode}`);
        } catch (error) {
          console.error(
            `Failed to create component ${component.componentCode}:`,
            error
          );
        }
      }

      // Refresh the store to get all components
      await manholeComponentsStore.find();

      const message = `Created ${successCount} components${
        skipCount > 0 ? `, skipped ${skipCount} existing` : ""
      }`;
      console.log(message);

      if (successCount === 0 && skipCount > 0) {
        this.setError("All test components already exist in the database");
      } else {
        this.setError(null); // Clear any errors on success
      }
    } catch (error) {
      console.error("Failed to create test components:", error);
      this.setError("Failed to create test components in database");
    } finally {
      this.setLoading(false);
    }
  };

  // Computed values - Reference ServiceStore entities
  get activeProject(): IManholeProject | null {
    if (!this.activeProjectId) return null;

    const project = (
      this.rootStore as any
    ).manholeProjectsStore?.entities?.find(
      (p: any) =>
        p.id === this.activeProjectId || p.projectId === this.activeProjectId
    );

    if (!project) return null;

    // Always fetch from separate services and merge into project
    const pipes = this.projectPipes;
    const internals = this.projectInternals;
    const components = this.projectSequences;

    // Get rimElevation from the current structure (manholeStructuresStore), not from project
    // This fixes the bug where rimElevation was saved to structure but read from project
    const currentStructureData = this.getStructureFromStore(
      this.currentStructureId || ""
    );
    const structureEdits = this.currentStructureId
      ? this.structureEdits.get(this.currentStructureId)
      : undefined;
    const structureRimElevation =
      structureEdits?.rimElevation ?? currentStructureData?.rimElevation;

    // Build the complete project with data from all services
    return {
      ...project,
      manholeStructure: {
        rimElevation:
          structureRimElevation ??
          project.rimElevation ??
          project.manholeStructure?.rimElevation ??
          0,
        pipes: pipes.length > 0 ? pipes : project.manholeStructure?.pipes || [],
        components:
          components.length > 0
            ? components
            : project.manholeStructure?.components || project.components || [],
        internals: internals ||
          project.manholeStructure?.internals ||
          project.internals || {
            hasSteps: false,
            hasLadder: false,
            stepAngleDegrees: 180,
          },
      },
    };
  }

  // Check if the required project fields are filled
  get hasRequiredProjectFields(): boolean {
    const project = this.editingProject || this.activeProject;

    if (!project) return false;

    // Check if all required fields have non-empty values
    const hasCustomerName =
      project.customerName && project.customerName.trim().length > 0;
    const hasJobName = project.jobName && project.jobName.trim().length > 0;

    return hasCustomerName && hasJobName;
  }

  get allProjects(): IManholeProject[] {
    return (this.rootStore as any).manholeProjectsStore?.entities || [];
  }

  // Get estimates only (not awarded)
  get estimates(): IManholeProject[] {
    return this.allProjects.filter(
      (p) =>
        p.status === "draft" || p.status === "estimate" || p.status === "quoted"
    );
  }

  // Get awarded projects only
  get awardedProjects(): IManholeProject[] {
    return this.allProjects.filter(
      (p) =>
        p.status === "awarded" ||
        p.status === "revised" ||
        p.status === "completed"
    );
  }

  // Get all accounts for dropdown
  get accounts() {
    return (this.rootStore as any).customersStore?.entities || [];
  }

  // Get pipes from separate service
  get projectPipes(): IPipeConnection[] {
    if (!this.activeProjectId) return [];

    return (
      (this.rootStore as any).manholePipeConnectionsStore?.entities?.filter(
        (pipe: any) => pipe.manholeProjectId === this.activeProjectId
      ) || []
    );
  }

  // Get internals from separate service and transform to UI format
  get projectInternals(): any {
    if (!this.activeProjectId) return null;

    const internalsData = (
      this.rootStore as any
    ).manholeInternalsStore?.entities?.find(
      (i: any) => i.manholeProjectId === this.activeProjectId
    );

    if (!internalsData) return null;

    // Transform API format to UI format
    return {
      hasSteps: internalsData.includeSteps || false,
      hasLadder:
        internalsData.ladderType && internalsData.ladderType !== "none",
      stepAngleDegrees: internalsData.stepAngle || 180,
      stepType: internalsData.stepType || "standard",
      ladderType: internalsData.ladderType || "none",
      hasPreChannelInvert: internalsData.preChannelInvert || false,
      hasGULiner: internalsData.guLiner || false,
      hasCustomInternal: internalsData.custom || false,
      customInternalDescription: internalsData.customDescription || "",
    };
  }

  // Get sequences (components) from separate service and transform to IStackComponent format
  get projectSequences(): IStackComponent[] {
    if (!this.activeProjectId) return [];

    const sequences =
      (this.rootStore as any).manholeSequencesStore?.entities?.filter(
        (s: any) => s.manholeProjectId === this.activeProjectId
      ) || [];

    // Sort by sequence order
    const sorted = [...sequences].sort(
      (a: any, b: any) => a.sequenceOrder - b.sequenceOrder
    );

    // Transform to IStackComponent format
    let cumulativeHeight = 0;
    return sorted.map((seq: any) => {
      const component: IStackComponent = {
        instanceId: seq.id,
        product: {
          id: seq.componentCode || seq.id,
          componentCode: seq.componentCode || seq.id,
          name: seq.componentName,
          description: seq.componentName,
          // Use UPPERCASE for new componentType
          type: seq.componentType?.toUpperCase() || seq.componentType,
          heightInches: seq.heightInches,
          diameterInches: seq.diameterInches || 48,
          wallThicknessInches: 4,
          weightLbs: seq.weight || 0,
          weight: seq.weight || 0,
          // Support both new and legacy cost fields
          estimatedCost: seq.estimatedCost ?? seq.cost ?? 0,
          costUSD: seq.estimatedCost ?? seq.cost ?? 0,
        },
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += seq.heightInches / 12; // Convert inches to feet
      return component;
    });
  }

  // ============================================
  // NEW: Structure-Keyed Computed Getters
  // ============================================

  // Get all structures for the current project from the store
  get projectStructures(): any[] {
    if (!this.activeProjectId) return [];
    return (
      (this.rootStore as any).manholeStructuresStore?.entities?.filter(
        (s: any) => s.projectId === this.activeProjectId
      ) || []
    );
  }

  // Helper: Get a specific structure from the store by UUID
  getStructureFromStore = (structureId: string): Record<string, any> | null => {
    if (!structureId) return null;
    // Check if it's a temp structure (not in store yet)
    if (structureId.startsWith("temp-")) return null;
    return (
      (this.rootStore as any).manholeStructuresStore?.entities?.find(
        (s: any) => s.id === structureId
      ) || null
    );
  };

  // Helper: Get pipes for a specific structure from the store
  getPipesFromStore = (structureId: string): IPipeConnection[] => {
    if (!structureId || !this.activeProjectId) return [];
    // For temp structures, there's no saved data
    if (structureId.startsWith("temp-")) return [];

    // Filter by STRUCTURE ID (not just project ID)
    const pipes =
      (this.rootStore as any).manholePipeConnectionsStore?.entities?.filter(
        (p: any) => p.structureId === structureId
      ) || [];

    // Filter out deleted pipes
    const deletedIds = this.deletedPipeIds.get(structureId);
    return deletedIds ? pipes.filter((p: any) => !deletedIds.has(p.id)) : pipes;
  };

  // Helper: Get components for a specific structure from the store
  getComponentsFromStore = (structureId: string): IStackComponent[] => {
    if (!structureId || !this.activeProjectId) return [];
    // For temp structures, there's no saved data
    if (structureId.startsWith("temp-")) return [];

    // Filter by STRUCTURE ID (not just project ID)
    const sequences =
      (this.rootStore as any).manholeSequencesStore?.entities?.filter(
        (s: any) => s.structureId === structureId
      ) || [];

    // Filter out deleted sequences
    const deletedIds = this.deletedSequenceIds.get(structureId);
    const filtered = deletedIds
      ? sequences.filter((s: any) => !deletedIds.has(s.id))
      : sequences;

    // Transform to IStackComponent format
    return this.transformSequencesToComponents(filtered);
  };

  // Helper: Get internals for a specific structure from the store
  getInternalsFromStore = (structureId: string): IManholeInternals | null => {
    if (!structureId || !this.activeProjectId) return null;
    // For temp structures, there's no saved data
    if (structureId.startsWith("temp-")) return null;

    const internalsData = (
      this.rootStore as any
    ).manholeInternalsStore?.entities?.find(
      (i: any) => i.structureId === structureId
    );

    if (!internalsData) return null;

    // Transform to UI format
    return {
      hasSteps: internalsData.includeSteps || false,
      hasLadder:
        internalsData.ladderType && internalsData.ladderType !== "none",
      stepAngleDegrees: internalsData.stepAngle || 180,
      stepType: internalsData.stepType || "standard",
      ladderType: internalsData.ladderType || "none",
      hasPreChannelInvert: internalsData.preChannelInvert || false,
      hasGULiner: internalsData.guLiner || false,
      hasCustomInternal: internalsData.custom || false,
      customInternalDescription: internalsData.customDescription || "",
    } as IManholeInternals;
  };

  // Get current structure with edits merged
  get currentStructure(): Record<string, any> | null {
    if (!this.currentStructureId) return null;
    const saved = this.getStructureFromStore(this.currentStructureId);
    const edits = this.structureEdits.get(this.currentStructureId);

    if (edits && saved) {
      return { ...saved, ...edits };
    }
    if (edits) {
      // New unsaved structure - edits only
      return { id: this.currentStructureId, ...edits };
    }
    return saved;
  }

  // Derived from currentStructure — auto-updates when navigating between structures
  get selectedStructureCodeId(): string | null {
    return this.currentStructure?.structureCodeId || null;
  }

  get selectedStructureType(): string | null {
    return this.currentStructure?.structureType || null;
  }

  get sealantConfig(): ISealantConfig {
    const structure = this.currentStructure;
    const type = (structure?.sealantType || "none") as ISealantConfig["type"];
    return {
      type,
      gasketSize: type === "gasket" ? (parseInt(structure?.gasketSize) || 48) : undefined,
    };
  }

  // Get pipes for current structure with edits merged
  get currentPipes(): IPipeConnection[] {
    if (!this.currentStructureId) return [];
    const edits = this.pipeEdits.get(this.currentStructureId);
    if (edits !== undefined) {
      // Filter out any deleted items from edits too
      const deletedIds = this.deletedPipeIds.get(this.currentStructureId);
      return deletedIds ? edits.filter((p) => !deletedIds.has(p.id)) : edits;
    }
    return this.getPipesFromStore(this.currentStructureId);
  }

  // Get components for current structure with edits merged
  get currentComponents(): IStackComponent[] {
    if (!this.currentStructureId) return [];
    const edits = this.componentEdits.get(this.currentStructureId);
    if (edits !== undefined) {
      // Filter out any deleted items from edits too
      const deletedIds = this.deletedSequenceIds.get(this.currentStructureId);
      return deletedIds
        ? edits.filter((c) => !deletedIds.has(c.instanceId))
        : edits;
    }
    return this.getComponentsFromStore(this.currentStructureId);
  }

  // Get internals for current structure with edits merged
  get currentInternals(): IManholeInternals | null {
    if (!this.currentStructureId) return null;
    const edits = this.internalEdits.get(this.currentStructureId);
    if (edits !== undefined) return edits;
    return this.getInternalsFromStore(this.currentStructureId);
  }

  // Check if current structure has unsaved changes
  get currentStructureIsDirty(): boolean {
    if (!this.currentStructureId) return false;
    return (
      this.structureEdits.has(this.currentStructureId) ||
      this.pipeEdits.has(this.currentStructureId) ||
      this.componentEdits.has(this.currentStructureId) ||
      this.internalEdits.has(this.currentStructureId) ||
      this.deletedSequenceIds.has(this.currentStructureId) ||
      this.deletedPipeIds.has(this.currentStructureId)
    );
  }

  // Per-tab dirty indicators for badge display
  get projectTabIsDirty(): boolean {
    return this.projectEdits !== null;
  }

  get structureTabIsDirty(): boolean {
    if (!this.currentStructureId) return false;
    return this.structureEdits.has(this.currentStructureId);
  }

  get pipesTabIsDirty(): boolean {
    if (!this.currentStructureId) return false;
    return (
      this.pipeEdits.has(this.currentStructureId) ||
      this.deletedPipeIds.has(this.currentStructureId)
    );
  }

  get internalsTabIsDirty(): boolean {
    if (!this.currentStructureId) return false;
    return this.internalEdits.has(this.currentStructureId);
  }

  get componentsTabIsDirty(): boolean {
    if (!this.currentStructureId) return false;
    return (
      this.componentEdits.has(this.currentStructureId) ||
      this.deletedSequenceIds.has(this.currentStructureId)
    );
  }

  // Check if any structure has unsaved changes
  get hasAnyUnsavedChanges(): boolean {
    return (
      this.projectEdits !== null ||
      this.structureEdits.size > 0 ||
      this.pipeEdits.size > 0 ||
      this.componentEdits.size > 0 ||
      this.internalEdits.size > 0 ||
      this.deletedSequenceIds.size > 0 ||
      this.deletedPipeIds.size > 0
    );
  }

  // Check if a structure is new (temp) and unsaved
  isNewStructure = (structureId: string): boolean => {
    return structureId.startsWith("temp-");
  };

  // Get current structure index in the order
  get currentStructureIndex(): number {
    if (!this.currentStructureId) return -1;
    return this.structureOrder.indexOf(this.currentStructureId);
  }

  // Check if at first structure
  get isFirstStructure(): boolean {
    return this.currentStructureIndex <= 0;
  }

  // Check if at last structure
  get isLastStructure(): boolean {
    return this.currentStructureIndex >= this.structureOrder.length - 1;
  }

  // Get total structure count
  get structureCount(): number {
    return this.structureOrder.length;
  }

  // ============================================
  // NEW: Structure-Keyed Navigation Methods
  // ============================================

  // Initialize structure order when loading a project
  initializeStructureOrder = async (projectId: string) => {
    // Get all structures for this project from the store
    const structures =
      (this.rootStore as any).manholeStructuresStore?.entities?.filter(
        (s: any) =>
          s.projectId === projectId || s.manholeProjectId === projectId
      ) || [];

    // Sort by sequenceOrder or createdAt
    const sortedStructures = [...structures].sort((a, b) => {
      if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
        return a.sequenceOrder - b.sequenceOrder;
      }
      return (
        new Date(a.createdAt || 0).getTime() -
        new Date(b.createdAt || 0).getTime()
      );
    });

    runInAction(() => {
      this.structureOrder = sortedStructures.map((s: any) => s.id);
      // Set current to first structure if we have any
      if (this.structureOrder.length > 0 && !this.currentStructureId) {
        this.currentStructureId = this.structureOrder[0];
      }
    });

  };

  // Navigate to next structure (simple index-based, no saving required)
  /**
   * Navigate to next structure.
   * Fix for nav sequence bug (Clay 2026-01-22):
   * Re-read the index AFTER save completes because save may replace temp IDs
   * in structureOrder, changing the current index.
   */
  goToNextStructure = async () => {
    // Save first — this may replace temp IDs in structureOrder
    await this.saveCurrentStructureIfDirty();

    // Re-read index after save (temp ID may have been replaced)
    const currentIndex = this.structureOrder.indexOf(this.currentStructureId!);
    console.log("[NEXT] goToNextStructure called", {
      currentIndex,
      structureOrder: [...this.structureOrder],
      currentStructureId: this.currentStructureId,
      isLastStructure: this.isLastStructure,
    });

    if (currentIndex >= 0 && currentIndex < this.structureOrder.length - 1) {
      // Go to existing next structure
      const nextId = this.structureOrder[currentIndex + 1];
      console.log("[NEXT] Moving to existing structure:", nextId);
      runInAction(() => {
        this.currentStructureId = nextId;
      });
    } else {
      // At end - create new structure
      console.log("[NEXT] At end, creating new structure");
      this.createNewStructure();
      await this.setActiveConfigTab(1); // Switch to Structure tab for new structure
    }
    console.log("[NEXT] After navigation:", {
      currentStructureId: this.currentStructureId,
      currentStructureIndex: this.currentStructureIndex,
    });
  };

  /**
   * Navigate to previous structure.
   * Fix for nav sequence bug: Re-read index after save.
   */
  goToPreviousStructure = async () => {
    // Save first — this may replace temp IDs in structureOrder
    await this.saveCurrentStructureIfDirty();

    // Re-read index after save
    const currentIndex = this.structureOrder.indexOf(this.currentStructureId!);
    console.log("[PREV] goToPreviousStructure called", {
      currentIndex,
      structureOrder: [...this.structureOrder],
      currentStructureId: this.currentStructureId,
      isFirstStructure: this.isFirstStructure,
    });

    if (currentIndex > 0) {
      const prevId = this.structureOrder[currentIndex - 1];
      console.log("[PREV] Moving to previous structure:", prevId);
      runInAction(() => {
        this.currentStructureId = prevId;
      });
    } else {
      console.log("[PREV] Already at first structure, cannot go back");
    }
    console.log("[PREV] After navigation:", {
      currentStructureId: this.currentStructureId,
      currentStructureIndex: this.currentStructureIndex,
    });
  };

  // Navigate to a specific structure by index
  goToStructureByIndex = (index: number) => {
    console.log("[PAGE] goToStructureByIndex called", {
      requestedIndex: index,
      currentIndex: this.currentStructureIndex,
      structureOrder: [...this.structureOrder],
      currentStructureId: this.currentStructureId,
    });

    if (index >= 0 && index < this.structureOrder.length) {
      const targetId = this.structureOrder[index];
      console.log("[PAGE] Navigating to structure:", targetId);
      runInAction(() => {
        this.currentStructureId = targetId;
      });
    } else {
      console.log("[PAGE] Invalid index, no navigation");
    }
    console.log("[PAGE] After navigation:", {
      currentStructureId: this.currentStructureId,
      currentStructureIndex: this.currentStructureIndex,
    });
  };

  // Create a new structure (local-only until saved)
  createNewStructure = () => {
    const tempId = `temp-${uuid()}`;
    const nextOrder = this.structureOrder.length + 1;
    const defaultStructureId = `MH-${nextOrder}`;

    runInAction(() => {
      // Add to structure order
      this.structureOrder = [...this.structureOrder, tempId];
      this.currentStructureId = tempId;

      // Initialize structure edits with default values
      this.structureEdits.set(tempId, {
        structureId: defaultStructureId,
        station: "",
        structureSubType: "Type 1",
        catchDepthFeet: 0,
        rimElevation: 0,
        castingId: "",
        castingType: "",
        formAssignment: "",
        hatchType: "",
        status: "draft",
      });

      // Initialize empty arrays for other data
      this.pipeEdits.set(tempId, []);
      this.componentEdits.set(tempId, []);
      this.internalEdits.set(tempId, {
        hasSteps: false,
        hasLadder: false,
        stepAngleDegrees: 180,
        stepType: "standard",
        ladderType: "none",
        hasPreChannelInvert: false,
        hasGULiner: false,
        hasCustomInternal: false,
        customInternalDescription: "",
      } as IManholeInternals);
    });
  };

  // ============================================
  // NEW: Structure-Keyed Edit Methods
  // ============================================

  // Update a field on the current structure
  updateStructureField = (field: string, value: any) => {
    if (!this.currentStructureId) return;

    const current = this.structureEdits.get(this.currentStructureId) || {};
    runInAction(() => {
      this.structureEdits.set(this.currentStructureId!, {
        ...current,
        [field]: value,
      });
    });
  };

  // Update all pipes for the current structure
  updateCurrentPipes = (pipes: IPipeConnection[]) => {
    if (!this.currentStructureId) return;

    runInAction(() => {
      this.pipeEdits.set(this.currentStructureId!, pipes);
    });
  };

  // Add a pipe to the current structure
  addPipeToCurrentStructure = () => {
    if (!this.currentStructureId) return;

    const currentPipes = this.currentPipes;
    const pipeCount = currentPipes.length;
    const isFirstPipe = pipeCount === 0;

    const newPipe: IPipeConnection = {
      id: `temp-pipe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      manholeProjectId: this.activeProjectId || "",
      pipeNumber: pipeCount + 1,
      invertElevation: 0,
      pipeDiameterInches: 8,
      pipeMaterial: "PVC 3034",
      angleDegrees: 0,
      direction: isFirstPipe ? "outlet" : "inlet",
      connectionType: "hole",
      holeType: "40612AW",
      holeSizeInches: 12,
      // coreDrillHeightInches is now derived from the component stack via getCoreDrillHeight()
    };

    runInAction(() => {
      this.pipeEdits.set(this.currentStructureId!, [...currentPipes, newPipe]);
    });
  };

  /**
   * Add a pipe from a preset configuration.
   * Per Clay Prewitt (2026-01-06): Users should be able to select from predefined pipe
   * configurations when clicking "Add Pipe" instead of entering all details manually.
   */
  addPipeFromPreset = (presetValues: {
    pipeDiameterInches: number;
    pipeMaterial: string;
    connectionType: "hole" | "boot" | "adapter" | "collar";
    bootType?: string;
    holeSizeInches: number;
    holePositionInches: number;
    pipeSystem?: "Sanitary" | "Storm" | "Water";
    direction: "inlet" | "outlet";
    holeMethod?: "cast-in" | "cored" | "field-cut";
  }) => {
    // Delegate to structure-keyed method if we have a currentStructureId
    if (this.currentStructureId) {
      const currentPipes = this.currentPipes;
      const pipeCount = currentPipes.length;
      const isFirstPipe = pipeCount === 0;

      // First pipe is always outlet, subsequent pipes use user selection
      const direction = isFirstPipe ? "outlet" : presetValues.direction;

      // All pipes default to 0° — user must explicitly set the angle
      // Per Clay Prewitt (2026-01-31): auto-spread makes it too easy to miss wrong angles
      const angleDegrees = 0;

      const newPipe: IPipeConnection = {
        id: `temp-pipe-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        manholeProjectId: this.activeProjectId || "",
        pipeNumber: pipeCount + 1,
        invertElevation: 0,
        pipeDiameterInches: presetValues.pipeDiameterInches,
        pipeMaterial: presetValues.pipeMaterial,
        angleDegrees,
        direction,
        connectionType: presetValues.connectionType,
        bootType: presetValues.bootType as any,
        holeSizeInches: presetValues.holeSizeInches,
        holePositionInches: presetValues.holePositionInches,
        holeMethod: presetValues.holeMethod,
        pipeSystem: presetValues.pipeSystem,
        // coreDrillHeightInches is now derived from the component stack via getCoreDrillHeight()
      };

      runInAction(() => {
        this.pipeEdits.set(this.currentStructureId!, [
          ...currentPipes,
          newPipe,
        ]);
      });
      return;
    }

    // Fall back to editing project method
    if (!this.editingProject?.manholeStructure) return;

    const currentPipes = this.editingProject.manholeStructure.pipes;
    const pipeCount = currentPipes.length;
    const isFirstPipe = pipeCount === 0;

    // First pipe is always outlet, subsequent pipes use user selection
    const direction = isFirstPipe ? "outlet" : presetValues.direction;

    // All pipes default to 0° — user must explicitly set the angle
    // Per Clay Prewitt (2026-01-31): auto-spread makes it too easy to miss wrong angles
    const angleDegrees = 0;

    const newPipe: IPipeConnection = {
      id: `temp-pipe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      manholeProjectId: this.activeProjectId || "",
      pipeNumber: pipeCount + 1,
      invertElevation: 0,
      pipeDiameterInches: presetValues.pipeDiameterInches,
      pipeMaterial: presetValues.pipeMaterial,
      angleDegrees,
      direction,
      connectionType: presetValues.connectionType,
      bootType: presetValues.bootType as any,
      holeSizeInches: presetValues.holeSizeInches,
      holePositionInches: presetValues.holePositionInches,
      holeMethod: presetValues.holeMethod,
      pipeSystem: presetValues.pipeSystem,
    };

    runInAction(() => {
      this.editingProject!.manholeStructure!.pipes.push(newPipe);
    });
  };

  // Update a pipe in the current structure
  updatePipeInCurrentStructure = (
    pipeId: string,
    updates: Partial<IPipeConnection>
  ) => {
    if (!this.currentStructureId) return;

    const currentPipes = this.currentPipes;
    const pipeIndex = currentPipes.findIndex((p) => p.id === pipeId);
    if (pipeIndex === -1) return;

    const pipe = currentPipes[pipeIndex];

    // Safeguard: First pipe must remain outlet at 0°
    if (pipe.pipeNumber === 1) {
      updates = {
        ...updates,
        angleDegrees: 0,
        direction: "outlet",
      };
    }

    const updatedPipes = [...currentPipes];
    updatedPipes[pipeIndex] = { ...pipe, ...updates };

    runInAction(() => {
      this.pipeEdits.set(this.currentStructureId!, updatedPipes);
    });
  };

  // Remove a pipe from the current structure
  removePipeFromCurrentStructure = (pipeId: string) => {
    if (!this.currentStructureId) return;

    // Check if this pipe is from the store (has a real ID, not locally generated)
    const isFromStore =
      !pipeId.startsWith("pipe-") && !pipeId.startsWith("temp-");

    if (isFromStore) {
      // Track for deletion on save
      runInAction(() => {
        const deletedSet =
          this.deletedPipeIds.get(this.currentStructureId!) || new Set();
        deletedSet.add(pipeId);
        this.deletedPipeIds.set(this.currentStructureId!, deletedSet);
      });
    }

    const currentPipes = this.currentPipes;
    const filteredPipes = currentPipes
      .filter((p) => p.id !== pipeId)
      .map((pipe, index) => ({
        ...pipe,
        pipeNumber: index + 1,
      }));

    runInAction(() => {
      this.pipeEdits.set(this.currentStructureId!, filteredPipes);
    });
  };

  // Update all components for the current structure
  updateCurrentComponents = (components: IStackComponent[]) => {
    if (!this.currentStructureId) return;

    runInAction(() => {
      this.componentEdits.set(this.currentStructureId!, components);
    });
  };

  // Add a component to the current structure
  addComponentToCurrentStructure = (product: ICatalogProduct) => {
    if (!this.currentStructureId) return;

    const currentComponents = [...this.currentComponents];

    // Calculate correct insert position based on component type ordering
    // This ensures BASE is always at bottom, CONE/FLAT_TOP above risers, etc.
    const insertPosition = this.calculateInsertPositionByType(
      currentComponents,
      product.type
    );

    // Create new component
    const newComponent: IStackComponent = {
      instanceId: `comp-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      product,
      positionInStack: insertPosition,
      bottomElevation: 0, // Will be recalculated
    };

    // Insert at correct position
    currentComponents.splice(insertPosition, 0, newComponent);

    // Recalculate positions and elevations for all components
    let cumulativeHeight = 0;
    const recalculated = currentComponents.map((comp, i) => {
      const updated = {
        ...comp,
        positionInStack: i,
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += getEffectiveHeightInches(comp) / 12;
      return updated;
    });

    runInAction(() => {
      this.componentEdits.set(this.currentStructureId!, recalculated);
    });
  };

  // Calculate bottom elevation for new component
  calculateBottomElevationForNewComponent = (
    components: IStackComponent[]
  ): number => {
    if (components.length === 0) return 0;
    const lastComponent = components[components.length - 1];
    return (
      lastComponent.bottomElevation + getEffectiveHeightInches(lastComponent) / 12
    );
  };

  // Remove a component from the current structure
  removeComponentFromCurrentStructure = (instanceId: string) => {
    if (!this.currentStructureId) return;

    // Check if this component is from the store (has a real ID, not locally generated)
    const isFromStore =
      !instanceId.startsWith("comp-") && !instanceId.startsWith("temp-");

    if (isFromStore) {
      // Track for deletion on save
      runInAction(() => {
        const deletedSet =
          this.deletedSequenceIds.get(this.currentStructureId!) || new Set();
        deletedSet.add(instanceId);
        this.deletedSequenceIds.set(this.currentStructureId!, deletedSet);
      });
    }

    // Get current components (already filters out deleted items from store)
    const currentComponents = this.currentComponents;

    // Also remove from componentEdits if present there
    const index = currentComponents.findIndex(
      (c) => c.instanceId === instanceId
    );
    if (index === -1) {
      // Item was tracked for deletion but not in current view - UI should update via getter
      return;
    }

    const updatedComponents = [
      ...currentComponents.slice(0, index),
      ...currentComponents.slice(index + 1),
    ];

    // Recalculate positions and elevations
    let cumulativeHeight = 0;
    const recalculated = updatedComponents.map((comp, i) => {
      const updated = {
        ...comp,
        positionInStack: i,
        bottomElevation: cumulativeHeight,
      };
      cumulativeHeight += getEffectiveHeightInches(comp) / 12;
      return updated;
    });

    runInAction(() => {
      this.componentEdits.set(this.currentStructureId!, recalculated);
    });
  };

  // Update internals for the current structure
  updateCurrentInternals = (updates: Partial<IManholeInternals>) => {
    if (!this.currentStructureId) return;

    const current = this.currentInternals || {
      hasSteps: false,
      hasLadder: false,
      stepAngleDegrees: 180,
      stepType: "standard",
      ladderType: "none",
      hasPreChannelInvert: false,
      hasGULiner: false,
      hasCustomInternal: false,
      customInternalDescription: "",
    };

    runInAction(() => {
      this.internalEdits.set(this.currentStructureId!, {
        ...current,
        ...updates,
      } as IManholeInternals);
    });
  };

  // ============================================
  // NEW: Structure-Keyed Save Methods
  // ============================================

  // Save the current structure to the backend
  saveCurrentStructure = async () => {
    // Guard against concurrent saves - prevents duplicate component creation
    if (this.isLoading) {
      console.log("[SAVE] Aborting - save already in progress");
      return;
    }

    console.log("[SAVE] saveCurrentStructure called", {
      currentStructureId: this.currentStructureId,
      activeProjectId: this.activeProjectId,
      isDirty: this.isDirty,
      currentStructureIsDirty: this.currentStructureIsDirty,
      structureOrder: [...this.structureOrder],
      hasStructureEdits: this.structureEdits.has(this.currentStructureId || ""),
      hasPipeEdits: this.pipeEdits.has(this.currentStructureId || ""),
      hasComponentEdits: this.componentEdits.has(this.currentStructureId || ""),
      hasInternalEdits: this.internalEdits.has(this.currentStructureId || ""),
    });

    if (!this.currentStructureId || !this.activeProjectId) {
      console.log(
        "[SAVE] Aborting - missing currentStructureId or activeProjectId"
      );
      return;
    }

    const isNew = this.isNewStructure(this.currentStructureId);
    const originalStructureId = this.currentStructureId; // Capture before any ID updates
    const structureData = this.currentStructure;
    const pipes = this.currentPipes;
    const components = this.currentComponents;
    const internals = this.currentInternals;

    console.log("[SAVE] Structure details:", {
      isNew,
      structureData: structureData
        ? { id: structureData.id, structureId: structureData.structureId }
        : null,
      pipesCount: pipes?.length || 0,
      componentsCount: components?.length || 0,
      hasInternals: !!internals,
    });

    this.setLoading(true);

    try {
      let savedStructureId = this.currentStructureId;

      // 1. Save structure (create or update)
      if (isNew && (this.rootStore as any).manholeStructuresStore) {
        // Build create data with only valid fields from the schema
        const createData: Record<string, unknown> = {
          projectId: this.activeProjectId,
          structureId:
            structureData?.structureId || `MH-${this.structureOrder.length}`,
          status: "draft",
        };

        // Add optional fields only if they have values
        if (structureData?.station) createData.station = structureData.station;
        if (structureData?.type) createData.type = structureData.type;
        if (structureData?.structureSubType)
          createData.structureSubType = structureData.structureSubType;
        if (structureData?.catchDepthFeet !== undefined)
          createData.catchDepthFeet = Number(structureData.catchDepthFeet) || 0;
        if (structureData?.rimElevation !== undefined)
          createData.rimElevation = Number(structureData.rimElevation) || 0;
        if (structureData?.castingId)
          createData.castingId = structureData.castingId;
        if (structureData?.castingType)
          createData.castingType = structureData.castingType;
        if (structureData?.formAssignment)
          createData.formAssignment = structureData.formAssignment;
        if (structureData?.hatchType)
          createData.hatchType = structureData.hatchType;
        if (structureData?.structureCodeId)
          createData.structureCodeId = structureData.structureCodeId;
        if (structureData?.customerNeededDate)
          createData.customerNeededDate = structureData.customerNeededDate;
        if (structureData?.priority)
          createData.priority = structureData.priority;

        const newStructure = await (
          this.rootStore as any
        ).manholeStructuresStore.create(createData);
        savedStructureId = newStructure.id;

        // Update structure order with real ID
        runInAction(() => {
          const index = this.structureOrder.indexOf(this.currentStructureId!);
          if (index !== -1) {
            const updatedOrder = [...this.structureOrder];
            updatedOrder[index] = savedStructureId;
            this.structureOrder = updatedOrder;
            this.currentStructureId = savedStructureId;
          }
        });
      } else if (
        !isNew &&
        this.structureEdits.has(this.currentStructureId!) &&
        (this.rootStore as any).manholeStructuresStore
      ) {
        // Only patch if there are actual structure edits
        const edits = this.structureEdits.get(this.currentStructureId!);
        if (edits && Object.keys(edits).length > 0) {
          // Build patch object with only the edited values
          const patchData: Record<string, unknown> = {};

          // Only include fields that were actually edited
          if ("structureId" in edits && typeof edits.structureId === "string")
            patchData.structureId = edits.structureId;
          if ("station" in edits && typeof edits.station === "string")
            patchData.station = edits.station;
          if (
            "structureSubType" in edits &&
            typeof edits.structureSubType === "string"
          )
            patchData.structureSubType = edits.structureSubType;
          if (
            "catchDepthFeet" in edits &&
            edits.catchDepthFeet !== undefined &&
            edits.catchDepthFeet !== null
          ) {
            patchData.catchDepthFeet = Number(edits.catchDepthFeet) || 0;
          }
          if (
            "rimElevation" in edits &&
            edits.rimElevation !== undefined &&
            edits.rimElevation !== null
          ) {
            patchData.rimElevation = Number(edits.rimElevation) || 0;
          }
          if ("castingId" in edits && typeof edits.castingId === "string")
            patchData.castingId = edits.castingId;
          if ("castingType" in edits && typeof edits.castingType === "string")
            patchData.castingType = edits.castingType;
          if (
            "formAssignment" in edits &&
            typeof edits.formAssignment === "string"
          )
            patchData.formAssignment = edits.formAssignment;
          if ("hatchType" in edits && typeof edits.hatchType === "string")
            patchData.hatchType = edits.hatchType;
          if ("type" in edits && typeof edits.type === "string")
            patchData.type = edits.type;
          if ("structureCodeId" in edits && edits.structureCodeId)
            patchData.structureCodeId = edits.structureCodeId;
          if ("customerNeededDate" in edits)
            patchData.customerNeededDate = edits.customerNeededDate || null;
          if ("priority" in edits) patchData.priority = edits.priority || null;

          // Only patch if there's something to update
          if (Object.keys(patchData).length > 0) {
            console.log("[SAVE] Structure - patching with edits:", patchData);
            await (this.rootStore as any).manholeStructuresStore.patch(
              this.currentStructureId,
              patchData
            );
          }
        }
      } else if (!isNew) {
        console.log("[SAVE] Structure - no edits, skipping patch");
      }

      // 2. Save pipes (only if there are pipe edits or deletions for this structure)
      const hasPipeChanges =
        this.pipeEdits.has(this.currentStructureId!) ||
        this.deletedPipeIds.has(this.currentStructureId!) ||
        isNew;

      if (
        hasPipeChanges &&
        (this.rootStore as any).manholePipeConnectionsStore
      ) {
        const pipeStore = (this.rootStore as any).manholePipeConnectionsStore;

        // Delete existing pipes for this STRUCTURE (not entire project)
        const existingPipes = pipeStore.entities.filter(
          (p: any) => p.structureId === savedStructureId
        );
        console.log("[SAVE] Pipes - deleting existing:", existingPipes.length);
        for (const pipe of existingPipes) {
          try {
            await pipeStore.remove(pipe.id);
          } catch (e) {
            console.warn("Failed to remove pipe:", pipe.id, e);
          }
        }

        // Create new pipes with structure ID
        console.log("[SAVE] Pipes - creating new:", pipes.length);
        for (const pipe of pipes) {
          // Build pipe data object, only including optional fields if they have values
          // to avoid sending undefined which fails backend validation
          const pipeData: Record<string, unknown> = {
            manholeProjectId: this.activeProjectId, // Required by database
            structureId: savedStructureId,
            pipeNumber: pipe.pipeNumber,
            invertElevation: pipe.invertElevation,
            pipeDiameterInches: pipe.pipeDiameterInches,
          };

          // Only include optional string/number fields if defined
          if (pipe.pipeMaterial) pipeData.pipeMaterial = pipe.pipeMaterial;
          if (pipe.angleDegrees !== undefined)
            pipeData.angleDegrees = pipe.angleDegrees;
          if (pipe.direction) pipeData.direction = pipe.direction;
          if (pipe.holeType) pipeData.holeType = pipe.holeType;
          if (pipe.holeSizeInches !== undefined)
            pipeData.holeSizeInches = pipe.holeSizeInches;
          if (pipe.holeMethod) pipeData.holeMethod = pipe.holeMethod;
          if (pipe.connectionType)
            pipeData.connectionType = pipe.connectionType;
          if (pipe.requiresSpecialFormer !== undefined)
            pipeData.requiresSpecialFormer = pipe.requiresSpecialFormer;

          await pipeStore.create(pipeData);
        }
        console.log(
          "[SAVE] Pipes - saved successfully:",
          pipes.length,
          "pipes"
        );
      } else {
        console.log("[SAVE] Pipes - no changes, skipping");
      }

      // 3. Save components (only if there are component edits or deletions for this structure)
      const hasComponentChanges =
        this.componentEdits.has(this.currentStructureId!) ||
        this.deletedSequenceIds.has(this.currentStructureId!) ||
        isNew;

      if (
        hasComponentChanges &&
        (this.rootStore as any).manholeSequencesStore
      ) {
        const sequenceStore = (this.rootStore as any).manholeSequencesStore;

        // Delete existing sequences for this STRUCTURE (not entire project)
        const existingSequences = sequenceStore.entities.filter(
          (s: any) => s.structureId === savedStructureId
        );
        console.log(
          "[SAVE] Components - deleting existing sequences:",
          existingSequences.length
        );
        for (const seq of existingSequences) {
          try {
            await sequenceStore.remove(seq.id);
          } catch (e) {
            console.warn("Failed to remove sequence:", seq.id, e);
          }
        }

        // Create new sequence entries with structure ID
        console.log(
          "[SAVE] Components - creating new sequences:",
          components.length
        );
        let runningHeight = 0;
        for (let i = 0; i < components.length; i++) {
          const comp = components[i];
          runningHeight += getEffectiveHeightInches(comp);
          await sequenceStore.create({
            manholeProjectId: this.activeProjectId, // Required by database
            structureId: savedStructureId,
            sequenceOrder: i + 1,
            componentCode: comp.product.id || comp.product.productId,
            componentName: comp.product.name || comp.product.description,
            componentType: comp.product.type,
            heightInches: getEffectiveHeightInches(comp),
            heightModifierInches: comp.heightModifierInches || 0,
            diameterInches: comp.product.diameterInches,
            weight: comp.product.weightLbs,
            cost: comp.product.costUSD,
            quantity: 1,
            cumulativeHeight: runningHeight,
          });
        }
        console.log(
          "[SAVE] Components - saved successfully:",
          components.length,
          "sequence components"
        );
      } else {
        console.log("[SAVE] Components - no changes, skipping");
      }

      // 4. Save internals (only if there are internal edits for this structure)
      const hasInternalChanges =
        this.internalEdits.has(this.currentStructureId!) || isNew;

      if (
        hasInternalChanges &&
        internals &&
        (this.rootStore as any).manholeInternalsStore
      ) {
        const internalsStore = (this.rootStore as any).manholeInternalsStore;

        // Check for existing internals for this STRUCTURE
        const existing = internalsStore.entities.find(
          (i: any) => i.structureId === savedStructureId
        );
        console.log("[SAVE] Internals - existing record found:", !!existing);

        const internalsData: Record<string, unknown> = {
          manholeProjectId: this.activeProjectId, // Required by database
          structureId: savedStructureId,
          includeSteps: internals.hasSteps,
          stepAngle: internals.stepAngleDegrees,
          stepType: internals.stepType || "standard",
          ladderType: internals.hasLadder
            ? internals.ladderType || "steel"
            : "none",
          preChannelInvert:
            (internals as IManholeInternalsExtended).hasPreChannelInvert ||
            false,
          guLiner: (internals as IManholeInternalsExtended).hasGULiner || false,
          custom:
            (internals as IManholeInternalsExtended).hasCustomInternal || false,
          customDescription:
            (internals as IManholeInternalsExtended)
              .customInternalDescription || "",
        };

        if (existing) {
          console.log("[SAVE] Internals - updating existing:", existing.id);
          await internalsStore.patch(existing.id, internalsData);
        } else {
          console.log("[SAVE] Internals - creating new record");
          await internalsStore.create(internalsData);
        }
        console.log("[SAVE] Internals - saved successfully:", {
          hasSteps: internals.hasSteps,
          hasLadder: internals.hasLadder,
          stepType: internals.stepType,
        });
      } else {
        console.log("[SAVE] Internals - no changes, skipping");
      }

      // 5. Clear edit maps and deletion tracking for this structure
      // Use originalStructureId (captured at start) to ensure we clear temp ID entries
      const oldTempId = isNew ? originalStructureId : null;
      runInAction(() => {
        // If we had a temp ID, remove old entries
        if (oldTempId && oldTempId !== savedStructureId) {
          this.structureEdits.delete(oldTempId);
          this.pipeEdits.delete(oldTempId);
          this.componentEdits.delete(oldTempId);
          this.internalEdits.delete(oldTempId);
          this.deletedSequenceIds.delete(oldTempId);
          this.deletedPipeIds.delete(oldTempId);
        }
        // Also clear any entries for the saved ID
        this.structureEdits.delete(savedStructureId);
        this.pipeEdits.delete(savedStructureId);
        this.componentEdits.delete(savedStructureId);
        this.internalEdits.delete(savedStructureId);
        this.deletedSequenceIds.delete(savedStructureId);
        this.deletedPipeIds.delete(savedStructureId);

        // Reset isDirty flag after successful save
        this.isDirty = false;
      });

      console.log("[SAVE] ✅ Structure saved successfully:", {
        structureId: savedStructureId,
        isNew,
        pipesCount: pipes.length,
        componentsCount: components.length,
        hasInternals: !!internals,
      });

      // Sync URL with persisted IDs after successful save
      if (
        this.activeProjectId &&
        !this.activeProjectId.startsWith("temp-") &&
        savedStructureId &&
        !savedStructureId.startsWith("temp-") &&
        (this.rootStore as any).routerStore
      ) {
        (this.rootStore as any).routerStore.goTo("Manhole Designer", null, {
          projectId: this.activeProjectId,
          structureId: savedStructureId,
        });
      }
    } catch (error: any) {
      console.error("Failed to save structure:", error);
      this.setError("Failed to save structure: " + error.message);
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Save current structure if it has unsaved changes.
   * Used for auto-save on navigation between structures.
   */
  /**
   * Save current structure if dirty, with re-entry guard.
   * Prevents concurrent saves that cause data duplication or loss.
   */
  saveCurrentStructureIfDirty = async (): Promise<void> => {
    if (this.isSaving) return; // Prevent concurrent saves

    // For new projects without a persisted projectId, use isDirty (which
    // covers project-level edits like customerName/jobName) so that the
    // project gets created in the DB on the first tab change.
    const needsNewProjectSave =
      !this.activeProjectId &&
      this.isDirty &&
      this.editingProject &&
      this.canAutoSave;

    // For existing projects, check if project-level metadata changed
    // (e.g. customerName, jobName edited on the Project tab)
    // Use projectEdits (not isDirty) to avoid false positives from structure edits
    const needsProjectMetadataSave =
      !!this.activeProjectId &&
      this.projectEdits !== null &&
      !!this.editingProject;

    const needsStructureSave =
      this.currentStructureIsDirty && !!this.currentStructureId;

    if (!needsNewProjectSave && !needsProjectMetadataSave && !needsStructureSave) {
      return;
    }

    this.isSaving = true;
    try {
      if (needsNewProjectSave) {
        // saveProject creates the project + structure and updates the URL
        // with real IDs so subsequent saves use saveCurrentStructure.
        await this.saveProject();
      } else {
        // Save project metadata and structure independently
        if (needsProjectMetadataSave) {
          await this.saveProject();
        }
        if (needsStructureSave) {
          await this.saveCurrentStructure();
        }
      }
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  // Save all structures with unsaved changes
  saveAllStructures = async () => {
    console.log("[FINISH] saveAllStructures called", {
      currentStructureId: this.currentStructureId,
      structureOrder: [...this.structureOrder],
      structureEditsKeys: [...this.structureEdits.keys()],
      pipeEditsKeys: [...this.pipeEdits.keys()],
      componentEditsKeys: [...this.componentEdits.keys()],
      internalEditsKeys: [...this.internalEdits.keys()],
    });

    this.setLoading(true);

    try {
      // Get all structure IDs that have edits
      const dirtyStructureIds = new Set<string>();

      for (const id of this.structureEdits.keys()) {
        dirtyStructureIds.add(id);
      }
      for (const id of this.pipeEdits.keys()) {
        dirtyStructureIds.add(id);
      }
      for (const id of this.componentEdits.keys()) {
        dirtyStructureIds.add(id);
      }
      for (const id of this.internalEdits.keys()) {
        dirtyStructureIds.add(id);
      }

      console.log("[FINISH] Dirty structures to save:", [...dirtyStructureIds]);

      // Save each dirty structure
      const originalStructureId = this.currentStructureId;
      for (const structureId of dirtyStructureIds) {
        console.log("[FINISH] Saving structure:", structureId);
        runInAction(() => {
          this.currentStructureId = structureId;
        });
        await this.saveCurrentStructure();
      }

      // Restore original current structure
      runInAction(() => {
        this.currentStructureId = originalStructureId;
      });

      console.log("All structures saved successfully");
    } catch (error: any) {
      console.error("Failed to save all structures:", error);
      this.setError("Failed to save all structures: " + error.message);
    } finally {
      this.setLoading(false);
    }
  };

  // Clear all edit maps (discard changes)
  discardAllChanges = () => {
    runInAction(() => {
      this.projectEdits = null;
      this.structureEdits.clear();
      this.pipeEdits.clear();
      this.componentEdits.clear();
      this.internalEdits.clear();

      // Remove temp structures from order
      this.structureOrder = this.structureOrder.filter(
        (id) => !id.startsWith("temp-")
      );

      // Reset to first structure if current was temp
      if (
        this.currentStructureId?.startsWith("temp-") &&
        this.structureOrder.length > 0
      ) {
        this.currentStructureId = this.structureOrder[0];
      } else if (this.structureOrder.length === 0) {
        this.currentStructureId = null;
      }
    });
  };

  get catalogProducts(): ICatalogProduct[] {
    // First check manhole-components service
    let products =
      (this.rootStore as any).manholeComponentsStore?.entities || [];

    // If empty, fall back to products service with filter
    if (products.length === 0) {
      products =
        (this.rootStore as any).productsStore?.entities?.filter(
          (p: any) => p.isManholeProduct === true
        ) || [];
    }

    // If still empty and in development, check for mock data
    if (products.length === 0 && process.env.NODE_ENV === "development") {
      const mockDataStr = sessionStorage.getItem("mockManholeComponents");
      if (mockDataStr) {
        try {
          products = JSON.parse(mockDataStr);
          console.log("Using mock manhole components:", products.length);
        } catch (e) {
          console.error("Failed to parse mock components:", e);
        }
      }
    }

    // Normalize field names for consistent access across different sources
    // MIGRATION: Supports both old (lowercase type, cost, productCode) and new (UPPERCASE type, estimatedCost, componentCode)
    return products
      .map((p: any) => ({
        ...p,
        // Map schema fields to expected UI fields
        id: p.id,
        // Normalize type to uppercase for new schema, preserve original for backwards compat
        type:
          (p.componentType || p.type || p.manholeType)?.toUpperCase() || p.type,
        name: p.componentName || p.name,
        // Fall back to componentName if description is empty (UI displays description field)
        description: p.description || p.componentName || p.name || "",
        heightInches: p.heightInches,
        diameterInches: p.diameterInches,
        weightLbs: p.weight || p.weightLbs,
        weight: p.weight || p.weightLbs,
        // Support both new (estimatedCost) and legacy (cost, costUSD, basePrice) fields
        estimatedCost:
          p.estimatedCost ?? p.cost ?? p.costUSD ?? p.basePrice ?? 0,
        costUSD: p.estimatedCost ?? p.cost ?? p.costUSD ?? p.basePrice ?? 0,
        // componentCode is the new primary identifier (replaces productCode)
        componentCode: p.componentCode || p.productCode || p.productId || "",
        productId: p.componentCode || p.productCode || p.productId,
        wallThicknessInches: p.wallThicknessInches || 5, // Default if not provided
        isActive: p.isActive !== false,
      }))
      .filter((p) => p.isActive !== false)
      .sort((a, b) =>
        (a.componentCode || "").localeCompare(b.componentCode || "")
      );
  }

  // ============================================
  // Structure Code Computed Properties
  // ============================================

  // Get all available structure codes from the service store
  /**
   * Set the selected structure type filter (Sanitary, Storm, etc.).
   * Per Clay Prewitt (2026-01-22):
   * "So we're going to pick whether it's a sanitary sewer, a storm, whatever it may be...
   * it's only going to give me the sewer structure codes."
   */
  setSelectedStructureType = (type: string | null): void => {
    if (this.currentStructureId) {
      const edits = this.structureEdits.get(this.currentStructureId) || {};
      this.structureEdits.set(this.currentStructureId, {
        ...edits,
        structureType: type,
        structureCodeId: undefined, // clear code when type changes
      });
    }
  };

  /**
   * Get all structure codes, optionally filtered by selected structure type.
   * When a type is selected, only codes matching that type are returned.
   */
  get filteredStructureCodes(): Array<{
    id: string;
    structureCode: string;
    name: string;
    description?: string;
    plantId: string;
    isActive: boolean;
  }> {
    const all = this.structureCodes;
    if (!this.selectedStructureType) return all;

    const typeLower = this.selectedStructureType.toLowerCase();
    if (typeLower === "custom") return all;

    const matchesType = (code: { name: string; description?: string }) => {
      const text = `${code.name} ${code.description || ""}`.toLowerCase();
      if (typeLower === "sanitary") {
        return text.includes("sewer") || text.includes("sanitary");
      }
      if (typeLower === "storm") {
        return text.includes("storm");
      }
      if (typeLower === "water") {
        return text.includes("water");
      }
      return false;
    };

    const filtered = all.filter(matchesType);
    // If no codes match the type filter, show all codes rather than an empty list
    return filtered.length > 0 ? filtered : all;
  }

  get structureCodes(): Array<{
    id: string;
    structureCode: string;
    name: string;
    description?: string;
    plantId: string;
    isActive: boolean;
  }> {
    return (
      (
        this.rootStore as RootStore & {
          structureCodesStore?: {
            entities: Array<{
              id: string;
              structureCode: string;
              name: string;
              description?: string;
              plantId: string;
              isActive: boolean;
            }>;
          };
        }
      ).structureCodesStore?.entities || []
    );
  }

  // Get component IDs assigned to the selected structure code
  get structureCodeComponentIds(): Set<string> {
    if (!this.selectedStructureCodeId) return new Set();
    const assignments: Array<{
      structureCodeId: string;
      manholeComponentId: string;
    }> =
      (
        this.rootStore as RootStore & {
          structureCodeComponentsStore?: {
            entities: Array<{
              structureCodeId: string;
              manholeComponentId: string;
            }>;
          };
        }
      ).structureCodeComponentsStore?.entities || [];
    return new Set(
      assignments
        .filter((a) => a.structureCodeId === this.selectedStructureCodeId)
        .map((a) => a.manholeComponentId)
    );
  }

  // Filter catalog products by selected structure code
  // When no structure code is selected, return all products
  get filteredCatalogProducts(): ICatalogProduct[] {
    const all = this.catalogProducts;
    if (!this.selectedStructureCodeId) return all;
    const ids = this.structureCodeComponentIds;
    return all.filter((p) => ids.has(p.id as string));
  }

  // Get the full selected structure code object
  get selectedStructureCode(): any {
    if (!this.selectedStructureCodeId) return null;
    const structureCodesStore = (this.rootStore as any).structureCodesStore;
    return (
      structureCodesStore?.entities?.find(
        (c: any) => c.id === this.selectedStructureCodeId
      ) || null
    );
  }

  /**
   * Whether the user can add components to the stack.
   * Per Clay Prewitt (2026-01-22): Users must select a structure code before
   * adding any components — the code determines which components are available.
   */
  get canAddComponents(): boolean {
    return !!this.selectedStructureCodeId;
  }

  /**
   * Set the selected structure code and apply defaults.
   * Per Clay Prewitt (2026-01-22): Selecting a structure code should auto-populate
   * the casting, cone, ring size, pre-channel, floor thickness, and diameter.
   */
  setSelectedStructureCode = (id: string | null): void => {
    if (this.currentStructureId) {
      const edits = this.structureEdits.get(this.currentStructureId) || {};
      this.structureEdits.set(this.currentStructureId, {
        ...edits,
        structureCodeId: id || undefined,
      });
    }

    // Apply defaults from the structure code
    if (id) {
      this.applyStructureCodeDefaults(id);
    }
  };

  /**
   * Apply structure code defaults to the current structure.
   * Per Clay Prewitt (2026-01-22):
   * "When you get to your structure defaults, this is where you're defaulting that
   * a 48 inch sewer manhole is always going to have a pre-channel. So it automatically,
   * when you load that, it automatically puts it in. Currently it's picking an 817 sewer
   * ring and cover... And it's picking a six inch ring... And it's picking an eccentric cone."
   */
  applyStructureCodeDefaults = (structureCodeId: string): void => {
    const structureCode = this.structureCodes.find((c) => c.id === structureCodeId);
    if (!structureCode) return;

    const structureId = this.currentStructureId;
    if (!structureId) return;

    // Determine diameter and floor thickness from structure code name
    // Parse diameter from name like "48 Type 1 Sewer Manhole" or "60 inch..."
    const nameMatch = structureCode.name.match(/^(\d+)/);
    const diameterInches = nameMatch ? parseInt(nameMatch[1]) : 48;
    const floorThicknessInches = getDefaultFloorThickness(diameterInches);

    // Auto-infer structure type from code name
    const nameLower = structureCode.name.toLowerCase();
    const isSewer = nameLower.includes("sewer") || nameLower.includes("sanitary");
    const isStorm = nameLower.includes("storm");
    const isWater = nameLower.includes("water");
    const inferredType = isSewer ? "Sanitary" : isStorm ? "Storm" : isWater ? "Water" : "Custom";

    // Apply structure-level defaults
    const currentEdits = this.structureEdits.get(structureId) || {};
    this.structureEdits.set(structureId, {
      ...currentEdits,
      floorThicknessInches,
      diameterInches,
      wallThicknessInches: 5, // Standard wall thickness
      insideCircumferenceFeet: parseFloat(((Math.PI * diameterInches) / 12).toFixed(2)),
      structureCodeId,
      structureType: inferredType,
    });

    // Auto-add pre-channel for sewer manholes
    if (isSewer) {
      const currentInternals = this.internalEdits.get(structureId) || {} as any;
      this.internalEdits.set(structureId, {
        ...currentInternals,
        hasPreChannel: true,
      });
    }

    // Look up default components from the structure code's assigned components
    const assignedComponents = this.filteredCatalogProducts;

    // Auto-add default cone (first cone found in assigned components)
    const defaultCone = assignedComponents.find((p) =>
      isComponentType(p.type, "CONE") || isComponentType(p.type, "FLAT_TOP")
    );

    // Auto-add default casting (first casting-like component — usually adjustment ring)
    // Note: Castings are handled separately in the casting selection UI

    // If components list is empty and we have a default cone, seed the stack
    const currentComponents = this.componentEdits.get(structureId);
    if ((!currentComponents || currentComponents.length === 0) && defaultCone) {
      // Don't auto-populate components - let user add base first per Titan workflow
      // But store the default cone ID for later auto-add when base is selected
      this.structureEdits.set(structureId, {
        ...this.structureEdits.get(structureId),
        defaultConeId: defaultCone.id,
        defaultRingSizeInches: 6, // 99% use 6" per Clay
      });
    }

    this.isDirty = true;
  };

  // Type filter getters use isComponentType for case-insensitive comparison
  // NOTE: These now use filteredCatalogProducts to respect structure code selection
  get bases(): ICatalogProduct[] {
    return this.filteredCatalogProducts.filter((p) =>
      isComponentType(p.type, "BASE")
    );
  }

  get risers(): ICatalogProduct[] {
    return this.filteredCatalogProducts
      .filter((p) => isComponentType(p.type, "RISER"))
      .sort((a, b) => b.heightInches - a.heightInches); // Sort largest first for optimization
  }

  get cones(): ICatalogProduct[] {
    return this.filteredCatalogProducts.filter((p) =>
      isComponentType(p.type, "CONE")
    );
  }

  get slabs(): ICatalogProduct[] {
    // 'slab' maps to 'SECTION' in new schema
    return this.filteredCatalogProducts.filter(
      (p) =>
        isComponentType(p.type, "SECTION") || isComponentType(p.type, "SLAB")
    );
  }

  get frames(): ICatalogProduct[] {
    // 'frame' maps to 'OTHER' in new schema, but keep specific filter
    return this.filteredCatalogProducts.filter(
      (p) =>
        isComponentType(p.type, "FRAME") ||
        (p.type?.toUpperCase() === "OTHER" &&
          p.name?.toLowerCase().includes("frame"))
    );
  }

  get covers(): ICatalogProduct[] {
    // 'cover' maps to 'OTHER' in new schema, but keep specific filter
    return this.filteredCatalogProducts.filter(
      (p) =>
        isComponentType(p.type, "COVER") ||
        (p.type?.toUpperCase() === "OTHER" &&
          p.name?.toLowerCase().includes("cover"))
    );
  }

  get accessories(): ICatalogProduct[] {
    return this.filteredCatalogProducts.filter(
      (p) =>
        isComponentType(p.type, "OTHER") || isComponentType(p.type, "ACCESSORY")
    );
  }

  get lowestInvert(): number {
    // NEW: Use structure-keyed pipes when available
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (this.editingProject || this.activeProject)?.manholeStructure?.pipes ||
        [];

    if (!pipes.length) return 0;

    return Math.min(...pipes.map((p) => p.invertElevation));
  }

  /**
   * Get the outlet invert elevation (flow line).
   * Per Clay Prewitt: The invert is the inside elevation of the piping at flow line.
   * Rim to Invert should be calculated from the outlet pipe specifically.
   * When there are multiple outlets, returns the lowest outlet invert
   * as that determines the primary flow line.
   */
  get outletInvert(): number {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (this.editingProject || this.activeProject)?.manholeStructure?.pipes ||
        [];

    const outletPipes = pipes.filter((p) => p.direction === "outlet");
    if (outletPipes.length === 0) return this.lowestInvert; // Fallback to lowest if no outlet defined

    // Use the lowest outlet invert (determines primary flow line)
    return Math.min(...outletPipes.map((p) => p.invertElevation));
  }

  get requiredPrecastHeight(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return 0;

    const rimElevation = project.manholeStructure.rimElevation;
    const lowestInvert = this.lowestInvert;

    // Assume frame and cover height is 6 inches (0.5 feet)
    const frameAndCoverHeight = 0.5;

    return Math.max(0, rimElevation - lowestInvert - frameAndCoverHeight);
  }

  get rimToInvert(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return 0;

    const rimElevation =
      project.manholeStructure.rimElevation || project.rimElevation || 0;
    const outletInvert = this.outletInvert; // Use outlet pipe's invert (flow line)

    return Math.max(0, rimElevation - outletInvert);
  }

  /**
   * Calculate overall depth (contractor dig depth).
   *
   * Per Clay Prewitt (2026-01-22):
   * "How far is it to the bottom of the manhole? You're going to add .67 to it
   * in order to get to the bottom... you have 2 of annular space... and a 6 inch floor.
   * So you've got 8 inches before you're to the actual bottom."
   *
   * Formula: Overall Depth = Rim-to-Invert + Annular Space + Floor Thickness
   *
   * This tells contractors how deep to dig their hole.
   */
  get overallDepth(): number {
    const rimToInvert = this.rimToInvert;
    if (rimToInvert <= 0) return 0;

    // Get floor thickness from structure or default based on diameter
    const project = this.editingProject || this.activeProject;
    const structure = project?.manholeStructure;
    const floorThicknessInches = structure?.floorThicknessInches ||
      getDefaultFloorThickness(this.structureDiameterInches);

    // Get annular space from outlet pipe
    const annularSpaceInches = this.outletAnnularSpaceInches;

    return calculateOverallDepth(rimToInvert, floorThicknessInches, annularSpaceInches);
  }

  /**
   * Get the height below the pipe invert (floor + annular space).
   * This is the depth from the pipe invert to the actual bottom of the manhole.
   */
  get belowInvertHeight(): number {
    const project = this.editingProject || this.activeProject;
    const structure = project?.manholeStructure;
    const floorThicknessInches = structure?.floorThicknessInches ||
      getDefaultFloorThickness(this.structureDiameterInches);
    const annularSpaceInches = this.outletAnnularSpaceInches;

    return (floorThicknessInches + annularSpaceInches) / 12;
  }

  /**
   * Get the outlet pipe's annular space in inches.
   * Annular space = (hole size - pipe diameter) / 2
   */
  get outletAnnularSpaceInches(): number {
    const holeSize = this.outletHoleSizeInches;
    const pipeDiameter = this.outletPipeDiameterInches;
    if (!holeSize || !pipeDiameter || holeSize <= pipeDiameter) {
      return 2; // Default 2" for typical 8" pipe in 12" hole
    }
    return (holeSize - pipeDiameter) / 2;
  }

  /**
   * Get the structure diameter in inches (from components or default).
   */
  get structureDiameterInches(): number {
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure?.components || []);

    // Get diameter from base or first component
    const baseComponent = components.find((c) =>
      (c.product?.type || "").toUpperCase().includes("BASE")
    );
    if (baseComponent?.product?.diameterInches) {
      return baseComponent.product.diameterInches;
    }

    // Check any component for diameter
    const anyWithDiameter = components.find((c) => c.product?.diameterInches);
    if (anyWithDiameter?.product?.diameterInches) {
      return anyWithDiameter.product.diameterInches;
    }

    return 48; // Default to 48" if no components yet
  }

  /**
   * Check all pipes for collision (holes too close together).
   *
   * Per Clay Prewitt (2026-01-22):
   * - RED indicator: Pipes are too close, holes may overlap
   * - BLUE indicator: Acceptable spacing, no collision
   *
   * @returns Map of pipe ID to collision result
   */
  get pipeCollisionResults(): Map<string, IPipeCollisionResult> {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : ((this.editingProject || this.activeProject)?.manholeStructure?.pipes || []);

    if (pipes.length < 2) {
      // Need at least 2 pipes to have a collision
      const results = new Map<string, IPipeCollisionResult>();
      pipes.forEach((pipe, index) => {
        const pipeId = pipe.id || `pipe-${index}`;
        results.set(pipeId, {
          pipeId,
          status: 'acceptable',
          collidingWithPipeIds: [],
          minimumClearanceInches: Infinity,
          message: 'No collision - single pipe',
        });
      });
      return results;
    }

    const diameter = this.structureDiameterInches;
    return checkAllPipeCollisions(pipes, diameter);
  }

  /**
   * Check if any pipes have collisions.
   */
  get hasPipeCollisions(): boolean {
    const results = this.pipeCollisionResults;
    for (const result of results.values()) {
      if (result.status === 'collision') {
        return true;
      }
    }
    return false;
  }

  /**
   * Get collision status for a specific pipe.
   */
  getPipeCollisionStatus(pipeId: string): IPipeCollisionResult | undefined {
    return this.pipeCollisionResults.get(pipeId);
  }

  /**
   * Calculate the remaining rim-to-invert height to be filled.
   * This is a RUNNING TOTAL that decreases as components are added.
   *
   * Per Clay Prewitt (2026-01-06):
   * - Start with total rim-to-invert distance
   * - Subtract height above outlet invert for each component
   * - Base component only contributes height ABOVE the pipe invert
   * - Other components contribute their full height (minus joint overlap)
   *
   * Clay's formula: Base Rim-to-Invert = Base Height + Boot Factor (~0.08 ft) - Hole Position
   * Note: Annular space affects core drill depth but NOT the rim-to-invert calculation.
   * The 3" joint overlap is for stacking/core drilling, not for rim-to-invert height.
   *
   * SIMPLIFIED per Scott Stavig (January 13, 2026):
   * - rim-to-invert = rim_elevation - min(all_pipe_inverts) (pure elevation difference)
   * - Annular space, hole size, boot size, joint height are IRRELEVANT to rim-to-invert
   * - Those factors only matter for core drill height (manufacturing)
   * - Joints overlap when stacked, so they don't add to stack height
   *
   * Formula: baseContribution = (baseNominal - invertPosition) / 12
   * Example: (36 - 8) / 12 = 2.33 ft
   */
  /**
   * Calculate remaining rim-to-invert height.
   *
   * Per Clay Prewitt (2026-01-22):
   * - Start with total rim-to-invert distance
   * - Subtract BASE contribution (using invertToFirstJoint, NOT full height)
   * - Subtract other components (minus joint overlaps)
   * - Apply sump if structure exceeds rim-to-invert (shallow manholes)
   *
   * This is the CRITICAL fix: use rawHeightFilledByComponents which
   * correctly uses invertToFirstJoint for the base.
   */
  get remainingRimToInvert(): number {
    const totalRimToInvert = this.rimToInvert;
    if (totalRimToInvert <= 0) return 0;

    // Use the corrected filled height calculation that uses invertToFirstJoint for base
    const filledHeight = this.rawHeightFilledByComponents;
    const rawRemaining = totalRimToInvert - filledHeight;

    // If remaining is negative and sump is set, apply sump offset
    if (rawRemaining < 0 && this.currentSumpDepthFeet > 0) {
      return rawRemaining + this.currentSumpDepthFeet;
    }

    return Math.max(0, rawRemaining);
  }

  /**
   * Get the height that has been filled by added components.
   * This is the inverse of remainingRimToInvert.
   */
  get heightFilledByComponents(): number {
    return Math.max(0, this.rimToInvert - this.remainingRimToInvert);
  }

  /**
   * Get the outlet pipe's invert position within the base (inches from bottom).
   * Uses the stored holePositionInches value which represents the physical
   * hole drilling position in the base.
   */
  /**
   * Get the outlet pipe's invert position within the base (inches from bottom).
   * Calculated dynamically from the pipe's invertElevation using rim as datum.
   * Uses the same approach as ManholeCanvas for consistency.
   *
   * NOTE: We calculate stack height inline to avoid circular dependency with rawHeightFilledByComponents.
   */
  /**
   * Get the outlet pipe invert position in inches from the bottom of the base.
   *
   * Per Clay Prewitt (2026-01-22): The pipe invert sits at floor thickness
   * from the base bottom. This is a fixed dimension determined by the
   * manhole diameter, NOT derived from stack height math.
   *
   * 48" manhole = 6" floor → invert at 6"
   * 60" manhole = 8" floor → invert at 8"
   * 72" manhole = 8" floor → invert at 8"
   */
  get outletInvertPositionInches(): number {
    // Use floor thickness from structure data or default based on diameter
    const project = this.editingProject || this.activeProject;
    const structure = project?.manholeStructure;
    const floorThicknessInches = structure?.floorThicknessInches ||
      getDefaultFloorThickness(this.structureDiameterInches);

    return floorThicknessInches;
  }

  /**
   * Get the base component's nominal height in inches.
   */
  get baseNominalHeightInches(): number {
    const components = this.currentStructureId
      ? this.currentComponents
      : this.activeProject?.manholeStructure?.components || [];
    const baseComponent = components.find((c) =>
      (c.product?.type || "").toUpperCase().includes("BASE")
    );
    return baseComponent?.product?.heightInches || 0;
  }

  /**
   * Get the outlet pipe diameter in inches.
   */
  get outletPipeDiameterInches(): number {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : ((this.editingProject || this.activeProject)?.manholeStructure?.pipes || []);
    const outletPipe = pipes.find((p) => p.direction === "outlet");
    return outletPipe?.pipeDiameterInches || 0;
  }

  /**
   * Get the outlet pipe hole size in inches.
   * Uses explicit hole size if set, otherwise calculates default from pipe diameter and connection type.
   */
  get outletHoleSizeInches(): number {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : ((this.editingProject || this.activeProject)?.manholeStructure?.pipes || []);
    const outletPipe = pipes.find((p) => p.direction === "outlet");
    if (!outletPipe) return 0;
    // Use explicit hole size, or calculate default from pipe diameter
    return outletPipe.holeSizeInches ||
      getDefaultHoleSize(outletPipe.pipeDiameterInches, outletPipe.connectionType || 'hole', outletPipe.bootType);
  }

  /**
   * Height of base below the pipe hole (doesn't count toward rim-to-invert).
   * Formula: invertPosition - annularSpace
   * Where annularSpace = (holeSize - pipeSize) / 2
   */
  get baseBelowHoleInches(): number {
    const invertPosition = this.outletInvertPositionInches;
    const annularSpace = (this.outletHoleSizeInches - this.outletPipeDiameterInches) / 2;
    return Math.max(0, invertPosition - annularSpace);
  }

  /**
   * Height of base above the pipe hole (contributes to rim-to-invert).
   * Formula: baseHeight - invertPosition - pipeDiameter - annularSpace
   */
  get baseAboveHoleInches(): number {
    const baseHeight = this.baseNominalHeightInches;
    if (baseHeight === 0) return 0;
    const invertPosition = this.outletInvertPositionInches;
    const pipeDiameter = this.outletPipeDiameterInches;
    const annularSpace = (this.outletHoleSizeInches - pipeDiameter) / 2;
    const holeTop = invertPosition + pipeDiameter + annularSpace;
    return Math.max(0, baseHeight - holeTop);
  }

  /**
   * Determine which component the outlet pipe is physically located in.
   * This is based on whether the hole position fits within the base height,
   * AND whether the structure is tall enough to reach from rim to invert.
   *
   * - 'base': Pipe hole is within the base section (good!)
   * - 'riser': Pipe hole position exceeds base height (structure too tall/base too high)
   * - 'below-base': No base component exists
   * - 'below-structure': Structure is too short - pipe would be below the entire structure
   */
  get outletPipeComponentLocation(): "base" | "riser" | "below-base" | "below-structure" {
    const baseHeight = this.baseNominalHeightInches;
    const holePosition = this.outletInvertPositionInches;

    if (baseHeight === 0) {
      // No base component - pipe is below the structure
      return "below-base";
    }

    // Check if structure is tall enough overall
    // Use rawHeightFilledByComponents (correct formula accounting for base/annular space)
    // instead of totalOutsideHeight (naive sum) — fixes mismatch with remainingRimToInvert
    const rimToInvertDist = this.rimToInvert;
    const filledHeight = this.rawHeightFilledByComponents;
    if (rimToInvertDist > 0 && filledHeight > 0 && rimToInvertDist > filledHeight) {
      return "below-structure";
    }

    if (holePosition <= baseHeight) {
      // Pipe hole fits within the base section
      return "base";
    }

    // Pipe hole position exceeds base height - would need to be in a riser
    return "riser";
  }

  /**
   * Get the correct core drill height for a pipe, derived from the component stack.
   * Returns the manual override if set, otherwise derives from derivePipeLocation.
   *
   * Per Clay Prewitt (2026-01-31): the auto-calc formula was wrong; the correct
   * values come from the component-stack-aware algorithm (derivePipeLocation).
   */
  getCoreDrillHeight = (pipeId: string): number => {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (this.editingProject || this.activeProject)?.manholeStructure?.pipes || [];
    const pipe = pipes.find((p) => p.id === pipeId);
    if (!pipe) return 6;

    // Manual override takes precedence
    if (pipe.manualCoreDrillOverride && pipe.coreDrillHeightInches != null) {
      return pipe.coreDrillHeightInches;
    }

    // Derive from component stack
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure?.components as IStackComponent[]) || [];

    if (components.length === 0) {
      // No components — fall back to structure code default
      const structureCode = this.selectedStructureCode;
      return structureCode?.defaultCoreDrillHeightInches ?? 6;
    }

    const rimElevation = (this.editingProject || this.activeProject)?.manholeStructure?.rimElevation || 0;
    const lowestInvert = this.lowestInvert || 0;

    const derived = derivePipeLocation(pipe, components, rimElevation, lowestInvert);

    // Core drill height = hole position (distance from reference point to pipe invert)
    // For bases: measured from component bottom (= floor thickness, e.g., 6")
    // For risers: measured from shoulder
    // No annular space addition — the floor thickness already accounts for pipe + annular clearance
    return Math.max(derived.holePositionInches, 3);
  };

  /**
   * Get the raw (unclamped) height filled by components above the invert.
   * This can exceed rimToInvert if the structure is too tall.
   * Used for accurate "too tall" detection in balancingSuggestions.
   *
   * Per Clay Prewitt / NTX PDF:
   * - Base contributes: invertToFirstJoint (already accounts for floor + annular below pipe)
   *   Formula: (nominalHeight + jointBell - floorThickness - annularSpace) / 12
   *   Example 48" x 3': (36 + 3 - 6 - 2) / 12 = 2.583 ft
   * - Other components contribute their FULL nominal height (NO joint overlap deduction)
   *   Example: 3' riser = 3.0 ft, 6" adj ring = 0.5 ft
   *
   * Clay's validated stack (rim-to-invert 9.63 ft):
   *   Base(2.583) + 3' riser(3.0) + 6" adj(0.5) + 3' cone(3.0) + casting(0.5) = 9.583
   *   Remaining = 0.047 ≈ 0.05 ft — matches Titan exactly
   */
  get rawHeightFilledByComponents(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return 0;

    const components = this.currentStructureId
      ? this.currentComponents
      : project.manholeStructure?.components || [];

    if (components.length === 0) return 0;

    // Calculate filled height from components
    let filledHeightFeet = 0;
    const sortedComponents = [...components].sort(
      (a, b) => (a.positionInStack || 0) - (b.positionInStack || 0)
    );

    sortedComponents.forEach((comp, index) => {
      const compHeightInches = getEffectiveHeightInches(comp);
      const compType = (comp.product?.type || "").toUpperCase();

      if (index === 0 && (compType === "BASE" || compType.includes("BASE"))) {
        // Base component: invertToFirstJoint already accounts for floor + annular below pipe
        // Formula: (nominalHeight + jointBell - floorThickness - annularSpace) / 12
        const invertToFirstJoint = getBaseInvertToFirstJointFromProduct(comp.product, this.outletAnnularSpaceInches);
        if (invertToFirstJoint > 0) {
          const modifierFeet = (comp.heightModifierInches || 0) / 12;
          filledHeightFeet += invertToFirstJoint + modifierFeet;
        } else {
          // Fallback: use old calculation if invertToFirstJoint not available
          const invertPositionInches = this.outletInvertPositionInches;
          filledHeightFeet += calculateBaseContribution(
            compHeightInches,
            invertPositionInches
          );
        }
      } else {
        // Per Clay (2026-01-22): components contribute full nominal height, no joint overlap
        filledHeightFeet += calculateComponentContribution(compHeightInches, index === 0);
      }
    });

    return filledHeightFeet;
  }

  /**
   * Check if structure has a base component already.
   */
  get hasBase(): boolean {
    const components = this.currentStructureId
      ? this.currentComponents
      : (this.editingProject || this.activeProject)?.manholeStructure
          ?.components || [];
    return components.some((c) => {
      const type = (c.product?.type || "").toUpperCase();
      return type === "BASE" || type.includes("BASE");
    });
  }

  /**
   * Check if structure has a cone/top component already.
   */
  get hasConeOrTop(): boolean {
    const components = this.currentStructureId
      ? this.currentComponents
      : (this.editingProject || this.activeProject)?.manholeStructure
          ?.components || [];
    return components.some((c) => {
      const type = (c.product?.type || "").toUpperCase();
      return (
        type === "CONE" ||
        type === "FLAT_TOP" ||
        type.includes("CONE") ||
        type.includes("TOP")
      );
    });
  }

  /**
   * Check if a component type can be added (prevents duplicate base/cone).
   */
  canAddComponentType(type: string): boolean {
    const upperType = (type || "").toUpperCase();
    if ((upperType === "BASE" || upperType.includes("BASE")) && this.hasBase) {
      return false;
    }
    if (
      (upperType === "CONE" ||
        upperType === "FLAT_TOP" ||
        upperType.includes("CONE") ||
        upperType.includes("TOP")) &&
      this.hasConeOrTop
    ) {
      return false;
    }
    return true;
  }

  get stackComponents(): IStackComponent[] {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return [];

    const components: IStackComponent[] = [];
    const requiredHeight = this.requiredPrecastHeight * 12; // Convert to inches

    if (requiredHeight <= 0) return components;

    // 1. Select base
    const base = this.bases.find((b) => b.diameterInches === 48); // Default to 48" diameter
    if (!base) return components;

    components.push({
      instanceId: `stack-${Date.now()}-1`,
      product: base,
      positionInStack: 0,
      bottomElevation: this.lowestInvert,
    });

    let remainingHeight = requiredHeight - base.heightInches;
    let currentElevation = this.lowestInvert + base.heightInches / 12;
    let position = 1;

    // 2. Fill with risers (greedy algorithm - largest first)
    // Filter out risers with invalid heights to prevent infinite loops
    const sortedRisers = [...this.risers].filter(
      (r) => r.heightInches && r.heightInches > 0
    );

    while (remainingHeight > 24 && sortedRisers.length > 0) {
      // Leave room for top slab
      const riser = sortedRisers.find(
        (r) => r.heightInches <= remainingHeight - 24
      );

      if (!riser) break;

      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: riser,
        positionInStack: position,
        bottomElevation: currentElevation,
      });

      remainingHeight -= riser.heightInches;
      currentElevation += riser.heightInches / 12;
      position++;

      // Safety limit to prevent infinite loops
      if (components.length > 50) break;
    }

    // 3. Add top slab or cone
    const topSlab = this.slabs.find((s) => s.diameterInches === 48);
    if (topSlab) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: topSlab,
        positionInStack: position,
        bottomElevation: currentElevation,
      });
      position++;
      currentElevation += topSlab.heightInches / 12;
    }

    // 4. Add frame and cover
    const frame = this.frames[0];
    const cover = this.covers[0];

    if (frame) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: frame,
        positionInStack: position,
        bottomElevation: currentElevation,
      });
      position++;
    }

    if (cover) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: cover,
        positionInStack: position,
        bottomElevation: currentElevation,
      });
    }

    return components;
  }

  get billOfMaterials(): IBillOfMaterialsEntry[] {
    // Use structure-keyed components when available for multi-structure navigation
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];
    const project = this.editingProject || this.activeProject;
    const entries: IBillOfMaterialsEntry[] = [];

    // 1. COMPONENTS - Aggregate manhole structure components by unique product
    const componentMap = new Map<
      string,
      {
        product: ICatalogProduct;
        quantity: number;
        weight: number;
        cost: number;
      }
    >();

    components.forEach((comp) => {
      if (!comp?.product) return; // Skip invalid components

      // Use robust key that handles missing productId - fall back to componentCode or composite key
      const key =
        comp.product.productId ||
        comp.product.componentCode ||
        `${comp.product.type || "UNKNOWN"}-${comp.product.heightInches || 0}-${
          comp.product.diameterInches || 0
        }`;

      // Get weight and cost with fallbacks for different field names
      const weight = comp.product.weightLbs ?? comp.product.weight ?? 0;
      const cost =
        comp.product.estimatedCost ??
        comp.product.costUSD ??
        comp.product.basePrice ??
        0;

      if (componentMap.has(key)) {
        const existing = componentMap.get(key)!;
        existing.quantity++;
        existing.weight += weight;
        existing.cost += cost;
      } else {
        componentMap.set(key, {
          product: comp.product,
          quantity: 1,
          weight: weight,
          cost: cost,
        });
      }
    });

    // Convert components to BOM entries
    componentMap.forEach(({ product, quantity, weight, cost }) => {
      // Get component type label - prioritize type over UUIDs for display
      const typeLabel = product.type
        ? product.type.toUpperCase() === "FLAT_TOP"
          ? "TOP"
          : product.type.toUpperCase() === "ADJUST_RING"
          ? "RING"
          : product.type.toUpperCase()
        : "COMPONENT";
      entries.push({
        item: typeLabel,
        description:
          product.description ||
          product.name ||
          `${product.diameterInches}" × ${(product.heightInches / 12).toFixed(
            0
          )}' ${product.type || "Component"}`,
        quantity,
        weight: weight,
        unitCost: cost / quantity,
        totalCost: cost,
      });
    });

    // 2. PIPES - Add each pipe with its details and connection accessories
    // Use structure-keyed pipes when available for multi-structure navigation
    const pipes = this.currentStructureId
      ? this.currentPipes
      : project?.manholeStructure?.pipes || [];

    if (pipes.length) {
      // Process each pipe individually (no aggregation) to show actual pipe details
      pipes.forEach((pipe) => {
        const connectionType = pipe.connectionType || "hole";
        const direction = pipe.direction || "inlet";
        const directionLabel = direction === "outlet" ? "Outlet" : "Inlet";

        // Add pipe info entry (for reference - pipes supplied by others)
        entries.push({
          item: `PIPE-${pipe.pipeNumber}`,
          description: `${pipe.pipeDiameterInches}" ${
            pipe.pipeMaterial || "PVC"
          } ${directionLabel} @ ${pipe.angleDegrees}°`,
          quantity: 1,
          weight: 0, // Pipes supplied by others
          unitCost: 0,
          totalCost: 0,
        });

        // Add connection method based on type (boots, adapters, collars)
        // Note: Holes are not tracked in BOM - only the actual pipe connections
        if (connectionType === "boot") {
          const bootLabel = pipe.bootType
            ? pipe.bootType === "press-seal"
              ? "Press Seal"
              : pipe.bootType === "corn-seal"
              ? "Corn Seal (Trelle Borg)"
              : pipe.bootType === "kor-n-seal"
              ? "Kor-N-Seal"
              : "Press Seal"
            : "Press Seal";
          entries.push({
            item: `BOOT-${pipe.pipeDiameterInches}`,
            description: `${pipe.pipeDiameterInches}" ${bootLabel} Boot`,
            quantity: 1,
            weight: pipe.pipeDiameterInches <= 12 ? 5 : 10,
            unitCost: pipe.pipeDiameterInches <= 12 ? 45 : 85,
            totalCost: pipe.pipeDiameterInches <= 12 ? 45 : 85,
          });
        } else if (connectionType === "adapter") {
          entries.push({
            item: `ADAPTER-${pipe.pipeDiameterInches}`,
            description: `${pipe.pipeDiameterInches}" A-Lock Adapter`,
            quantity: 1,
            weight: pipe.pipeDiameterInches <= 12 ? 8 : 15,
            unitCost: pipe.pipeDiameterInches <= 12 ? 125 : 200,
            totalCost: pipe.pipeDiameterInches <= 12 ? 125 : 200,
          });
        } else if (connectionType === "collar") {
          entries.push({
            item: `COLLAR-${pipe.pipeDiameterInches}`,
            description: `${pipe.pipeDiameterInches}" Sand Collar`,
            quantity: 1,
            weight: pipe.pipeDiameterInches <= 12 ? 3 : 6,
            unitCost: pipe.pipeDiameterInches <= 12 ? 25 : 45,
            totalCost: pipe.pipeDiameterInches <= 12 ? 25 : 45,
          });
        }
        // If connectionType is 'hole', no additional item needed beyond the hole itself
      });
    }

    // 3. INTERNALS - Add steps, ladders, and other internal components
    // Use structure-keyed internals when available for multi-structure navigation
    const internals = this.currentStructureId
      ? this.currentInternals
      : project?.manholeStructure?.internals;

    if (internals) {
      const structureHeight = this.rimToInvert || 10; // Use calculated height

      // Add steps (Lane International)
      if (internals.hasSteps) {
        const stepCount = Math.max(1, Math.floor((structureHeight - 2) / 1.25)); // Steps every 1.25 feet, starting 2ft from bottom
        entries.push({
          item: "STEPS-LANE",
          description: `Lane International Steps at ${
            internals.stepAngleDegrees || 0
          }°`,
          quantity: stepCount,
          weight: 15 * stepCount, // 15 lbs per step
          unitCost: 52,
          totalCost: 52 * stepCount,
        });
      }

      // Add ladder if specified - now with type support
      if (internals.hasLadder) {
        const ladderLength = Math.ceil(structureHeight - 2); // Ladder starts 2ft from bottom
        const ladderType = internals.ladderType || "steel";

        if (ladderType === "steel") {
          entries.push({
            item: "LADDER-STEEL",
            description: `Steel Safety Ladder (${ladderLength}' length)`,
            quantity: 1,
            weight: ladderLength * 12, // 12 lbs per foot
            unitCost: ladderLength * 95,
            totalCost: ladderLength * 95,
          });
        } else if (ladderType === "polypropylene") {
          entries.push({
            item: "LADDER-POLY",
            description: `Polypropylene Safety Ladder (${ladderLength}' length)`,
            quantity: 1,
            weight: ladderLength * 6, // 6 lbs per foot
            unitCost: ladderLength * 145,
            totalCost: ladderLength * 145,
          });
        } else if (ladderType === "polypropylene-hanging") {
          entries.push({
            item: "LADDER-POLY-HANG",
            description: `Polypropylene Hanging Ladder (${ladderLength}' length)`,
            quantity: 1,
            weight: ladderLength * 5, // 5 lbs per foot
            unitCost: ladderLength * 165,
            totalCost: ladderLength * 165,
          });
        }
      }

      // Add bench if specified
      if (internals.hasBench) {
        entries.push({
          item: "BENCH-CONC",
          description: "Concrete Flow Channel Bench",
          quantity: 1,
          weight: 450,
          unitCost: 850,
          totalCost: 850,
        });
      }

      // Add drop bowl if specified
      if (internals.hasDropBowl) {
        entries.push({
          item: "DROP-BOWL",
          description: `Drop Bowl Connection (${
            internals.dropBowlSizeInches || 6
          }" inlet)`,
          quantity: 1,
          weight: 125,
          unitCost: 650,
          totalCost: 650,
        });
      }

      // Phase 5: Pre-Channel Invert
      if (internals.hasPreChannelInvert) {
        entries.push({
          item: "INVERT-PRECHAN",
          description: "Pre-Channel Invert",
          quantity: 1,
          weight: 200,
          unitCost: 450,
          totalCost: 450,
        });
      }

      // Phase 5: GU Liner
      if (internals.hasGULiner) {
        entries.push({
          item: "LINER-GU",
          description: "GU Liner (Corrosion Protection)",
          quantity: 1,
          weight: 50,
          unitCost: 375,
          totalCost: 375,
        });
      }

      // Phase 5: Custom Internal
      if (internals.hasCustomInternal && internals.customInternalDescription) {
        entries.push({
          item: "CUSTOM-INT",
          description: `Custom: ${internals.customInternalDescription.substring(
            0,
            50
          )}`,
          quantity: 1,
          weight: 0, // Custom weight TBD
          unitCost: 0, // Custom cost TBD
          totalCost: 0,
        });
      }
    }

    // 4. ACCESSORIES & SEALANTS
    // Add joint sealant for component connections based on sealantConfig
    if (components.length > 1 && this.sealantConfig.type !== "none") {
      // Count joints, excluding cone from count (cones don't get sealant on their tapered top)
      const nonConeComponents = components.filter(
        (comp) =>
          !comp.product.type?.toLowerCase().includes("cone") &&
          !comp.product.description?.toLowerCase().includes("cone")
      );
      // Joint count is number of component-to-component connections (excludes the top of cones)
      const jointCount = Math.max(0, components.length - 1);

      if (this.sealantConfig.type === "mastic") {
        // Mastic: 1 roll per joint (Ram-Nek style)
        entries.push({
          item: "SEAL-MASTIC",
          description: "Ram-Nek Joint Sealant Mastic (Roll)",
          quantity: jointCount,
          weight: 2.5 * jointCount, // ~2.5 lbs per roll
          unitCost: 28,
          totalCost: 28 * jointCount,
        });
      } else if (this.sealantConfig.type === "gasket") {
        // Gasket: Use configured size, one per joint
        const gasketSize = this.sealantConfig.gasketSize || 48;

        // Group joints by component size for proper gasket sizing
        const gasketCounts = new Map<number, number>();
        components.forEach((comp, index) => {
          if (index < components.length - 1) {
            // Use the component's diameter for gasket sizing, default to configured size
            const compDiameter = comp.product.diameterInches || gasketSize;
            gasketCounts.set(
              compDiameter,
              (gasketCounts.get(compDiameter) || 0) + 1
            );
          }
        });

        // Add gasket entries for each size needed
        gasketCounts.forEach((count, diameter) => {
          // Weight varies by gasket size (approximate)
          const weightPerGasket = diameter <= 24 ? 3 : diameter <= 48 ? 6 : 10;
          // Cost varies by size
          const costPerGasket = diameter <= 24 ? 45 : diameter <= 48 ? 75 : 110;

          entries.push({
            item: `GASKET-${diameter}`,
            description: `${diameter}" Rubber Gasket`,
            quantity: count,
            weight: weightPerGasket * count,
            unitCost: costPerGasket,
            totalCost: costPerGasket * count,
          });
        });
      }
    }

    // Add grade rings if rim adjustment is needed
    const rimAdjustment =
      (project?.manholeStructure as any)?.rimAdjustmentInches || 0;
    if (rimAdjustment > 0) {
      const ringCount = Math.ceil(rimAdjustment / 2); // 2" grade rings
      entries.push({
        item: "RING-GRADE-2",
        description: '2" Grade Adjustment Ring',
        quantity: ringCount,
        weight: 85 * ringCount,
        unitCost: 75,
        totalCost: 75 * ringCount,
      });
    }

    return entries;
  }

  // Calculate total height of actual user-added components
  get actualTotalHeight(): number {
    // NEW: Use structure-keyed components when available
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];

    if (!components.length) return 0;
    let totalHeight = 0;

    // Debug logging (enable by setting window.debugCalculations = true in console)
    const debug = (window as any).debugCalculations;

    if (debug) {
      console.log(
        "Calculating Total Height for",
        components.length,
        "components:"
      );
    }

    components.forEach((comp, index) => {
      const heightInches = getEffectiveHeightInches(comp);
      const heightFeet = heightInches / 12;
      totalHeight += heightFeet;

      if (debug) {
        console.log(
          `  Component ${index + 1}: ${
            comp.product.description
          } - ${heightInches}" = ${heightFeet.toFixed(2)} ft`
        );
      }
    });

    if (debug) {
      console.log("Total Height:", totalHeight.toFixed(2), "ft");
    }
    return totalHeight;
  }

  // ============================================
  // Phase 2: Invert-to-Rim Calculation Properties
  // ============================================

  /**
   * Total outside height for dig depth calculation
   * Joint overlaps are NOT added - components physically overlap at joints
   * (3" joint overlap is only used for core drill calculations)
   */
  get totalOutsideHeight(): number {
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];

    if (!components.length) return 0;

    let totalInches = 0;

    components.forEach((comp) => {
      totalInches += getEffectiveHeightInches(comp);
    });

    return totalInches / 12;
  }

  /**
   * Get elevations where component joints occur (for joint proximity warnings)
   * Joints occur at the top of each component (where they connect to the next)
   * Returns array of elevations in feet, measured from structure bottom (0)
   *
   * Example: 24" base (2ft) → first joint at 2ft
   *          + 36" riser (3ft) → second joint at 5ft
   */
  get jointElevations(): number[] {
    const project = this.editingProject || this.activeProject;
    const components = this.currentStructureId
      ? this.currentComponents
      : ((project)?.manholeStructure
          ?.components as IStackComponent[]) || [];
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (project?.manholeStructure?.pipes as IPipeConnection[]) || [];

    if (components.length < 2 || !pipes.length) return []; // No joints if 0 or 1 component

    const joints: number[] = [];

    // Sort components by position in stack (bottom to top)
    const sortedComponents = [...components].sort(
      (a, b) => (a.positionInStack || 0) - (b.positionInStack || 0)
    );

    // Use absolute elevations anchored to lowest pipe invert (matching derivePipeLocation.ts)
    const lowestInvert = Math.min(...pipes.map((p) => p.invertElevation));
    const DEFAULT_BASE_BELOW_INVERT_FT = 0.5;
    let currentElevation = lowestInvert - DEFAULT_BASE_BELOW_INVERT_FT;

    sortedComponents.forEach((comp, index) => {
      const componentHeightFt = getEffectiveHeightInches(comp) / 12;
      const jointFt = index > 0 ? JOINT_OVERLAP_INCHES / 12 : 0;

      // Advance to top of this component
      currentElevation += componentHeightFt + jointFt;

      // Joint at top of this component (except last component - that's the rim)
      if (index < sortedComponents.length - 1) {
        joints.push(currentElevation);
      }
    });

    return joints;
  }

  /**
   * Get components with their elevation bounds and contained pipes for per-component top view.
   * Used by the coring crew to see which holes are in which component.
   */
  get componentsWithPipes(): Array<{
    index: number;
    instanceId: string;
    name: string;
    bottomFt: number;
    topFt: number;
    pipeCount: number;
    pipeLabels: string[];
  }> {
    const project = this.editingProject || this.activeProject;
    const components = this.currentStructureId
      ? this.currentComponents
      : (project?.manholeStructure?.components as IStackComponent[]) || [];
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (project?.manholeStructure?.pipes as IPipeConnection[]) || [];

    if (!components.length) return [];

    // Sort components by position in stack (bottom to top)
    const sortedComponents = [...components].sort(
      (a, b) => (a.positionInStack || 0) - (b.positionInStack || 0)
    );

    // Build component bounds with pipe counts using absolute elevations
    const lowestInvert = pipes.length
      ? Math.min(...pipes.map((p) => p.invertElevation))
      : 0;
    const DEFAULT_BASE_BELOW_INVERT_FT = 0.5;
    let currentBottom = pipes.length ? lowestInvert - DEFAULT_BASE_BELOW_INVERT_FT : 0;
    const result: Array<{
      index: number;
      instanceId: string;
      name: string;
      bottomFt: number;
      topFt: number;
      pipeCount: number;
      pipeLabels: string[];
    }> = [];

    // Track outlet/inlet counters for labeling
    let outletCount = 0;
    let inletCount = 0;
    const sortedPipes = [...pipes].sort(
      (a, b) => (a.pipeNumber || 0) - (b.pipeNumber || 0)
    );
    const pipeLabelMap = new Map<string, string>();
    sortedPipes.forEach((pipe) => {
      const isOutlet = pipe.direction === "outlet";
      const label = isOutlet ? `o${++outletCount}` : `i${++inletCount}`;
      pipeLabelMap.set(pipe.id, label);
    });

    sortedComponents.forEach((comp, index) => {
      const heightFt = getEffectiveHeightInches(comp) / 12;
      const jointFt = index > 0 ? JOINT_OVERLAP_INCHES / 12 : 0;
      const bottomFt = currentBottom;
      const topFt = currentBottom + heightFt + jointFt;
      currentBottom = topFt;

      // Find pipes whose invert falls within this component's elevation range
      const containedPipes = pipes.filter((pipe) => {
        return pipe.invertElevation >= bottomFt && pipe.invertElevation < topFt;
      });

      const pipeLabels = containedPipes.map(
        (p) => pipeLabelMap.get(p.id) || ""
      );

      // Generate component name (use type assertion since componentType may exist on some products)
      const componentType =
        (comp.product as any).componentType || comp.product.type || "Unknown";
      const heightIn = getEffectiveHeightInches(comp);
      let name = `${componentType}`;
      if (heightIn > 0) {
        name = `${componentType} (${heightIn}")`;
      }

      result.push({
        index,
        instanceId: comp.instanceId || `comp-${index}`,
        name,
        bottomFt,
        topFt,
        pipeCount: containedPipes.length,
        pipeLabels,
      });
    });

    return result;
  }

  /**
   * Get pipes filtered by the selected top view component.
   * Returns all pipes if no component is selected (null).
   */
  get filteredPipesForTopView(): IPipeConnection[] {
    const project = this.editingProject || this.activeProject;
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (project?.manholeStructure?.pipes as IPipeConnection[]) || [];

    if (this.selectedTopViewComponentIndex === null) {
      return pipes; // Show all pipes
    }

    const componentsWithPipes = this.componentsWithPipes;
    const selectedComponent =
      componentsWithPipes[this.selectedTopViewComponentIndex];
    if (!selectedComponent) {
      return pipes; // Fallback to all pipes
    }

    // Filter pipes whose invert falls within the selected component's elevation range
    return pipes.filter((pipe) => {
      return (
        pipe.invertElevation >= selectedComponent.bottomFt &&
        pipe.invertElevation < selectedComponent.topFt
      );
    });
  }

  /**
   * Get pipe conflicts for each component in the stack.
   * Returns an object of componentInstanceId → conflicting pipe labels (e.g., ["o1", "i2"])
   * A conflict occurs when a pipe hole edge is within 2 inches of a component joint.
   */
  get componentPipeConflicts(): Record<string, string[]> {
    const THRESHOLD_INCHES = 2;
    const conflicts: Record<string, string[]> = {};

    // Use same fallback logic as ComponentCatalog and PipeEditor
    const project = this.editingProject || this.activeProject;
    const components = this.currentStructureId
      ? this.currentComponents
      : (project?.manholeStructure?.components as IStackComponent[]) || [];
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (project?.manholeStructure?.pipes as IPipeConnection[]) || [];

    if (!components.length || !pipes.length) return conflicts;

    // Sort components by position in stack (bottom to top)
    const sortedComponents = [...components].sort(
      (a, b) => (a.positionInStack || 0) - (b.positionInStack || 0)
    );

    // Build component bounds using absolute elevations (matching derivePipeLocation.ts)
    const lowestInvert = Math.min(...pipes.map((p) => p.invertElevation));
    const DEFAULT_BASE_BELOW_INVERT_FT = 0.5;
    let currentBottom = lowestInvert - DEFAULT_BASE_BELOW_INVERT_FT;
    const componentBounds = sortedComponents.map((comp, index) => {
      const heightFt = getEffectiveHeightInches(comp) / 12;
      const jointFt = index > 0 ? JOINT_OVERLAP_INCHES / 12 : 0;
      const bottomFt = currentBottom;
      const topFt = currentBottom + heightFt + jointFt;
      currentBottom = topFt;
      return { instanceId: comp.instanceId, bottomFt, topFt };
    });

    // Generate pipe labels (o1, o2, i1, i2)
    const sortedPipes = [...pipes].sort(
      (a, b) => (a.pipeNumber || 0) - (b.pipeNumber || 0)
    );
    let outletCount = 0;
    let inletCount = 0;
    const pipeLabels = sortedPipes.map((p) => {
      const isOutlet = p.direction === "outlet" || p.angleDegrees === 0;
      return isOutlet ? `o${++outletCount}` : `i${++inletCount}`;
    });

    // Check each pipe against each component using same logic as PipeEditor checkJointProximity
    sortedPipes.forEach((pipe, pipeIdx) => {
      const centerFt = pipe.invertElevation;
      const holeRadiusFt =
        (pipe.holeSizeInches || pipe.pipeDiameterInches + 4) / 2 / 12;
      const thresholdFt = THRESHOLD_INCHES / 12;

      componentBounds.forEach((comp, compIdx) => {
        let hasConflict = false;

        // Check conflict with component's TOP joint (except for topmost component)
        if (compIdx < componentBounds.length - 1) {
          const centerToTopJoint = Math.abs(centerFt - comp.topFt);
          const edgeToTopJoint = centerToTopJoint - holeRadiusFt;
          if (edgeToTopJoint < thresholdFt) {
            hasConflict = true;
          }
        }

        // Check conflict with component's BOTTOM joint (except for bottom component)
        if (compIdx > 0) {
          const centerToBottomJoint = Math.abs(centerFt - comp.bottomFt);
          const edgeToBottomJoint = centerToBottomJoint - holeRadiusFt;
          if (edgeToBottomJoint < thresholdFt) {
            hasConflict = true;
          }
        }

        if (hasConflict) {
          if (!conflicts[comp.instanceId]) {
            conflicts[comp.instanceId] = [];
          }
          if (!conflicts[comp.instanceId].includes(pipeLabels[pipeIdx])) {
            conflicts[comp.instanceId].push(pipeLabels[pipeIdx]);
          }
        }
      });
    });

    return conflicts;
  }

  /**
   * Calculate invert-to-rim (usable height) for a specific pipe
   * Uses Clay's formula from Dec 2, 2025 meeting:
   *   Usable Height = Actual Height - (Hole Position + Annular Space)
   *
   * Core drilling measurement conventions:
   * - Bases: "6 inches up" = from very bottom of base
   * - Risers: "6 inches up" = from inside top of bell (add 3" for joint)
   */
  calculatePipeInvertToRim(pipe: IPipeConnection): number {
    const JOINT_OVERLAP_INCHES = 3;
    const ANNULAR_SPACE_BY_BOOT: Record<string, number> = {
      "press-seal": 2,
      "corn-seal": 1.5,
      "kor-n-seal": 2,
      none: 0,
    };

    const holePositionInches = pipe.holePositionInches || 6;
    const componentLocation = pipe.componentLocation || "base";
    const bootType =
      pipe.bootType || (pipe.connectionType === "boot" ? "press-seal" : "none");
    const annularSpaceInches = ANNULAR_SPACE_BY_BOOT[bootType] || 0;

    // Hole position is measured from shoulder for risers (no joint overlap adjustment needed)
    const adjustedHolePosition = holePositionInches;

    // Get component height at this location
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];

    // Find the component that matches the pipe's location
    let componentHeight = 0;
    const componentType = componentLocation.toUpperCase();
    const matchingComponent = components.find(
      (c) =>
        c.product.type?.toUpperCase() === componentType ||
        (componentType === "BASE" &&
          c.product.type?.toUpperCase()?.includes("BASE")) ||
        (componentType === "RISER" &&
          c.product.type?.toUpperCase()?.includes("RISER")) ||
        (componentType === "CONE" &&
          c.product.type?.toUpperCase()?.includes("CONE"))
    );

    if (matchingComponent) {
      componentHeight = getEffectiveHeightInches(matchingComponent) / 12; // Convert to feet
    } else if (components.length > 0) {
      // Use first component (base) as fallback
      componentHeight = getEffectiveHeightInches(components[0]) / 12;
    }

    // Convert offset to feet
    const offsetFeet = (adjustedHolePosition + annularSpaceInches) / 12;

    // Calculate usable height from this pipe's invert to rim
    return Math.max(0, componentHeight - offsetFeet);
  }

  /**
   * Per-pipe invert-to-rim calculations with details
   * Returns array of calculations for each pipe connection
   */
  get pipeInvertToRimCalculations(): Array<{
    pipeId: string;
    pipeNumber: number;
    pipeDiameter: number;
    invertElevation: number;
    holePositionInches: number;
    componentLocation: string;
    bootType: string;
    holeSizeInches: number;
    invertToRimFeet: number;
  }> {
    const pipes = this.currentStructureId
      ? this.currentPipes
      : (this.editingProject || this.activeProject)?.manholeStructure?.pipes ||
        [];

    return pipes.map((pipe) => ({
      pipeId: pipe.id || `pipe-${pipe.pipeNumber}`,
      pipeNumber: pipe.pipeNumber,
      pipeDiameter: pipe.pipeDiameterInches,
      invertElevation: pipe.invertElevation,
      holePositionInches: pipe.holePositionInches || 6,
      componentLocation: pipe.componentLocation || "base",
      bootType:
        pipe.bootType ||
        (pipe.connectionType === "boot" ? "press-seal" : "none"),
      holeSizeInches: pipe.holeSizeInches || 0,
      invertToRimFeet: this.calculatePipeInvertToRim(pipe),
    }));
  }

  /**
   * Overall invert-to-rim height (from lowest pipe invert to top of structure)
   * This is the critical dimension for engineering approval.
   *
   * This is the same as heightFilledByComponents - the usable stack height
   * above the lowest pipe invert. Per Clay's rim-to-invert specification,
   * this represents how much of the rim-to-invert distance is filled by components.
   */
  get invertToRimUsableHeight(): number {
    // The usable Invert-to-Rim is exactly what heightFilledByComponents calculates:
    // the sum of all component contributions above the lowest pipe invert
    return this.heightFilledByComponents;
  }

  /**
   * Height display data for UI - both total and usable heights
   */
  get heightDisplayData(): {
    totalOutsideHeight: number;
    actualStackHeight: number;
    invertToRimUsable: number;
    rimElevation: number;
    lowestInvert: number;
    jointCount: number;
  } {
    const project = this.editingProject || this.activeProject;
    const components = this.currentStructureId
      ? this.currentComponents
      : (project?.manholeStructure?.components as IStackComponent[]) || [];

    return {
      totalOutsideHeight: this.totalOutsideHeight,
      actualStackHeight: this.actualTotalHeight,
      invertToRimUsable: this.invertToRimUsableHeight,
      rimElevation:
        project?.manholeStructure?.rimElevation || project?.rimElevation || 0,
      lowestInvert: this.lowestInvert,
      jointCount: Math.max(0, components.length - 1),
    };
  }

  // Calculate total weight of actual user-added components
  get totalWeight(): number {
    // NEW: Use structure-keyed components when available
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];

    if (!components.length) return 0;
    let totalWeight = 0;

    // Debug logging (enable by setting window.debugCalculations = true in console)
    const debug = (window as any).debugCalculations;

    if (debug) {
      console.log(
        "Calculating Total Weight for",
        components.length,
        "components:"
      );
    }

    components.forEach((comp, index) => {
      // Check for both weightLbs and weight properties
      const weight = comp.product.weightLbs || comp.product.weight || 0;
      totalWeight += weight;

      if (debug) {
        console.log(
          `  Component ${index + 1}: ${
            comp.product.description
          } - ${weight} lbs`
        );
      }
    });

    if (debug) {
      console.log("Total Weight:", totalWeight, "lbs");
    }
    return totalWeight;
  }

  // Calculate total cost of actual user-added components
  get totalCost(): number {
    // NEW: Use structure-keyed components when available
    const components = this.currentStructureId
      ? this.currentComponents
      : ((this.editingProject || this.activeProject)?.manholeStructure
          ?.components as IStackComponent[]) || [];

    if (!components.length) return 0;
    let totalCost = 0;

    // Debug logging (enable by setting window.debugCalculations = true in console)
    const debug = (window as any).debugCalculations;

    if (debug) {
      console.log(
        "Calculating Total Cost for",
        components.length,
        "components:"
      );
    }

    components.forEach((comp, index) => {
      // Check for both costUSD and basePrice properties
      const cost = comp.product.costUSD || comp.product.basePrice || 0;
      totalCost += cost;

      if (debug) {
        console.log(
          `  Component ${index + 1}: ${comp.product.description} - $${cost}`
        );
      }
    });

    if (debug) {
      console.log("Total Cost: $", totalCost.toFixed(2));
    }
    return totalCost;
  }

  // NEW: Color-coded validation for pipe clearances
  get pipeValidations(): IPipeValidation[] {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure?.pipes) return [];

    const MIN_CLEARANCE_INCHES = 2; // Minimum clearance from joint (updated from 6" per requirements)

    return project.manholeStructure.pipes.map((pipe) => {
      const clearance = this.calculateJointClearance(pipe);
      const isValid = clearance >= MIN_CLEARANCE_INCHES;

      return {
        pipeId: pipe.id || `pipe-${pipe.pipeNumber}`,
        status: isValid ? "valid" : "error",
        color: isValid ? "blue" : "red",
        message: isValid
          ? `Clear: ${clearance.toFixed(1)}" from nearest joint`
          : `TOO CLOSE: Only ${clearance.toFixed(
              1
            )}" from joint (min ${MIN_CLEARANCE_INCHES}")`,
        clearanceInches: clearance,
      };
    });
  }

  // Helper method to calculate joint clearance for a pipe
  private calculateJointClearance(pipe: IPipeConnection): number {
    const components = this.stackComponents;
    if (components.length === 0) return 100; // No components = no issue

    // Find which component the pipe penetrates
    const pipeElevation = pipe.invertElevation;
    const penetratedComponent = components.find((comp) => {
      const topElevation =
        comp.bottomElevation + getEffectiveHeightInches(comp) / 12;
      return (
        comp.bottomElevation <= pipeElevation && topElevation >= pipeElevation
      );
    });

    if (!penetratedComponent) return 100; // Pipe outside structure = no issue

    // Calculate distances to top and bottom joints
    const componentTop =
      penetratedComponent.bottomElevation +
      getEffectiveHeightInches(penetratedComponent) / 12;
    const componentBottom = penetratedComponent.bottomElevation;

    const distToTopInches = Math.abs(componentTop - pipeElevation) * 12;
    const distToBottomInches = Math.abs(pipeElevation - componentBottom) * 12;

    // Account for hole radius
    const holeRadius = (pipe.holeSizeInches || pipe.pipeDiameterInches + 4) / 2;

    // Return minimum clearance
    return Math.min(
      distToTopInches - holeRadius,
      distToBottomInches - holeRadius
    );
  }

  // NEW: Effective base elevation with catch basin depth
  get effectiveBaseElevation(): number {
    const project = this.editingProject || this.activeProject;
    if (!project) return 0;

    const catchDepth = project.catchDepthFeet || 0;
    return this.lowestInvert - catchDepth;
  }

  // NEW: Balancing suggestions for optimal height
  get balancingSuggestions(): IBalancingSuggestion {
    // Check outlet pipe location
    const outletLocation = this.outletPipeComponentLocation;

    // If outlet pipe is below the base, structure is too short
    if (outletLocation === "below-base") {
      return {
        status: "too-short",
        message: "Structure too short - outlet pipe is below the base",
        suggestedAction: "Add a base component to contain the outlet pipe",
        remainingHeight: this.remainingRimToInvert,
      };
    }

    // If structure is too short overall (pipe would be below the entire structure)
    if (outletLocation === "below-structure") {
      const remaining = this.remainingRimToInvert;
      return {
        status: "too-short",
        message: `Structure too short - need ${remaining.toFixed(2)} ft more height`,
        suggestedAction: "Add more components to reach the required rim-to-invert distance",
        remainingHeight: remaining,
      };
    }

    // If outlet pipe is in a riser (not base), structure positioning is wrong
    if (outletLocation === "riser") {
      const baseHeight = this.baseNominalHeightInches;
      const holePosition = this.outletInvertPositionInches;
      const excessInches = holePosition - baseHeight;

      return {
        status: "too-tall",
        message: `Outlet pipe is in riser, not base`,
        suggestedAction: `Use a taller base (need ${excessInches.toFixed(0)}" more) or lower the pipe invert`,
      };
    }

    // Check if there's remaining height to fill even though pipe is positioned correctly
    const remaining = this.remainingRimToInvert;
    if (remaining > 0 && !this.heightDismissed) {
      return {
        status: "need-more",
        message: `${remaining.toFixed(2)} ft remaining to fill`,
        remainingHeight: remaining,
        suggestedAction: remaining < ManholeViewStore.REMAINDER_ELIMINATION_THRESHOLD
          ? "Small remainder can be dismissed"
          : "Add components to fill remaining height",
      };
    }

    // Outlet pipe is properly within the base - height is acceptable
    return {
      status: "perfect",
      message: "Outlet pipe is correctly positioned in base",
    };
  }

  // NEW: Check if hatch specification is required
  get requiresHatch(): boolean {
    const topComponent = this.stackComponents[this.stackComponents.length - 1];
    return (
      topComponent?.product.type === "slab" &&
      (topComponent.product.description?.includes("Opening") ||
        topComponent.product.diameterInches === 24)
    );
  }

  // NEW: Available castings based on structure type
  get availableCastings(): IManholeCasting[] {
    // Since manholeCastingsStore doesn't exist yet, always use default castings
    // TODO: When castings service is available, uncomment the code below
    /*
    const castingsStore = (this.rootStore as any).manholeCastingsStore;
    if (castingsStore?.entities && castingsStore.entities.length > 0) {
      const project = this.editingProject || this.activeProject;
      const structureType = project?.type || 'Sanitary';

      return castingsStore.entities.filter((c: IManholeCasting) =>
        c.compatibleTypes.includes(structureType)
      );
    }
    */

    // Return default castings
    return this.getDefaultCastings();
  }

  // Default castings when service is not available
  private getDefaultCastings(): IManholeCasting[] {
    return [
      {
        id: "cast-4",
        name: '4" Tall Casting',
        description: 'Standard 4" tall cast iron frame and cover',
        heightInches: 4,
        type: "4inch",
        compatibleTypes: ["Storm", "Sanitary", "Custom"],
        productCode: "FC-4-STD",
        weight: 350,
        cost: 425,
      },
      {
        id: "cast-6",
        name: '6" Tall Casting',
        description: 'Heavy duty 6" tall cast iron frame and cover',
        heightInches: 6,
        type: "6inch",
        compatibleTypes: ["Storm", "Sanitary", "Custom"],
        productCode: "FC-6-HD",
        weight: 450,
        cost: 525,
      },
      {
        id: "cast-wa",
        name: "Washington State Casting",
        description: "WSDOT approved casting",
        heightInches: 5,
        type: "wa-state",
        compatibleTypes: ["Storm", "Sanitary"],
        productCode: "FC-WA-DOT",
        weight: 400,
        cost: 575,
      },
      {
        id: "cast-yakima",
        name: "City of Yakima Casting",
        description: "Yakima municipal standard",
        heightInches: 4.5,
        type: "yakima",
        compatibleTypes: ["Storm", "Sanitary"],
        productCode: "FC-YAK-STD",
        weight: 375,
        cost: 495,
      },
      {
        id: "cast-or",
        name: "Oregon Casting",
        description: "ODOT approved casting",
        heightInches: 5.5,
        type: "oregon",
        compatibleTypes: ["Storm", "Sanitary"],
        productCode: "FC-OR-DOT",
        weight: 425,
        cost: 550,
      },
    ];
  }

  // NEW: Form assignment suggestions
  get formAssignment(): IFormAssignment | null {
    const formsStore = (this.rootStore as any).manholeFormsStore;
    if (!formsStore?.entities) return null;

    // Find available form with capacity
    const availableForm = formsStore.entities.find(
      (f: IFormAssignment) => f.availableSlots > 0
    );

    if (!availableForm) {
      return {
        formId: "none",
        formName: "No Forms Available",
        capacity: 0,
        currentLoad: 0,
        availableSlots: 0,
        isDefault: false,
        alternativeForms: [],
      };
    }

    return availableForm;
  }

  get drawingObjects(): IDrawingObject[] {
    const components = this.stackComponents;
    const objects: IDrawingObject[] = [];

    if (this.currentView === "side") {
      // Side view - vertical stack
      components.forEach((comp, index) => {
        const y = comp.bottomElevation * 100; // Scale factor
        const height = getEffectiveHeightInches(comp) * (100 / 12); // Convert to scaled units
        const width = comp.product.diameterInches * (100 / 12);

        // Main component rectangle
        objects.push({
          type: "rect",
          x: -width / 2, // Center horizontally
          y,
          width,
          height,
          fill: this.getComponentColor(comp.product.type),
          stroke: "#333",
          strokeWidth: 1,
        });

        // Label
        objects.push({
          type: "text",
          x: width / 2 + 10,
          y: y + height / 2,
          text: comp.product.description,
          fill: "#000",
        });
      });

      // Add pipe connections
      const project = this.editingProject || this.activeProject;
      if (project?.manholeStructure?.pipes) {
        project.manholeStructure.pipes.forEach((pipe) => {
          const y = pipe.invertElevation * 100;
          const diameter = pipe.pipeDiameterInches * (100 / 12);

          objects.push({
            type: "circle",
            x: -50, // Left side
            y: y + diameter / 2,
            radius: diameter / 2,
            fill: "#4A90E2",
            stroke: "#333",
            strokeWidth: 1,
          });
        });
      }
    } else {
      // Top view - circular with pipe holes
      const mainDiameter = 48 * (100 / 12); // 48" manhole

      // Main circle
      objects.push({
        type: "circle",
        x: 0,
        y: 0,
        radius: mainDiameter / 2,
        fill: "#E0E0E0",
        stroke: "#333",
        strokeWidth: 2,
      });

      // Pipe holes
      const project = this.editingProject || this.activeProject;
      if (project?.manholeStructure?.pipes) {
        project.manholeStructure.pipes.forEach((pipe) => {
          const angle = (pipe.angleDegrees * Math.PI) / 180;
          const distance = mainDiameter / 2 - 20; // Offset from edge
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          const holeRadius = (pipe.holeSizeInches * (100 / 12)) / 2;

          objects.push({
            type: "circle",
            x,
            y,
            radius: holeRadius,
            fill: "#4A90E2",
            stroke: "#333",
            strokeWidth: 1,
          });

          // Pipe number label
          objects.push({
            type: "text",
            x: x - 5,
            y: y + 5,
            text: `(${pipe.pipeNumber})`,
            fill: "#fff",
          });
        });
      }
    }

    return objects;
  }

  private getComponentColor(type: string): string {
    const colors: Record<string, string> = {
      base: "#8B4513",
      riser: "#A0522D",
      cone: "#CD853F",
      slab: "#D2691E",
      ring: "#DEB887",
      frame: "#696969",
      cover: "#2F4F4F",
      accessory: "#778899",
    };
    return colors[type] || "#CCCCCC";
  }

  // ============================================
  // DELETE METHODS (Phase 3)
  // ============================================

  /**
   * Delete a structure and all its related data (pipes, components, internals)
   * @param structureId - The ID of the structure to delete
   */
  deleteStructure = async (structureId: string) => {
    if (!structureId) return;

    this.setLoading(true);
    this.setError(null);

    try {
      // 1. Delete related pipes
      const pipeStore = (this.rootStore as any).manholePipeConnectionsStore;
      if (pipeStore) {
        const pipes = await pipeStore.find({
          query: { structureId },
        });
        if (pipes?.data) {
          for (const pipe of pipes.data) {
            await pipeStore.remove(pipe.id);
          }
        }
      }

      // 2. Delete related components (stack sequence)
      const componentStore = (this.rootStore as any).manholeSequencesStore;
      if (componentStore) {
        const components = await componentStore.find({
          query: { structureId },
        });
        if (components?.data) {
          for (const comp of components.data) {
            await componentStore.remove(comp.id);
          }
        }
      }

      // 3. Delete related internals
      const internalStore = (this.rootStore as any).manholeInternalsStore;
      if (internalStore) {
        const internals = await internalStore.find({
          query: { structureId },
        });
        if (internals?.data) {
          for (const internal of internals.data) {
            await internalStore.remove(internal.id);
          }
        }
      }

      // 4. Delete the structure itself
      const structureStore = (this.rootStore as any).manholeStructuresStore;
      if (structureStore) {
        await structureStore.remove(structureId);
      }

      // 5. Clear local edits for this structure
      runInAction(() => {
        this.structureEdits.delete(structureId);
        this.pipeEdits.delete(structureId);
        this.componentEdits.delete(structureId);
        this.internalEdits.delete(structureId);

        // Remove from structureOrder if present
        const orderIndex = this.structureOrder.indexOf(structureId);
        if (orderIndex > -1) {
          this.structureOrder.splice(orderIndex, 1);
        }
      });

      // If this was the active structure, navigate to next available structure
      // (activeStructureId is computed from URL queryParams)
      if (this.activeStructureId === structureId && (this.rootStore as any).routerStore) {
        const nextStructureId = this.structureOrder[0] || null;
        (this.rootStore as any).routerStore.goTo("Manhole Designer", null, {
          projectId: this.activeProjectId || undefined,
          structureId: nextStructureId || undefined,
        });
      }

      console.log(`Successfully deleted structure: ${structureId}`);
    } catch (error: any) {
      console.error("Failed to delete structure:", error);
      this.setError(
        `Failed to delete structure: ${error.message || "Unknown error"}`
      );
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Delete a draft quote
   * @param quoteId - The ID of the quote to delete
   */
  deleteQuote = async (quoteId: string) => {
    if (!quoteId) return;

    this.setLoading(true);
    this.setError(null);

    try {
      const quoteStore = (this.rootStore as any).manholeQuotesStore;
      if (!quoteStore) {
        throw new Error("Quote store not available");
      }

      // Verify quote exists and is in draft status
      const quote = quoteStore.entities.find((q: any) => q.id === quoteId);
      if (!quote) {
        throw new Error("Quote not found");
      }

      if (quote.status !== "draft") {
        throw new Error("Only draft quotes can be deleted");
      }

      // Delete the quote
      await quoteStore.remove(quoteId);

      console.log(`Successfully deleted quote: ${quoteId}`);
    } catch (error: any) {
      console.error("Failed to delete quote:", error);
      this.setError(
        `Failed to delete quote: ${error.message || "Unknown error"}`
      );
    } finally {
      this.setLoading(false);
    }
  };

  // Cleanup
  cleanup = () => {
    // Note: activeProjectId/activeStructureId are computed from URL queryParams
    // They will naturally clear when navigating away from this route
    this.selectedPipeId = null;
    this.editingProject = null;
    this.isDirty = false;
    this.currentView = "side";
    this.resetView();
    // NEW: Reset sequence state
    this.currentSequenceId = null;
    this.currentManholeIndex = 0;
    this.heightDismissed = false;
    this.showPipesAndDimensions = false;
    this.showBelowInvert = false;
    this.catalogPanelOpen = false;
  };
}
